import * as React from "react";
import shortid from "shortid";
import cloneDeep from "lodash/cloneDeep";
import { Modal, styled, Tooltip } from "@lib/components";
import { ModalContent } from "@lib/components";
import { Button } from "@lib/components";
import { RotateLoader } from "@lib/components";
import { DragDropContext, DropResult } from "react-beautiful-dnd";
import { arrayMove } from "react-sortable-hoc";
import { CoreUtils, getId, logger, normalizeObject, RestaurantUtils, Untrusive } from "@lib/common";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import { Tag } from "@lib/components";
import { Checkbox } from "@lib/components";

import { getNextDayOpenTime, isDishPaused } from "../orders/sidebars/pauseServicesSidebar/constants";
import { FaHistory } from "react-icons/fa"
import { MenuVersionHistory } from "../menus/version-history";
import { POSSyncStatus, lastSyncAt, requestPOSSync, waitForPOSSync } from "../menus/pos-sync";
import { UI } from "../../../../../core/ui";
import moment from "moment";
import { ResumeModal } from "../menus/components/ResumeModal";
import { MENU_ITEM_TYPE, MENU_COMPONENTS } from "../menus/constant";
import { RestaurantFormCategory } from "../menus/forms/category";
import { RestaurantFormDish } from "../menus/forms/dish";
import { RestaurantFormMenu } from "../menus/forms/menu";
import { MenuListButton } from "../menus/layout/button";
import { MenuItemText, MenuListFloatingButtonWrapper, MenuListFloatingContent, MenuListFloatingTitle } from "../menus/layout/floating-button";
import { MenuListItem } from "../menus/layout/item";
import { MenuList } from "../menus/layout/list";
import { PauseSidebar } from "../menus/pauseItemsSidebar";
import { ItemButton, MenuListItemV2 } from "./item-v2";
import { RestaurantFormMenuV2 } from "./menu_v2";
import { TabGroup, TabItem } from "../settings-v2/receipt-printing";
import { RestaurantFormCategoryV2 } from "./category_v2";
import { RestaurantFormDishV2 } from "./dish-v2";
import { BottomMenuControls, CopyIcon, DeleteIcon, EditIcon } from "../menus/mobileView/menuList/bottomMenuControls";
import { MenuCardTitle, MenuItemColor, TextEllipsis } from ".";
import { MenuColor } from "../../../../components/v2/menucolor";
import {
	MenuVisibility,
	buildForkedAvailabilityRowWithVisibility,
	categoryInheritsMenuAvailability,
	effectiveCategoryAvailabilityId,
	effectiveItemAvailabilityId,
	itemInheritsParentAvailability,
	menuDefinesPosOnlineBlocks,
	mergeAvailabilityWithPosOnline,
	normalizeMenuVisibilityConditionsForSave,
} from "../../../../components/v2/menuvisibility";
import { MenuPreVisibility } from "../../../../components/v2/menuprevisibility";
import { MenuMoreAction } from "../../../../components/v2/menumoreaction";
import { MenuPresence } from "../../../../components/menu-presence";
import { MenuFilterMenu } from "../../../../components/v2/menufiltermenu";
import { MenuPrinterSettings } from "../../../../components/v2/menuprintersettings";
import { MenuPrinterEligibleItems } from "../../../../components/v2/menuprintereligibleitems";
import { MenuPrinterAssignItems } from "../../../../components/v2/menuprinterassignitem";
import {
	MenuPreOverride,
	type OverrideTargetEntity,
	type OverrideChangesSummary,
	type OverrideEditKind,
	type AffectedEntityRow,
	getServicesFromAvailability,
	getServiceLabels,
	getPosOnlineSlice,
	formatPosPrinterValuesForDisplay,
	formatPosOnlineAvailabilityPreview,
	posOnlineVisibilityChanged,
	isAvailabilityPosEnabled,
	isAvailabilityOnlineEnabled,
} from "../../../../components/v2/menupreoverride";
import { ButtonV2, EditingStatusAlertModal, EditingStatusAlertModalBody, EditingStatusAlertModalContent, EditingStatusAlertModalHeader, EditingStatusAlertModalHeaderText, EditingStatusIconWrapper, MenuActionContainer, MenuActionItem, MenuStatusAlert } from "../../../../components/v2/layout.c";
import { BulkConfirmModal } from "../../../../components/v2/bulk-confirm-modal";
import {
	applyMenuListView,
	canShowListSortControls,
	categoryHasMatchingItem,
	getSortDirectionForId,
	menuHasMatchingItem,
	MenuListSortDirection,
	MenuListSortDirectionsById,
	applyPendingSorts,
	applyPendingSortsV2,
	hasPendingSorts,
} from "../menus/utils/sortLists";
import { MenuSortDirectionButtons } from "../menus/components/menu-sort-direction-buttons";
import { IconArrowForward, IconArrowForwardWhite, IconCopyAlt, IconDelete, IconEdit, IconEditingClose } from "../../../../components/v2/icons";
import { isEmpty, isObject } from "lodash";
import uuid from "uuid";
import { PauseIcon } from "../../../../components/v2/icons";
import { IoIosPause } from "react-icons/io";
import { SettingsActionButtonContainer, SettingsActionButtonWrapper } from "../settings-v2";
import { MenuReceiptPrinterAssignItem } from "../../../../components/v2/menureceiptprinterassignitem";
export type MenuComponents = "menu" | "category" | "item";

const isV2ItemPaused = (item: T.Schema.Restaurant.Menu.RestaurantItem) => {
	if (item.pause?.infinite) return true;
	const pauseStartRaw = item.pause?.start_utc ?? item.pause?.start;
	const pauseEndRaw = item.pause?.end_utc ?? item.pause?.end;
	const pauseStart = pauseStartRaw != null ? new Date(pauseStartRaw).getTime() : NaN;
	const pauseEnd = pauseEndRaw != null ? new Date(pauseEndRaw).getTime() : NaN;
	if (!Number.isFinite(pauseStart) || !Number.isFinite(pauseEnd)) return false;
	const now = Date.now();
	return now >= pauseStart && now < pauseEnd;
};

interface RestaurantDishWithTaxes
	extends T.Schema.Restaurant.Menu.RestaurantDish { }

type MenusEditMode = "normal" | "reOrder" | "editPrices";

type MenuItemRefV2 = { menu_id: string; category_id: string; id: string };

type MenuPendingOverride = {
	kind: 'visibility' | 'color' | 'printers' | 'receipt_printers';
	targetEntityType: 'menu' | 'category' | 'item';
	menuIndex: number;
	menuId: string;
	categoryId?: string;
	/** Single-item color (MenuColor list) */
	itemId?: string;
	parentAvailabilityId: string;
	parentMergedAvailability: any;
	newColor?: string;
	newMediaUrl?: string;
	/** When kind is printers, value propagated to listed descendants */
	newPosPrinters?: readonly any[];
	receiptItemIds?: string[];
	receiptPrinterIds?: string[];
	/** Parent menu/category change not persisted yet — show override modal first */
	awaitingParentPersist?: boolean;
	/** MenuVisibility form payload when awaiting visibility persist */
	visibilityConditions?: any;
	/** Bulk item-level updates (cross-menu/cross-category) confirmed via pre-override modal. */
	bulkItemRefs?: MenuItemRefV2[];
};

const EditPricesModeWrapper = styled("div")`

	display: flex;
	flex-direction: column-reverse;
	align-items: center;
	justify-content: center;
	gap: 16px;
	background: white;
	position: fixed;
	width: 100%;
	left: 0px;
	right: 0px;
	padding: 0px 16px 16px;
	bottom: 0px;

	& .grid-2 {
		width: 100%;
		& .col {
			width: 100%;
			& button {
				width: 100%;
			}
		}
	}
	p {
		color: #fb2f2f;
	}

	@media (min-width: 768px) {
		& .grid-2 {
			width: 25%;
		}
	}

`;

const MobileMoreActionsWrapper = styled("div")`
	display: block;

	@media (min-width: 768px) {
		display: none;
	}
`;

interface Props {
	/** When false, skip initial menu fetch and list loader (e.g. form-only mount). */
	loadMenusOnMount?: boolean;
	filterText?: string;
	sortDirection?: MenuListSortDirection;
}
interface State {
	/** True while a POS sync request is in flight, so the button cannot be double-pressed. */
	syncing_pos: boolean;
	/** Where this restaurant stands with the POS, from GET .../pos/status. Null until read. */
	pos_sync_status: POSSyncStatus | null;
	panel: MenuComponents | null;
	panel_index: number[];
	panel_pause: MenuComponents | null;
	panel_index_pause: number[];
	copy_type: MenuComponents | null;
	copy_index: number[];
	copy_loading: boolean;
	remove_type: MenuComponents | null;
	remove_index: number[];
	remove_loading: boolean;
	mode: MenusEditMode;
	loading: boolean;
	selected_dishes: string[];
	selected_loading: boolean;
	error: string | null;
	currentMenuItemId: string;
	currentMenuItem: T.Schema.Restaurant.Menu.RestaurantDish | T.Schema.Restaurant.Menu.RestaurantItem | null;
	currentMenuItems: T.Schema.Restaurant.Menu.RestaurantDish[] | T.Schema.Restaurant.Menu.RestaurantItem[];
	resumeModalOpen: boolean;
	oneTimeRenderFlag: boolean;
	activeDeleteItemModal: boolean;
	activeMobile: string | null;
	showBottomControl: string | null;
	activeMenuColorModal: boolean;
	activeMenuColorTitle?: string;
	activeMenuColorData: {
		color?: string;
		media_url?: string,
		title?: string
	}
	targetItem: string;
	targetIndex: number;
	targetMenuIndex: number;
	history_menu_id: string | null;
	targetCategoryIndex: number;
	activeMenuVisibilityModal: boolean;
	activeMenuPrevisibilityModal: boolean;
	/** Item ids to apply item color to on save (snapshot when opening color from a selected row). */
	menuColorBulkItemIds: string[] | null;
	/** When user clicks a printer in Printer Setup, we open Eligible Items with this name */
	selectedPrinterForEligible: { name: string; _id: string } | null;
	/** When opening Assign Printers from an item row, we pass this target so we can load/save item printers */
	printerAssignTarget: { menuIndex: number; categoryIndex: number; itemId: string; menuId: string; categoryId: string } | null;
	/** When set, printer assign save applies the same printers to all these item ids (bulk from selection sheet). */
	printerAssignBulkItemIds: string[] | null;
	/** Receipt printer quick-assign from dish row (eligible_items on `settings.printers`) */
	receiptPrinterAssignItem: { itemId: string; name: string } | null;
	/** User closed the v2 bulk selection sheet while items remain selected; reopen when selection goes 0 → 1+. */
	bulkSelectedDishesSheetDismissed: boolean;
	/** Context for override warning/confirm modals: which entity and what will change */
	preOverrideContext: {
		targetEntity: OverrideTargetEntity;
		changesSummary?: OverrideChangesSummary | null;
		overrideSource?: { menuId: string; categoryId?: string; itemId?: string };
		editKind?: OverrideEditKind;
		affectedEntities?: AffectedEntityRow[];
		pendingOverride?: MenuPendingOverride | null;
	} | null;
	hideMoreActionsOnMobile: boolean;
	/** Desktop filter: show only entities with POS / Online channel enabled (v2 menus). */
	menuChannelFilterPos: boolean;
	menuChannelFilterOnline: boolean;
	categorySortDirections: MenuListSortDirectionsById;
	itemSortDirections: MenuListSortDirectionsById;
}

function getAvailFromMenuById(menu: any, id: string | undefined) {
	if (!id) return null;
	return menu.availabilities?.find((a: any) => a.id === id) ?? null;
}

function toMenuApiMediaUrlValue(value: string | null | undefined): string | null {
	const next = typeof value === 'string' ? value.trim() : '';
	return next.length > 0 ? next : null;
}

function getConditionsBlobFromAvailRow(avail: any): any {
	if (avail == null || typeof avail !== 'object') return {};
	if (avail.conditions != null && typeof avail.conditions === 'object') return cloneDeep(avail.conditions);
	return cloneDeep(avail);
}

function mergeParentChannelsIntoChildBlob(childBlob: any, parentAvailRow: any | null): any {
	if (!parentAvailRow) return childBlob;
	const { pos: pPos, online: pOn } = getPosOnlineSlice(parentAvailRow);
	const merged = mergeAvailabilityWithPosOnline(childBlob, pPos, pOn);
	return merged != null ? { ...childBlob, ...merged } : childBlob;
}

function effectiveConditionsBlobForMenu(menu: any): any {
	const menuRow = getAvailFromMenuById(menu, menu.availability_id);
	return getConditionsBlobFromAvailRow(menuRow);
}

function effectiveConditionsBlobForCategory(menu: any, category: any): any {
	const menuRow = getAvailFromMenuById(menu, menu.availability_id);
	const catId = category?.availability_id || menu.availability_id;
	const catRow = getAvailFromMenuById(menu, catId);
	let blob = getConditionsBlobFromAvailRow(catRow);
	blob = mergeParentChannelsIntoChildBlob(blob, menuRow);
	return blob;
}

function effectiveConditionsBlobForItem(menu: any, category: any, item: any): any {
	const menuRow = getAvailFromMenuById(menu, menu.availability_id);
	const catId = category?.availability_id || menu.availability_id;
	const catRow = getAvailFromMenuById(menu, catId);
	const itemId = item?.availability_id || catId;
	const itemRow = getAvailFromMenuById(menu, itemId);
	let blob = getConditionsBlobFromAvailRow(itemRow);
	blob = mergeParentChannelsIntoChildBlob(blob, catRow);
	blob = mergeParentChannelsIntoChildBlob(blob, menuRow);
	return blob;
}

function passesChannelFilters(blob: any, filterPos: boolean, filterOnline: boolean): boolean {
	if (!filterPos && !filterOnline) return true;
	const wrap = { conditions: blob };
	const posOk = !filterPos || isAvailabilityPosEnabled(wrap);
	const onlineOk = !filterOnline || isAvailabilityOnlineEnabled(wrap);
	return posOk && onlineOk;
}

function categoryVisibleWithChannelFilters(menu: any, cat: any, filterPos: boolean, filterOnline: boolean): boolean {
	const catBlob = effectiveConditionsBlobForCategory(menu, cat);
	if (passesChannelFilters(catBlob, filterPos, filterOnline)) return true;
	const items = (menu.items || []).filter((i: any) => i.category_id === cat.id);
	return items.some((item: any) =>
		passesChannelFilters(effectiveConditionsBlobForItem(menu, cat, item), filterPos, filterOnline)
	);
}

function menuVisibleWithChannelFilters(menu: any, filterPos: boolean, filterOnline: boolean): boolean {
	const menuBlob = effectiveConditionsBlobForMenu(menu);
	if (passesChannelFilters(menuBlob, filterPos, filterOnline)) return true;
	return (menu.categories || []).some((cat: any) => categoryVisibleWithChannelFilters(menu, cat, filterPos, filterOnline));
}

function patchAvailabilitySlot(menu: any, availId: string | undefined, nextAvail: any) {
	if (!menu?.availabilities || !availId || !nextAvail) return;
	const ix = menu.availabilities.findIndex((a: any) => a.id === availId);
	if (ix < 0) return;
	menu.availabilities[ix] = cloneDeep(nextAvail);
}

/** Keeps pre-visibility / list snapshots aligned with `menus_v2` after a visibility save. */
function refreshedPreOverrideForVisibility(
	baseCtx: any,
	menuFinal: any,
	entityType: string | undefined,
	targetCategory: any,
	targetItemRow: any
) {
	let conditions: any = null;
	if (entityType === 'item' && targetItemRow) {
		const catRow =
			targetCategory &&
			(menuFinal.categories || []).find((c: any) => c.id === targetCategory.id);
		const itemRow =
			(menuFinal.items || []).find((it: any) => it.id === targetItemRow.id) || targetItemRow;
		const aid = effectiveItemAvailabilityId(menuFinal, catRow || targetCategory, itemRow);
		conditions = aid ? getAvailFromMenuById(menuFinal, aid) : null;
	} else if (entityType === 'category' && targetCategory) {
		const catRow =
			(menuFinal.categories || []).find((c: any) => c.id === targetCategory.id) || targetCategory;
		const aid = effectiveCategoryAvailabilityId(menuFinal, catRow);
		conditions = aid ? getAvailFromMenuById(menuFinal, aid) : null;
	} else {
		conditions = getAvailFromMenuById(menuFinal, menuFinal.availability_id);
	}
	return {
		...baseCtx,
		pendingOverride: null,
		changesSummary: null,
		affectedEntities: null,
		targetEntity: {
			...baseCtx.targetEntity,
			conditions: conditions ?? baseCtx.targetEntity?.conditions,
			...(entityType === 'menu'
				? {
						categoryCount: menuFinal.categories?.length ?? 0,
						itemCount: menuFinal.items?.length ?? 0,
					}
				: {}),
		},
	};
}

function trimColorStr(s: string | undefined | null): string {
	if (s == null || typeof s !== 'string') return '';
	return s.trim();
}

function normalizeColorForCompare(s: string | undefined | null): string {
	const trimmed = trimColorStr(s);
	if (!trimmed || trimmed === '—') return '';
	try {
		if (trimmed.startsWith('#') || /^rgba?\(/i.test(trimmed)) {
			return `#${CoreUtils.colors.rgbToHex(trimmed).replace(/^#/, '')}`.toUpperCase();
		}
	} catch {
		// fall through to trimmed literal
	}
	return trimmed.toUpperCase();
}

function itemDisplayColorWouldChange(
	item: any,
	category: any | null | undefined,
	menu: any,
	newColor: string,
): boolean {
	const current = resolveDisplayColorForEntity('item', item, category, menu);
	return normalizeColorForCompare(newColor) !== normalizeColorForCompare(current);
}

function normalizePosPrintersForCompare(printers: readonly any[] | undefined): string {
	const normalized = (printers || []).map((value) => {
		if (typeof value === 'number' && Number.isFinite(value)) return String(value);
		if (typeof value === 'string' && value.trim() !== '') {
			const num = Number(value);
			if (!Number.isNaN(num) && Number.isFinite(num)) return String(num);
		}
		return String(value);
	}).sort();
	return JSON.stringify(normalized);
}

function itemPosPrintersWouldChange(item: any, nextPrinters: readonly any[] | undefined): boolean {
	const current = ((item as any)?.integrations?.pushpos?.printers || []) as any[];
	return normalizePosPrintersForCompare(current) !== normalizePosPrintersForCompare(nextPrinters);
}

/** Effective menu list display color: own hex or inherited from category then menu. */
function resolveDisplayColorForEntity(
	entityType: 'category' | 'item',
	ent: any,
	cat: any | null,
	menu: any
): string {
	if (entityType === 'category') {
		const own = trimColorStr(ent?.color);
		if (own) return own;
		return trimColorStr(menu?.color);
	}
	const own = trimColorStr(ent?.color);
	if (own) return own;
	const fromCat = cat ? trimColorStr(cat.color) : '';
	if (fromCat) return fromCat;
	return trimColorStr(menu?.color);
}

function resolvePosColorStr(ent: any): string {
	const c = ent?.integrations?.pushpos?.color;
	return typeof c === 'string' && c.trim().length > 0 ? c.trim() : '';
}

function hasDisplayColor(ent: any) {
	return trimColorStr(ent?.color).length > 0;
}

function hasPosColor(ent: any) {
	return resolvePosColorStr(ent) !== '';
}

function hasPosPrinterList(ent: any) {
	const p = ent?.integrations?.pushpos?.printers;
	return Array.isArray(p) && p.length > 0;
}

function categoryHasOwnAvailability(menu: any, cat: any) {
	return !!(cat?.availability_id && cat.availability_id !== menu.availability_id);
}

function itemHasOwnAvailability(menu: any, cat: any, item: any) {
	const parentId = cat?.availability_id || menu.availability_id;
	return !!(item?.availability_id && item.availability_id !== parentId);
}

function analyzeDescendantsOverride(args: {
	menu: any;
	targetType: 'menu' | 'category' | 'item';
	categoryId?: string;
	editKind: OverrideEditKind;
	preMergeAvailById: Map<string, any>;
	mergedParentAvailability: any;
	mergedParentAvailabilityId: string;
	newColor?: string;
	newMediaUrl?: string;
	newPosPrinters?: readonly any[];
	/** Menu/category display color before this edit (for summary `from` + arrow UI). */
	previousParentDisplayColor?: string;
}): { affectedRows: AffectedEntityRow[]; changesSummary: OverrideChangesSummary | null } {
	const {
		menu,
		targetType,
		categoryId,
		editKind,
		preMergeAvailById,
		mergedParentAvailability,
		mergedParentAvailabilityId,
		newColor,
		newPosPrinters,
		previousParentDisplayColor,
	} = args;
	const affectedRows: AffectedEntityRow[] = [];
	const toServiceLabels = getServiceLabels(getServicesFromAvailability(mergedParentAvailability));

	const buildRow = (
		entityType: 'category' | 'item',
		ent: any,
		cat: any | null,
		resolvedAvailId: string | undefined
	): AffectedEntityRow | null => {
		const fromAvail =
			(resolvedAvailId && preMergeAvailById.get(resolvedAvailId)) ||
			getAvailFromMenuById(menu, resolvedAvailId);
		const fromPreview = formatPosOnlineAvailabilityPreview(fromAvail);
		const toPreview = formatPosOnlineAvailabilityPreview(mergedParentAvailability);

		if (editKind === 'color') {
			const colorRelevant = hasDisplayColor(ent) || hasPosColor(ent);
			if (!colorRelevant) return null;
			const row: AffectedEntityRow = {
				entityType,
				name: ent.name ?? '—',
				id: ent.id,
			};
			if (hasDisplayColor(ent) && newColor != null) {
				row.displayColor = {
					from: resolveDisplayColorForEntity(entityType, ent, cat, menu),
					to: newColor,
				};
			}
			if (hasPosColor(ent)) {
				row.posColor = { from: resolvePosColorStr(ent), to: '—' };
			}
			return row;
		}

		if (editKind === 'visibility') {
			const willChangeAvail =
				entityType === 'category'
					? categoryHasOwnAvailability(menu, ent)
					: itemHasOwnAvailability(menu, cat!, ent);
			if (!willChangeAvail) return null;
			if (!posOnlineVisibilityChanged(fromAvail, mergedParentAvailability)) return null;
			return {
				entityType,
				name: ent.name ?? '—',
				id: ent.id,
				visibilityPreview: { from: fromPreview, to: toPreview },
			};
		}

		/* printers */
		if (!hasPosPrinterList(ent)) return null;
		const fromLabs = formatPosPrinterValuesForDisplay(ent.integrations.pushpos.printers);
		const toLabs =
			newPosPrinters != null ? formatPosPrinterValuesForDisplay(newPosPrinters) : fromLabs;
		return {
			entityType,
			name: ent.name ?? '—',
			id: ent.id,
			posPrinters: { from: fromLabs, to: toLabs },
		};
	};

	if (targetType === 'menu') {
		for (const cat of menu.categories ?? []) {
			const resolved = cat.availability_id || menu.availability_id;
			const row = buildRow('category', cat, null, resolved);
			if (row) affectedRows.push(row);
		}
		for (const item of menu.items ?? []) {
			const cat = menu.categories?.find((c: any) => c.id === item.category_id);
			if (!cat) continue;
			const resolved = item.availability_id || cat.availability_id || menu.availability_id;
			const row = buildRow('item', item, cat, resolved);
			if (row) affectedRows.push(row);
		}
	} else if (targetType === 'category' && categoryId) {
		const cat = menu.categories?.find((c: any) => c.id === categoryId);
		if (!cat) return { affectedRows: [], changesSummary: null };
		for (const item of (menu.items ?? []).filter((i: any) => i.category_id === categoryId)) {
			const resolved = item.availability_id || cat.availability_id || menu.availability_id;
			const row = buildRow('item', item, cat, resolved);
			if (row) affectedRows.push(row);
		}
	}

	let changesSummary: OverrideChangesSummary | null = null;
	if (editKind === 'visibility') {
		const menuFromAvail =
			preMergeAvailById.get(mergedParentAvailabilityId) || mergedParentAvailability;
		const before = menuFromAvail;
		const after = mergedParentAvailability;
		const fromLabs = getServiceLabels(getServicesFromAvailability(before));
		const toLabs = toServiceLabels;

		changesSummary = {};
		if (posOnlineVisibilityChanged(before, after)) {
			changesSummary.posOnlinePreview = {
				from: formatPosOnlineAvailabilityPreview(before),
				to: formatPosOnlineAvailabilityPreview(after),
			};
		}
		if (
			!changesSummary.posOnlinePreview &&
			(fromLabs.length || toLabs.length) &&
			fromLabs.join('\0') !== toLabs.join('\0')
		) {
			changesSummary.serviceTypes = { from: fromLabs, to: toLabs };
		}
		if (!changesSummary.posOnlinePreview && !changesSummary.serviceTypes) {
			changesSummary = null;
		}
	} else if (editKind === 'color' && newColor != null) {
		const fromParent =
			previousParentDisplayColor !== undefined ? trimColorStr(previousParentDisplayColor) : '';
		changesSummary = {
			displayColor: {
				from: fromParent.length > 0 ? fromParent : '—',
				to: newColor,
			},
		};
	} else if (editKind === 'printers' && newPosPrinters != null) {
		const toLabs = formatPosPrinterValuesForDisplay(newPosPrinters);
		changesSummary = { posPrinters: { from: [], to: toLabs } };
	}

	return { affectedRows, changesSummary };
}

function itemVisibilityMeaningfullyChanged(beforeAvail: any, afterAvail: any): boolean {
	if (posOnlineVisibilityChanged(beforeAvail, afterAvail)) return true;
	const fromLabs = getServiceLabels(getServicesFromAvailability(beforeAvail));
	const toLabs = getServiceLabels(getServicesFromAvailability(afterAvail));
	if ((fromLabs.length || toLabs.length) && fromLabs.join('\0') !== toLabs.join('\0')) return true;
	return (
		JSON.stringify(beforeAvail?.external_platforms ?? null) !==
		JSON.stringify(afterAvail?.external_platforms ?? null)
	);
}

/** Same summary shape as menu/category visibility override (POS/online + service chips). */
function buildItemVisibilityChangesSummary(
	beforeAvail: any,
	afterAvail: any
): OverrideChangesSummary | null {
	const fromLabs = getServiceLabels(getServicesFromAvailability(beforeAvail));
	const toLabs = getServiceLabels(getServicesFromAvailability(afterAvail));
	const changesSummary: OverrideChangesSummary = {};
	if (posOnlineVisibilityChanged(beforeAvail, afterAvail)) {
		changesSummary.posOnlinePreview = {
			from: formatPosOnlineAvailabilityPreview(beforeAvail),
			to: formatPosOnlineAvailabilityPreview(afterAvail),
		};
	}
	if (
		!changesSummary.posOnlinePreview &&
		(fromLabs.length || toLabs.length) &&
		fromLabs.join('\0') !== toLabs.join('\0')
	) {
		changesSummary.serviceTypes = { from: fromLabs, to: toLabs };
	}
	if (!changesSummary.posOnlinePreview && !changesSummary.serviceTypes) {
		const eb = JSON.stringify(beforeAvail?.external_platforms ?? null);
		const ea = JSON.stringify(afterAvail?.external_platforms ?? null);
		if (eb !== ea) {
			changesSummary.posOnlinePreview = {
				from: formatPosOnlineAvailabilityPreview(beforeAvail),
				to: formatPosOnlineAvailabilityPreview(afterAvail),
			};
		}
	}
	if (!changesSummary.posOnlinePreview && !changesSummary.serviceTypes) {
		return null;
	}
	return changesSummary;
}

function findItemInMenusV2(
	menus: any[],
	itemId: string
): { menuIndex: number; categoryId: string; item: any; globalIdx: number } | null {
	for (let menuIndex = 0; menuIndex < menus.length; menuIndex++) {
		const menu = menus[menuIndex];
		const items = menu.items || [];
		for (let globalIdx = 0; globalIdx < items.length; globalIdx++) {
			const it = items[globalIdx];
			if (it.id !== itemId) continue;
			const cat = menu.categories?.find((c: any) => c.id === it.category_id);
			if (!cat) return null;
			return { menuIndex, categoryId: cat.id, item: it, globalIdx };
		}
	}
	return null;
}

@inject("store")
@observer
export class RestaurantMenuListV2 extends MobxComponent<Props, State> {
	menu_backup: T.Schema.Restaurant.RestaurantSchema["menus"] | null;
	menu_backup_v2: T.Schema.Restaurant.RestaurantSchema["menus_v2"] | null = null;
	interval: number | null = null;
	_menuEditCollaborationActive = false;
	_unmounted = false;

	constructor(props: Props) {
		super(props);
		this.state = {
			syncing_pos: false,
			pos_sync_status: null,
			panel: null,
			panel_index: [],
			copy_type: null,
			copy_index: [],
			copy_loading: false,
			remove_type: null,
			remove_index: [],
			remove_loading: false,
			mode: "normal",
			loading: false,
			selected_dishes: [],
			selected_loading: false,
			error: null,
			activeDeleteItemModal: false,
			panel_pause: null,
			panel_index_pause: [],
			currentMenuItemId: "",
			currentMenuItem: null,
			currentMenuItems: [],
			resumeModalOpen: false,
			oneTimeRenderFlag: true,
			activeMobile: '',
			showBottomControl: '',
			activeMenuColorModal: false,
			activeMenuColorTitle: '',
			activeMenuColorData: {
				color: '',
				media_url: '',
				title: ''
			},
			targetIndex: -1,
			targetCategoryIndex: -1,
			targetMenuIndex: -1,
			history_menu_id: null,
			targetItem: 'menu',
			activeMenuVisibilityModal: false,
			activeMenuPrevisibilityModal: false,
			menuColorBulkItemIds: null,
			selectedPrinterForEligible: null,
			printerAssignTarget: null,
			printerAssignBulkItemIds: null,
			receiptPrinterAssignItem: null,
			bulkSelectedDishesSheetDismissed: false,
			preOverrideContext: null,
			hideMoreActionsOnMobile: true,
			menuChannelFilterPos: false,
			menuChannelFilterOnline: false,
			categorySortDirections: {},
			itemSortDirections: {},
	};
		this.menu_backup = null;
		this.interval = null;
	}

	_maybeStartOrStopMenuEditTracking() {
		const { store } = this.injected;
		const want =
			store.organisation?.dashboard === 'v2.1' &&
			store.isNewMenuSchema;
		if (want && !this._menuEditCollaborationActive) {
			this._menuEditCollaborationActive = true;
			store.notifications.menuEditingMenusMounted();
		} else if (!want && this._menuEditCollaborationActive) {
			store.notifications.menuEditingMenusUnmounted();
			this._menuEditCollaborationActive = false;
		}
	}

	async componentDidMount(): Promise<void> {
		const { store } = this.injected;
		const { loadMenusOnMount = true } = this.props;
		this.loadPOSSyncStatus();
		// Unconditional, unlike the edit lock: seeing who else is here cannot conflict with anything.
		store.notifications.menuPresenceMounted(null);
		if (store.isNewMenuSchema && loadMenusOnMount) {
			const restaurant = store.restaurant!;
			this.setState({
				loading: true
			});
			const get_menus = await store.api.menu_get_menus(restaurant._id!);
			store.updateRestaurant({
				menus_v2: store.isNewMenuSchema ? get_menus.menus : [], //v2.0
				menus: !store.isNewMenuSchema ? get_menus.menus: [], //v1.0
				option_sets_v2: store.isNewMenuSchema ? get_menus.modifiers : [], //v2.0
				option_sets: !store.isNewMenuSchema ? get_menus.option_sets: [], //v1.0
			});
			this.setState({
				loading: false
			});
		}

		this._maybeStartOrStopMenuEditTracking();
	}

	componentDidUpdate() {
		this._maybeStartOrStopMenuEditTracking();
	}

	/** Persist menu/category color only (used before descendant propagation or on Keep current). */
	persistParentMenuColor = async (pending: MenuPendingOverride, menus: any[]) => {
		const store = this.injected.store;
		const r = store.restaurant!;
		const menu = menus[pending.menuIndex];
		if (!menu || menu.id !== pending.menuId || pending.newColor == null) return;
		if (pending.targetEntityType === 'category' && pending.categoryId) {
			const ix = menu.categories?.findIndex((c: any) => c.id === pending.categoryId) ?? -1;
			const cat = ix >= 0 ? menu.categories[ix] : null;
			if (!cat || ix < 0) return;
			const toUpdateCategory = {
				color: pending.newColor,
				media_url: toMenuApiMediaUrlValue(pending.newMediaUrl),
			} as any;
			await store.api.menu_partial_update_category({
				restaurant_id: r._id,
				menu_id: menu.id,
				category_id: cat.id,
				data: toUpdateCategory,
			});
			const copyCategories = [...menu.categories];
			copyCategories[ix] = {
				...copyCategories[ix],
				color: pending.newColor,
				media_url: pending.newMediaUrl ?? '',
			};
			menus[pending.menuIndex] = { ...menu, categories: copyCategories };
		} else if (pending.targetEntityType === 'menu') {
			const toUpdateMenu = {
				color: pending.newColor,
				media_url: toMenuApiMediaUrlValue(pending.newMediaUrl),
			} as any;
			await store.api.menu_partial_update_menu({
				restaurant_id: r._id,
				menu_id: menu.id,
				data: toUpdateMenu,
			});
			menus[pending.menuIndex] = {
				...menu,
				color: pending.newColor,
				media_url: pending.newMediaUrl ?? '',
			};
		} else if (pending.targetEntityType === 'item' && pending.categoryId && pending.itemId) {
			const cat = menu.categories?.find((c: any) => c.id === pending.categoryId);
			const itemIdx = menu.items?.findIndex((i: any) => i.id === pending.itemId) ?? -1;
			if (!cat || itemIdx < 0) return;
			try {
				const toUpdateItem = { color: pending.newColor } as any;
				if(pending.newMediaUrl) {
					toUpdateItem.media_url = toMenuApiMediaUrlValue(pending.newMediaUrl);
				}
				await store.api.menu_partial_update_item({
					restaurant_id: r._id,
					menu_id: menu.id,
					category_id: cat.id,
					item_id: pending.itemId,
					data: toUpdateItem,
				});
				const nextItems = [...(menu.items || [])];
				nextItems[itemIdx] = {
					...nextItems[itemIdx],
					color: pending.newColor,
					media_url: pending.newMediaUrl ?? '',
				};
				menus[pending.menuIndex] = { ...menu, items: nextItems };
			} catch (e) {
				throw new Error('Failed to update item');
			}
		}
	};

	/**
	 * Menu/category forms: defer save when POS/online changes would affect descendants with their own availability.
	 */
	v2FormVisibilityDelegate = async (args: {
		entity: 'menu' | 'category';
		menuIndex: number;
		menuId: string;
		categoryId?: string;
		categoryIndex?: number;
		nextConditions: any;
		entityName: string;
	}): Promise<boolean> => {
		const store = this.injected.store;
		const copyMenus = cloneDeep(store.restaurant!.menus_v2 || []);
		const menu = copyMenus[args.menuIndex];
		if (!menu || menu.id !== args.menuId) return false;

		const normalized = normalizeMenuVisibilityConditionsForSave({
			pos: args.nextConditions?.pos,
			online: args.nextConditions?.online,
			external_platforms:
				args.nextConditions?.external_platforms ?? {
					uber: { enabled: false },
					abacus: { enabled: false },
				},
		});

		if (!menuDefinesPosOnlineBlocks(normalized).defines) return false;

		const preMergeAvailById = new Map<string, any>();
		(menu.availabilities || []).forEach((a: any) => preMergeAvailById.set(a.id, cloneDeep(a)));

		if (args.entity === 'menu') {
			const hasCategories = (menu.categories?.length ?? 0) > 0;
			const hasItems = (menu.items?.length ?? 0) > 0;
			if (!hasCategories && !hasItems) return false;

			const menuAvailSlotId = args.nextConditions?.id || menu.availability_id;
			const idx = menu.availabilities?.findIndex((a: any) => a.id === menuAvailSlotId) ?? -1;
			if (idx < 0 || !menu.availabilities?.[idx]) return false;
			copyMenus[args.menuIndex].availabilities[idx] = {
				...menu.availabilities[idx],
				...normalized,
			} as any;
			const menuForAnalysis = copyMenus[args.menuIndex];
			const mergedParentAvailability = menuForAnalysis.availabilities[idx];
			const mergedParentAvailabilityId = menuAvailSlotId || '';
			const { affectedRows, changesSummary } = analyzeDescendantsOverride({
				menu: menuForAnalysis,
				targetType: 'menu',
				editKind: 'visibility',
				preMergeAvailById,
				mergedParentAvailability,
				mergedParentAvailabilityId,
			});
			const shouldOpenPreoverride = affectedRows.length > 0 || hasCategories || hasItems;
			if (!shouldOpenPreoverride) return false;
			const visibilityAffectedRows =
				affectedRows.length > 0
					? affectedRows
					: [
							...(menu.categories ?? []).map((c: any) => ({
								entityType: 'category' as const,
								name: c?.name ?? '—',
								id: c?.id,
							})),
							...(menu.items ?? []).map((i: any) => ({
								entityType: 'item' as const,
								name: i?.name ?? '—',
								id: i?.id,
							})),
					  ];

			const pending: MenuPendingOverride = {
				kind: 'visibility',
				targetEntityType: 'menu',
				menuIndex: args.menuIndex,
				menuId: menu.id,
				parentAvailabilityId: mergedParentAvailabilityId,
				parentMergedAvailability: mergedParentAvailability,
				awaitingParentPersist: true,
				visibilityConditions: normalized,
			};

			this.setState({
				preOverrideContext: {
					targetEntity: {
						type: 'menu',
						name: args.entityName,
						categoryCount: menu.categories?.length ?? 0,
						itemCount: menu.items?.length ?? 0,
					},
					overrideSource: { menuId: menu.id },
					changesSummary,
					editKind: 'visibility',
					affectedEntities: visibilityAffectedRows,
					pendingOverride: pending,
				},
			});
			this.injected.store.setActiveMobileModal('desktop-menu-preoverride');
			return true;
		}

		const cat =
			args.categoryIndex != null
				? menu.categories?.[args.categoryIndex]
				: menu.categories?.find((c: any) => c.id === args.categoryId);
		if (!cat || (args.categoryId && cat.id !== args.categoryId)) return false;

		const itemsInCategory = (menu.items ?? []).filter((i: any) => i.category_id === cat.id);
		if (itemsInCategory.length === 0) return false;

		const catAvailSlotId = args.nextConditions?.id || effectiveCategoryAvailabilityId(menu, cat);
		const cidx = menu.availabilities?.findIndex((a: any) => a.id === catAvailSlotId) ?? -1;
		if (cidx < 0 || !menu.availabilities?.[cidx]) return false;
		copyMenus[args.menuIndex].availabilities[cidx] = {
			...menu.availabilities[cidx],
			...normalized,
		} as any;
		const menuForAnalysis = copyMenus[args.menuIndex];
		const mergedParentAvailability = menuForAnalysis.availabilities[cidx];
		const mergedParentAvailabilityId = catAvailSlotId || '';
		const { affectedRows, changesSummary } = analyzeDescendantsOverride({
			menu: menuForAnalysis,
			targetType: 'category',
			categoryId: cat.id,
			editKind: 'visibility',
			preMergeAvailById,
			mergedParentAvailability,
			mergedParentAvailabilityId,
		});
		const shouldOpenPreoverride = affectedRows.length > 0 || itemsInCategory.length > 0;
		if (!shouldOpenPreoverride) return false;
		const visibilityAffectedRows =
			affectedRows.length > 0
				? affectedRows
				: itemsInCategory.map((i: any) => ({
						entityType: 'item' as const,
						name: i?.name ?? '—',
						id: i?.id,
				  }));

		const pending: MenuPendingOverride = {
			kind: 'visibility',
			targetEntityType: 'category',
			menuIndex: args.menuIndex,
			menuId: menu.id,
			categoryId: cat.id,
			parentAvailabilityId: mergedParentAvailabilityId,
			parentMergedAvailability: mergedParentAvailability,
			awaitingParentPersist: true,
			visibilityConditions: normalized,
		};

		this.setState({
			preOverrideContext: {
				targetEntity: {
					type: 'category',
					name: args.entityName,
					itemCount: itemsInCategory.length,
				},
				overrideSource: { menuId: menu.id, categoryId: cat.id },
				changesSummary,
				editKind: 'visibility',
				affectedEntities: visibilityAffectedRows,
				pendingOverride: pending,
			},
		});
		this.injected.store.setActiveMobileModal('desktop-menu-preoverride');
		return true;
	};

	v2FormPrintersDelegate = async (args: {
		entity: 'menu' | 'category';
		menuIndex: number;
		menuId: string;
		categoryId?: string;
		categoryIndex?: number;
		nextPosPrinters: readonly any[];
		entityName: string;
	}): Promise<boolean> => {
		const store = this.injected.store;
		const copyMenus = cloneDeep(store.restaurant!.menus_v2 || []);
		const menu = copyMenus[args.menuIndex];
		if (!menu || menu.id !== args.menuId) return false;

		const preMergeAvailById = new Map<string, any>();
		(menu.availabilities || []).forEach((a: any) => preMergeAvailById.set(a.id, cloneDeep(a)));
		const menuAvailability =
			menu.availabilities?.find((a: any) => a.id === menu.availability_id) ??
			menu.availabilities?.[0] ??
			{};
		const mergedParentAvailabilityId = menu.availability_id || menuAvailability?.id || '';

		if (args.entity === 'menu') {
			const hasCategories = (menu.categories?.length ?? 0) > 0;
			const hasItems = (menu.items?.length ?? 0) > 0;
			if (!hasCategories && !hasItems) return false;

			const { affectedRows, changesSummary } = analyzeDescendantsOverride({
				menu,
				targetType: 'menu',
				editKind: 'printers',
				preMergeAvailById,
				mergedParentAvailability: menuAvailability,
				mergedParentAvailabilityId,
				newPosPrinters: args.nextPosPrinters,
			});
			if (affectedRows.length === 0) return false;

			const pending: MenuPendingOverride = {
				kind: 'printers',
				targetEntityType: 'menu',
				menuIndex: args.menuIndex,
				menuId: menu.id,
				parentAvailabilityId: mergedParentAvailabilityId,
				parentMergedAvailability: menuAvailability,
				newPosPrinters: args.nextPosPrinters,
				awaitingParentPersist: true,
			};

			this.setState({
				preOverrideContext: {
					targetEntity: {
						type: 'menu',
						name: args.entityName,
						categoryCount: menu.categories?.length ?? 0,
						itemCount: menu.items?.length ?? 0,
					},
					overrideSource: { menuId: menu.id },
					changesSummary,
					editKind: 'printers',
					affectedEntities: affectedRows,
					pendingOverride: pending,
				},
			});
			this.injected.store.setActiveMobileModal('desktop-menu-preoverride');
			return true;
		}

		const cat =
			args.categoryIndex != null
				? menu.categories?.[args.categoryIndex]
				: menu.categories?.find((c: any) => c.id === args.categoryId);
		if (!cat || (args.categoryId && cat.id !== args.categoryId)) return false;
		const itemsInCategory = (menu.items ?? []).filter((i: any) => i.category_id === cat.id);
		if (itemsInCategory.length === 0) return false;

		const catAvailId = effectiveCategoryAvailabilityId(menu, cat) || mergedParentAvailabilityId;
		const categoryAvailability =
			menu.availabilities?.find((a: any) => a.id === catAvailId) ?? menuAvailability;
		const { affectedRows, changesSummary } = analyzeDescendantsOverride({
			menu,
			targetType: 'category',
			categoryId: cat.id,
			editKind: 'printers',
			preMergeAvailById,
			mergedParentAvailability: categoryAvailability,
			mergedParentAvailabilityId: catAvailId,
			newPosPrinters: args.nextPosPrinters,
		});
		if (affectedRows.length === 0) return false;

		const pending: MenuPendingOverride = {
			kind: 'printers',
			targetEntityType: 'category',
			menuIndex: args.menuIndex,
			menuId: menu.id,
			categoryId: cat.id,
			parentAvailabilityId: catAvailId,
			parentMergedAvailability: categoryAvailability,
			newPosPrinters: args.nextPosPrinters,
			awaitingParentPersist: true,
		};

		this.setState({
			preOverrideContext: {
				targetEntity: {
					type: 'category',
					name: args.entityName,
					itemCount: itemsInCategory.length,
				},
				overrideSource: { menuId: menu.id, categoryId: cat.id },
				changesSummary,
				editKind: 'printers',
				affectedEntities: affectedRows,
				pendingOverride: pending,
			},
		});
		this.injected.store.setActiveMobileModal('desktop-menu-preoverride');
		return true;
	};

	/**
	 * API save for menu/category visibility plus pos/online merge — no descendant override modal.
	 * Used when there are no inheriting descendants to warn about, or from Keep current on the override modal.
	 * `persistOnly`: update store only (no toasts / preOverrideContext) — first step before descendant override apply.
	 */
	commitMenuCategoryVisibilitySave = async (
		conditions: any,
		opts?: { persistOnly?: boolean }
	) => {
		const store = this.injected.store;
		const r = store.restaurant!;
		const baseCtx = this.state.preOverrideContext;
		const overrideSource = baseCtx?.overrideSource;
		const overrideTargetEntity = baseCtx?.targetEntity;
		if (!overrideSource?.menuId || !overrideTargetEntity || !baseCtx) {
			return;
		}

		const copyMenus = cloneDeep(r.menus_v2 || []);
		const targetMenuIndex = copyMenus.findIndex((menu: any) => menu.id === overrideSource.menuId);
		if (targetMenuIndex < 0) return;

		const targetMenu = copyMenus[targetMenuIndex];

		const targetCategoryIndex = targetMenu.categories.findIndex(
			(category: any) => category.id === overrideSource.categoryId
		);
		const targetCategory =
			targetCategoryIndex >= 0 ? targetMenu.categories[targetCategoryIndex] : null;
		const targetItemIndex = targetMenu.items.findIndex((item: any) => item.id === overrideSource.itemId);
		const targetItemRow = targetItemIndex >= 0 ? targetMenu.items[targetItemIndex] : null;

		const targetCategoryAvailabilityIndex =
			targetCategory
				? targetMenu.availabilities.findIndex(
						(a: any) => a.id === effectiveCategoryAvailabilityId(targetMenu, targetCategory)
					)
				: -1;
		const targetCategoryAvailability =
			targetCategoryAvailabilityIndex >= 0
				? targetMenu.availabilities[targetCategoryAvailabilityIndex]
				: null;

		const targetMenuAvailabilityIndex = targetMenu.availabilities.findIndex(
			(a: any) => a.id === targetMenu.availability_id
		);
		const targetMenuAvailability = targetMenu.availabilities[targetMenuAvailabilityIndex];

		let mergedParentAvailability: any;
		let mergedParentAvailabilityId: string;

		const visibilityNorm = normalizeMenuVisibilityConditionsForSave(conditions);

		if (overrideTargetEntity.type === 'item') {
			if (!overrideSource.categoryId || !overrideSource.itemId || !targetCategory || !targetItemRow) {
				UI.notification.error('Could not update item visibility');
				return;
			}
			const effItemAid = effectiveItemAvailabilityId(targetMenu, targetCategory, targetItemRow);
			const targetItemAvailabilityIndex =
				effItemAid != null
					? targetMenu.availabilities.findIndex((a: any) => a.id === effItemAid)
					: -1;
			const targetItemAvailability =
				targetItemAvailabilityIndex >= 0
					? targetMenu.availabilities[targetItemAvailabilityIndex]
					: null;
			if (targetItemAvailabilityIndex < 0 || !targetItemAvailability) {
				UI.notification.error('Could not update item visibility');
				return;
			}
			const vnorm = visibilityNorm;
			let itemAvailIdOut = targetItemRow.availability_id;
			let slotPayload: any;

			if (itemInheritsParentAvailability(targetMenu, targetCategory, targetItemRow)) {
				const newAvailId = shortid.generate();
				const newSlot = buildForkedAvailabilityRowWithVisibility(
					targetItemAvailability,
					conditions,
					newAvailId
				);
				const nextAvails = [...(copyMenus[targetMenuIndex].availabilities || []), newSlot];
				await store.api.menu_partial_update_menu({
					restaurant_id: r._id,
					menu_id: targetMenu.id,
					data: { availabilities: nextAvails },
				});
				copyMenus[targetMenuIndex].availabilities = nextAvails;
				copyMenus[targetMenuIndex].items = (copyMenus[targetMenuIndex].items || []).map((it: any) =>
					it.id === targetItemRow.id ? { ...it, availability_id: newAvailId } : it
				);
				itemAvailIdOut = newAvailId;
				slotPayload = newSlot;
			} else {
				const mergedSlot = { ...cloneDeep(targetItemAvailability), ...vnorm } as any;
				copyMenus[targetMenuIndex].availabilities[targetItemAvailabilityIndex] = mergedSlot as any;
				slotPayload = mergedSlot;
			}

			await store.api.menu_partial_update_item({
				restaurant_id: r._id,
				menu_id: targetMenu.id,
				category_id: targetCategory.id,
				item_id: targetItemRow.id,
				data: {
					availability: slotPayload as any,
					availability_id: itemAvailIdOut,
				},
			});

			const menuFinalItem = copyMenus[targetMenuIndex];
			const rItem = store.restaurant!;
			const updatedItemRow =
				(copyMenus[targetMenuIndex].items || []).find((it: any) => it.id === targetItemRow.id) ||
				targetItemRow;
			store.setRestaurant({ ...rItem, menus_v2: copyMenus });
			if (!opts?.persistOnly) {
				UI.notification.success('Item updated');
				this.setState({
					activeMenuVisibilityModal: false,
					activeMenuPrevisibilityModal: false,
					preOverrideContext: refreshedPreOverrideForVisibility(
						baseCtx,
						menuFinalItem,
						overrideTargetEntity.type,
						targetCategory,
						updatedItemRow
					),
				});
			}
			return;
		}

		if (overrideTargetEntity.type === 'category') {
			if (!targetCategory || targetCategoryAvailabilityIndex < 0 || !targetCategoryAvailability) {
				UI.notification.error('Could not update category visibility');
				return;
			}
			const catEffSlotId = effectiveCategoryAvailabilityId(targetMenu, targetCategory)!;
			const catBaseAvail = targetCategoryAvailability;

			if (categoryInheritsMenuAvailability(targetMenu, targetCategory)) {
				const newAvailId = shortid.generate();
				const newSlot = buildForkedAvailabilityRowWithVisibility(catBaseAvail, conditions, newAvailId);
				const nextAvails = [...(copyMenus[targetMenuIndex].availabilities || []), newSlot];
				await store.api.menu_partial_update_menu({
					restaurant_id: r._id,
					menu_id: targetMenu.id,
					data: { availabilities: nextAvails },
				});
				copyMenus[targetMenuIndex].availabilities = nextAvails;
				await store.api.menu_partial_update_category({
					restaurant_id: r._id,
					menu_id: targetMenu.id,
					category_id: targetCategory.id,
					data: {
						availability: newSlot as any,
						availability_id: newAvailId,
					},
				});
				copyMenus[targetMenuIndex].categories = copyMenus[targetMenuIndex].categories.map((c: any) =>
					c.id === targetCategory.id ? { ...c, availability_id: newAvailId } : c
				);
				mergedParentAvailability = newSlot;
				mergedParentAvailabilityId = newAvailId;
			} else {
				const mergedSlot = { ...cloneDeep(catBaseAvail), ...visibilityNorm } as any;
				await store.api.menu_partial_update_category({
					restaurant_id: r._id,
					menu_id: targetMenu.id,
					category_id: targetCategory.id,
					data: {
						availability: mergedSlot,
						availability_id: targetCategory.availability_id,
					},
				});
				copyMenus[targetMenuIndex].availabilities[targetCategoryAvailabilityIndex] = mergedSlot as any;
				mergedParentAvailability = mergedSlot;
				mergedParentAvailabilityId = targetCategory.availability_id!;
			}
			if (!opts?.persistOnly) {
				UI.notification.success('Category updated');
			}
		} else {
			if (targetMenuAvailabilityIndex < 0 || !targetMenuAvailability) {
				UI.notification.error('Could not update menu visibility');
				return;
			}
			const mergedMenuSlot = { ...cloneDeep(targetMenuAvailability), ...visibilityNorm } as any;
			await store.api.menu_partial_update_menu({
				restaurant_id: r._id,
				menu_id: targetMenu.id,
				data: {
					availability: mergedMenuSlot,
					availability_id: targetMenu.availability_id,
				},
			});
			copyMenus[targetMenuIndex].availabilities[targetMenuAvailabilityIndex] = mergedMenuSlot as any;
			mergedParentAvailability = mergedMenuSlot;
			mergedParentAvailabilityId = targetMenu.availability_id!;
			if (!opts?.persistOnly) {
				UI.notification.success('Menu updated');
			}
		}

		const shouldPropagateDescendants = store.isNewMenuSchema && !opts?.persistOnly;
		if (shouldPropagateDescendants) {
			const menuRef = copyMenus[targetMenuIndex];
			const categoriesToUpdate: any[] = [];
			const itemsToUpdate: any[] = [];
			const localAvailabilityPatches: Array<{ availId: string; availability: any }> = [];

			if (overrideTargetEntity.type === 'menu') {
				const menuConditionsBeforeApi = mergedParentAvailability!;
				const menuAvailId = menuConditionsBeforeApi?.id ?? menuRef.availability_id;
				const { defines: menuDefinesPosOnline, pos: menuSourcePos, online: menuSourceOnline } =
					menuDefinesPosOnlineBlocks(menuConditionsBeforeApi);
				const resolveMenuAvail = (aid: string | undefined) =>
					aid && menuRef.availabilities
						? (menuRef.availabilities as any[]).find((a: any) => a.id === aid) ?? null
						: null;
				if (menuDefinesPosOnline && menuAvailId) {
					for (const c of menuRef.categories || []) {
						const categoryId = getId(c);
						const catAid = c.availability_id || menuAvailId;
						if (catAid !== menuAvailId) {
							const resolvedCat = resolveMenuAvail(catAid);
							const mergedCatAvail = mergeAvailabilityWithPosOnline(
								resolvedCat,
								menuSourcePos,
								menuSourceOnline
							);
							if (mergedCatAvail != null) {
								categoriesToUpdate.push({
									menu_id: menuRef.id,
									id: categoryId,
                  availability: mergedCatAvail,
                  availability_id: catAid,
								});
								localAvailabilityPatches.push({ availId: catAid, availability: mergedCatAvail });
							}
						}
						for (const d of (menuRef.items || []).filter(
							(item: any) => item.category_id === categoryId
						)) {
							const itemAid = d.availability_id || catAid;
							if (itemAid === menuAvailId || itemAid === catAid) continue;
							const resolvedItem = resolveMenuAvail(itemAid);
							const mergedItemAvail = mergeAvailabilityWithPosOnline(
								resolvedItem,
								menuSourcePos,
								menuSourceOnline
							);
							if (mergedItemAvail == null) continue;
							itemsToUpdate.push({
								menu_id: menuRef.id,
								category_id: categoryId,
								id: getId(d),
                availability: mergedItemAvail,
                availability_id: itemAid,
							});
							localAvailabilityPatches.push({ availId: itemAid, availability: mergedItemAvail });
						}
					}
				}
			} else if (overrideTargetEntity.type === 'category') {
				const { defines: categoryDefinesPosOnline, pos: catSourcePos, online: catSourceOnline } =
					menuDefinesPosOnlineBlocks(mergedParentAvailability);
				if (categoryDefinesPosOnline) {
					const categoryAvailId =
						mergedParentAvailability?.id ?? targetCategory?.availability_id;
					if (categoryAvailId && targetCategory) {
						const menuId = menuRef.id;
						const categoryId = targetCategory.id;
						for (const d of (menuRef.items || []).filter(
							(i: any) => i.category_id === categoryId
						)) {
							const itemAid = d.availability_id || categoryAvailId;
							if (itemAid === categoryAvailId) continue;
							const resolved = (menuRef.availabilities || []).find(
								(a: any) => a.id === itemAid
							);
							const mergedItemAvail = mergeAvailabilityWithPosOnline(
								resolved,
								catSourcePos,
								catSourceOnline
							);
							if (mergedItemAvail == null) continue;
							itemsToUpdate.push({
								menu_id: menuId,
								category_id: categoryId,
								id: getId(d),
                availability: mergedItemAvail,
                availability_id: itemAid,
							});
							localAvailabilityPatches.push({ availId: itemAid, availability: mergedItemAvail });
						}
					}
				}
			}

			if (categoriesToUpdate.length > 0) {
				await store.api.menu_partial_update_batch_categories({
					restaurant_id: r._id,
					data: { categories: categoriesToUpdate },
				});
			}
			if (itemsToUpdate.length > 0) {
				await store.api.menu_partial_update_batch_items({
					restaurant_id: r._id,
					data: { items: itemsToUpdate },
				});
			}
			for (const { availId, availability } of localAvailabilityPatches) {
				patchAvailabilitySlot(menuRef, availId, availability);
			}
		}

		const menuFinal = copyMenus[targetMenuIndex];
		const rLive = store.restaurant!;
		store.setRestaurant({ ...rLive, menus_v2: copyMenus });

		if (!opts?.persistOnly) {
			this.setState({
				activeMenuVisibilityModal: false,
				activeMenuPrevisibilityModal: false,
				preOverrideContext: refreshedPreOverrideForVisibility(
					baseCtx,
					menuFinal,
					overrideTargetEntity.type,
					targetCategory,
					targetItemRow
				),
			});
		}
	};

	commitMenuCategoryPrintersSave = async (
		nextPosPrinters: readonly any[],
		opts?: { persistOnly?: boolean }
	) => {
		const store = this.injected.store;
		const r = store.restaurant!;
		const baseCtx = this.state.preOverrideContext;
		const overrideSource = baseCtx?.overrideSource;
		const overrideTargetEntity = baseCtx?.targetEntity;
		if (!overrideSource?.menuId || !overrideTargetEntity || !baseCtx) return;

		const copyMenus = cloneDeep(r.menus_v2 || []);
		const targetMenuIndex = copyMenus.findIndex((menu: any) => menu.id === overrideSource.menuId);
		if (targetMenuIndex < 0) return;
		const targetMenu = copyMenus[targetMenuIndex];

		if (overrideTargetEntity.type === 'menu') {
			await store.api.menu_partial_update_menu({
				restaurant_id: r._id,
				menu_id: targetMenu.id,
				data: {
					integrations: {
						pushpos: {
							...(targetMenu as any).integrations?.pushpos,
							printers: nextPosPrinters,
						},
					},
				},
			});
			(targetMenu as any).integrations = (targetMenu as any).integrations || {};
			(targetMenu as any).integrations.pushpos = {
				...((targetMenu as any).integrations?.pushpos || {}),
				printers: nextPosPrinters,
			};
			if (!opts?.persistOnly) UI.notification.success('Menu updated');
		} else if (overrideTargetEntity.type === 'category' && overrideSource.categoryId) {
			const targetCategory = targetMenu.categories?.find((c: any) => c.id === overrideSource.categoryId);
			if (!targetCategory) return;
			await store.api.menu_partial_update_category({
				restaurant_id: r._id,
				menu_id: targetMenu.id,
				category_id: targetCategory.id,
				data: {
					integrations: {
						pushpos: {
							...(targetCategory as any).integrations?.pushpos,
							printers: nextPosPrinters,
						},
					},
				},
			});
			(targetCategory as any).integrations = (targetCategory as any).integrations || {};
			(targetCategory as any).integrations.pushpos = {
				...((targetCategory as any).integrations?.pushpos || {}),
				printers: nextPosPrinters,
			};
			if (!opts?.persistOnly) UI.notification.success('Category updated');
		}

		const rLive = store.restaurant!;
		store.setRestaurant({ ...rLive, menus_v2: copyMenus });
	};

	applyPendingMenuOverride = async () => {
		const ctx = this.state.preOverrideContext;
		const pending = ctx?.pendingOverride;
		const store = this.injected.store;
		const r = store.restaurant;
		if (!pending || !r?._id) return;

		let menus = cloneDeep(r.menus_v2 || []);

		if (
			pending.targetEntityType === 'item' &&
			pending.bulkItemRefs &&
			pending.bulkItemRefs.length > 0
		) {
			try {
				if (pending.kind === 'color' && pending.newColor != null) {
					const itemUpdates: any[] = [];
					for (const ref of pending.bulkItemRefs) {
						const menu = menus.find((m: any) => m.id === ref.menu_id);
						const itemIdx = menu?.items?.findIndex((i: any) => i.id === ref.id) ?? -1;
						if (!menu || itemIdx < 0) continue;
						const item = menu.items[itemIdx];
						const category = menu.categories?.find((c: any) => c.id === ref.category_id);
						if (!itemDisplayColorWouldChange(item, category, menu, pending.newColor!)) continue;
						const prevMedia = item.media_url || '';
						itemUpdates.push({
							id: item.id,
							menu_id: menu.id,
							category_id: ref.category_id,
							color: pending.newColor,
							media_url: toMenuApiMediaUrlValue(prevMedia),
						});
						const nextItems = [...menu.items];
						nextItems[itemIdx] = {
							...nextItems[itemIdx],
							color: pending.newColor,
							media_url: prevMedia,
						};
						const menuIx = menus.findIndex((m: any) => m.id === menu.id);
						if (menuIx >= 0) menus[menuIx] = { ...menu, items: nextItems };
					}
					if (itemUpdates.length > 0) {
						await store.api.menu_partial_update_batch_items({
							restaurant_id: r._id,
							data: { items: itemUpdates },
						});
						await store.updateRestaurant({ menus_v2: menus });
					}
					UI.notification.success(
						itemUpdates.length === 1 ? 'Item updated' : `${itemUpdates.length} items updated`,
					);
					return;
				}
				if (pending.kind === 'printers' && pending.newPosPrinters != null) {
					const itemUpdates: any[] = [];
					for (const ref of pending.bulkItemRefs) {
						const menu = menus.find((m: any) => m.id === ref.menu_id);
						const itemIdx = menu?.items?.findIndex((i: any) => i.id === ref.id) ?? -1;
						if (!menu || itemIdx < 0) continue;
						const item = menu.items[itemIdx];
						if (!itemPosPrintersWouldChange(item, pending.newPosPrinters)) continue;
						itemUpdates.push({
							id: item.id,
							menu_id: menu.id,
							category_id: ref.category_id,
							integrations: {
								pushpos: {
									...(item as any).integrations?.pushpos,
									printers: pending.newPosPrinters,
								},
							},
						});
						const nextItems = [...menu.items];
						nextItems[itemIdx] = {
							...nextItems[itemIdx],
							//@ts-ignore
							integrations: {
								...(nextItems[itemIdx] as any).integrations,
								pushpos: {
									...((nextItems[itemIdx] as any).integrations?.pushpos || {}),
									printers: pending.newPosPrinters,
								},
							},
						};
						const menuIx = menus.findIndex((m: any) => m.id === menu.id);
						if (menuIx >= 0) menus[menuIx] = { ...menu, items: nextItems };
					}
					if (itemUpdates.length > 0) {
						await store.api.menu_partial_update_batch_items({
							restaurant_id: r._id,
							data: { items: itemUpdates },
						});
						await store.updateRestaurant({ menus_v2: menus });
					}
					UI.notification.success(
						itemUpdates.length > 1 ? `Printers updated for ${itemUpdates.length} items` : 'Printers updated',
					);
					return;
				}
			} catch (e) {
				UI.notification.error((e as any)?.message ?? 'Failed to apply overrides');
				return;
			}
		}

		if (pending.kind === 'visibility' && pending.awaitingParentPersist && pending.visibilityConditions != null) {
			await this.commitMenuCategoryVisibilitySave(pending.visibilityConditions, { persistOnly: true });
			menus = cloneDeep(store.restaurant!.menus_v2 || []);
		}
		if (pending.kind === 'printers' && pending.awaitingParentPersist && pending.newPosPrinters != null) {
			await this.commitMenuCategoryPrintersSave(pending.newPosPrinters, { persistOnly: true });
			menus = cloneDeep(store.restaurant!.menus_v2 || []);
		}
		if (pending.kind === 'color' && pending.awaitingParentPersist && pending.newColor != null) {
			try {
			await this.persistParentMenuColor(pending, menus);
			await store.updateRestaurant({ menus_v2: menus });
			menus = cloneDeep(store.restaurant!.menus_v2 || []);
			} catch (e) {
				UI.notification.error((e as any)?.message ?? 'Failed to persist parent menu color');
				return;
			}
		}

		const menu = menus[pending.menuIndex];
		if (!menu || menu.id !== pending.menuId) return;

		try {
      const itemsToUpdate: any[] = [];
      const categoriesToUpdate: any[] = [];
			if (pending.kind === 'visibility') {
				let merged = pending.parentMergedAvailability;
				let aid = pending.parentAvailabilityId;
				if (pending.targetEntityType === 'menu') {
					let availabilitiesChanged = false;
					for (const cat of menu.categories ?? []) {
						const newChildAvailId = shortid.generate();
						const forkedChildAvailability = { ...cloneDeep(merged), id: newChildAvailId };
						menu.availabilities = [...(menu.availabilities || []), forkedChildAvailability];
						availabilitiesChanged = true;
						categoriesToUpdate.push({
							menu_id: menu.id,
							id: cat.id,
							availability: { ...forkedChildAvailability },
              availability_id: newChildAvailId,
						});
						cat.availability_id = newChildAvailId;
					}
					for (const item of menu.items ?? []) {
						const cat = menu.categories?.find((c: any) => c.id === item.category_id);
						if (!cat) continue;
						const newChildAvailId = shortid.generate();
						const forkedChildAvailability = { ...cloneDeep(merged), id: newChildAvailId };
						menu.availabilities = [...(menu.availabilities || []), forkedChildAvailability];
						availabilitiesChanged = true;
						itemsToUpdate.push({
							menu_id: menu.id,
							category_id: cat.id,
							id: item.id,
              availability: { ...forkedChildAvailability },
              availability_id: newChildAvailId,
						});
						item.availability_id = newChildAvailId;
					}
					// Persist all forked availabilities in a single menu PATCH before the batch
					// category/item updates fire (which reference the new availability_ids).
					if (availabilitiesChanged) {
						await store.api.menu_partial_update_menu({
							restaurant_id: r._id,
							menu_id: menu.id,
							data: { availabilities: menu.availabilities },
						});
					}
				}
				else if (pending.targetEntityType === 'category' && pending.categoryId) {
					const cat = menu.categories?.find((c: any) => c.id === pending.categoryId);
					if (!cat) return;
					// When category initially inherits menu, persistOnly step may fork a new category
					// availability slot. Re-resolve parent slot from latest store snapshot before
					// applying descendant item overrides.
					const currentCategoryAid = cat.availability_id || aid;
					const currentCategoryAvail =
						(menu.availabilities || []).find((a: any) => a.id === currentCategoryAid) || merged;
					aid = currentCategoryAid;
					merged = currentCategoryAvail;
					let updatedItemCount = 0;
					let availabilitiesChanged = false;
					for (const item of (menu.items ?? []).filter((i: any) => i.category_id === pending.categoryId)) {
						const newChildAvailId = shortid.generate();
						const forkedChildAvailability = { ...cloneDeep(merged), id: newChildAvailId };
						menu.availabilities = [...(menu.availabilities || []), forkedChildAvailability];
						availabilitiesChanged = true;
						itemsToUpdate.push({
							menu_id: menu.id,
							category_id: cat.id,
							id: item.id,
							availability: { ...forkedChildAvailability },
              availability_id: newChildAvailId,
						});
						item.availability_id = newChildAvailId;
						updatedItemCount += 1;
					}
					if (availabilitiesChanged) {
						await store.api.menu_partial_update_menu({
							restaurant_id: r._id,
							menu_id: menu.id,
							data: { availabilities: menu.availabilities },
						});
					}
				}
			}
      else if (pending.kind === 'color' && pending.newColor != null) {

				const pushCategoryColorUpdate = (cat: any) => {
					const toUpdateCategory = {
						color: pending.newColor,
						media_url: toMenuApiMediaUrlValue(pending.newMediaUrl),
					} as any;
					categoriesToUpdate.push({
						menu_id: menu.id,
						id: cat.id,
						...toUpdateCategory,
					});
					cat.color = pending.newColor;
					cat.media_url = pending.newMediaUrl ?? '';
				};

				const pushItemColorUpdate = (item: any, cat: any) => {
					const toUpdateItem = {
						color: pending.newColor,
						media_url: toMenuApiMediaUrlValue(pending.newMediaUrl),
					} as any;
					itemsToUpdate.push({
						menu_id: menu.id,
						category_id: cat.id,
						id: item.id,
						...toUpdateItem,
					});
					item.color = pending.newColor;
					item.media_url = pending.newMediaUrl ?? '';
				};

				if (pending.targetEntityType === 'menu') {
					for (const cat of menu.categories ?? []) {
						pushCategoryColorUpdate(cat);
					}
					for (const item of menu.items ?? []) {
						const cat = menu.categories?.find((c: any) => c.id === item.category_id);
						if (!cat) continue;
						pushItemColorUpdate(item, cat);
					}
				} else if (pending.targetEntityType === 'category' && pending.categoryId) {
					for (const item of (menu.items ?? []).filter((i: any) => i.category_id === pending.categoryId)) {
						const cat = menu.categories?.find((c: any) => c.id === pending.categoryId);
						if (!cat) continue;
						pushItemColorUpdate(item, cat);
					}
				} else {
					for (const row of ctx?.affectedEntities ?? []) {
						if (row.entityType === 'category') {
							const c = menu.categories?.find((x: any) => x.id === row.id);
							if (c) pushCategoryColorUpdate(c);
						} else {
							const item = menu.items?.find((x: any) => x.id === row.id);
							const cat = menu.categories?.find((c: any) => c.id === item?.category_id);
							if (item && cat) pushItemColorUpdate(item, cat);
						}
					}
				}
			}
      else if (pending.kind === 'printers' && pending.newPosPrinters != null) {
				const printers = pending.newPosPrinters;
				for (const row of ctx?.affectedEntities ?? []) {
					if (row.entityType === 'category') {
						const c = menu.categories?.find((x: any) => x.id === row.id);
						if (!c) continue;
            categoriesToUpdate.push({
              menu_id: menu.id,
              id: row.id,
              integrations: {
                pushpos: {
                  ...(c as any).integrations?.pushpos,
                  printers,
                },
              }
            });
						(c as any).integrations = (c as any).integrations || {};
						(c as any).integrations.pushpos = { ...(c as any).integrations.pushpos, printers };
					} else {
						const item = menu.items?.find((x: any) => x.id === row.id);
						const cat = menu.categories?.find((c: any) => c.id === item?.category_id);
						if (!item || !cat) continue;
						itemsToUpdate.push({
							menu_id: menu.id,
							category_id: cat.id,
							id: item.id,
							integrations: {
								pushpos: {
									...(item as any).integrations?.pushpos,
									printers,
								},
							},
						});
						(item as any).integrations = (item as any).integrations || {};
						(item as any).integrations.pushpos = { ...(item as any).integrations.pushpos, printers };
					}
				}
			}
      else if (
				pending.kind === 'receipt_printers' &&
				pending.receiptItemIds &&
				pending.receiptItemIds.length > 0 &&
				pending.receiptPrinterIds != null
			) {
				const rr = store.restaurant!;
				const { updatedPrinters, updateQuery } = RestaurantUtils.menu.assignItemsToPrinterV2(
					rr,
					pending.receiptItemIds,
					[...pending.receiptPrinterIds]
				);
				await store.api.restaurant_update({
					_id: rr._id,
					update: updateQuery,
				});
				rr.settings.printers = updatedPrinters;
				store.setRestaurant({ ...rr });
			}

      if (categoriesToUpdate.length > 0) {
        await store.api.menu_partial_update_batch_categories({
          restaurant_id: r._id,
          data: { categories: categoriesToUpdate }
        })
      }
      if (itemsToUpdate.length > 0) {
        await store.api.menu_partial_update_batch_items({
          restaurant_id: r._id,
          data: {items: itemsToUpdate}
        });
      }

			await store.updateRestaurant({ menus_v2: menus });
			UI.notification.success(
				pending.kind === 'visibility'
					? 'Visibility applied to descendants'
					: pending.kind === 'printers'
						? 'Printers applied to descendants'
						: pending.kind === 'receipt_printers'
							? 'Receipt printers updated for category items'
							: pending.targetEntityType === 'item'
								? 'Item color updated'
								: 'Colors applied to descendants'
			);
		} catch (e) {
			UI.notification.error((e as any)?.message ?? 'Failed to apply overrides');
		}
	};

	openCategoryReceiptPrinterOverride = (payload: {
		menuIndex: number;
		menuId: string;
		categoryId: string;
		categoryName: string;
		itemIds: string[];
		selectedPrinterIds: string[];
		affectedEntities: AffectedEntityRow[];
		changesSummary: OverrideChangesSummary;
	}) => {
		const store = this.injected.store;
		const pending: MenuPendingOverride = {
			kind: 'receipt_printers',
			targetEntityType: 'category',
			menuIndex: payload.menuIndex,
			menuId: payload.menuId,
			categoryId: payload.categoryId,
			parentAvailabilityId: '',
			parentMergedAvailability: {},
			receiptItemIds: payload.itemIds,
			receiptPrinterIds: payload.selectedPrinterIds,
		};
		this.setState({
			preOverrideContext: {
				targetEntity: {
					type: 'category',
					name: payload.categoryName,
					categoryCount: 0,
					itemCount: payload.itemIds.length,
				},
				overrideSource: { menuId: payload.menuId, categoryId: payload.categoryId },
				changesSummary: payload.changesSummary,
				editKind: 'receipt_printers',
				affectedEntities: payload.affectedEntities,
				pendingOverride: pending,
			},
		});
		store.setActiveMobileModal('desktop-menu-preoverride');
	};

	openMenuReceiptPrinterOverride = (payload: {
		menuIndex: number;
		menuId: string;
		menuName: string;
		itemIds: string[];
		selectedPrinterIds: string[];
		affectedEntities: AffectedEntityRow[];
		changesSummary: OverrideChangesSummary;
	}) => {
		const store = this.injected.store;
		const pending: MenuPendingOverride = {
			kind: 'receipt_printers',
			targetEntityType: 'menu',
			menuIndex: payload.menuIndex,
			menuId: payload.menuId,
			parentAvailabilityId: '',
			parentMergedAvailability: {},
			receiptItemIds: payload.itemIds,
			receiptPrinterIds: payload.selectedPrinterIds,
		};
		this.setState({
			preOverrideContext: {
				targetEntity: {
					type: 'menu',
					name: payload.menuName,
					categoryCount: 0,
					itemCount: payload.itemIds.length,
				},
				overrideSource: { menuId: payload.menuId },
				changesSummary: payload.changesSummary,
				editKind: 'receipt_printers',
				affectedEntities: payload.affectedEntities,
				pendingOverride: pending,
			},
		});
		store.setActiveMobileModal('desktop-menu-preoverride');
	};


	componentWillUnmount() {
		this._unmounted = true;
		this.injected.store.notifications.menuPresenceUnmounted(null);
		if (this._menuEditCollaborationActive) {
			this.injected.store.notifications.menuEditingMenusUnmounted();
			this._menuEditCollaborationActive = false;
		}
		if (this.state.mode !== "normal") {
			this.cancelEditMode();
		}
		if (this.interval) {
			clearInterval(this.interval);
		}
	}


	setPanel = (panel: MenuComponents | null, panel_index: number[]) => {
		this.setState({ panel, panel_index });
	};

	setOnPause = (panel: MenuComponents | null, panel_index: number[], id: string, item: T.Schema.Restaurant.Menu.RestaurantDish | T.Schema.Restaurant.Menu.RestaurantItem | null, items: T.Schema.Restaurant.Menu.RestaurantDish[] | T.Schema.Restaurant.Menu.RestaurantItem[]) => {
		this.setState({ panel_pause: panel, panel_index_pause: panel_index, currentMenuItemId: id, currentMenuItem: item, currentMenuItems: items });
	};

	create_menu = (): T.Schema.Restaurant.Menu.RestaurantMenu => {
		const _id = shortid.generate();
		return {
			_id,
			name: "",
			display_name: "",
			description: "",
			conditions: {
				hide_unavailable_menu: false,
				services: [],
				times: [],
				hours: [],
				pre_order: {
					enabled: false,
					days_in_advance: "",
					cutoff_time: "",
				},
				age_restricted: false,
			},
			categories: [],
			//@ts-ignore
			kounta: {
				kounta_sync_export_enabled: false,
				// kounta_username: "",
				// kounta_password: "",
				// kounta_site: ""
			},
		};
	};
	create_menu_v2 = (): T.Schema.Restaurant.Menu.RestaurantMenuV2 => {
		const { store } = this.injected;
		const restaurant = store.restaurant!;
		const id = shortid.generate();

		return {
			id,
			name: "",
			items: [],
			categories: [],
			restaurant_id: restaurant._id,
			availability_id: "",
			availabilities: []
		};
	};
	create_category(
		menu_id: string
	): T.Schema.Restaurant.Menu.RestaurantCategory {
		return {
			_id: shortid.generate(),
			menu_id,
			name: "",
			display_name: "",
			description: "",
			dishes: [],
			conditions: {
				hide_unavailable_category: false,
				services: [],
				hours: [],
			},
		};
	}
	create_dish(menu_id: string, category_id: string): RestaurantDishWithTaxes {
		const r = this.injected.store.restaurant!;
		const taxRates = r.settings.region.tax.rates ?? []
		const taxes = taxRates.map((rate: any) => getId(rate));
		return {
			type: "standard",
			_id: shortid.generate(),
			menu_id: menu_id,
			category_id: category_id,
			name: "",
			display_name: "",
			description: "",
			subtitle: "",
			price: "" as any,
			tags: [],
			image: null,
			status: null,
			// STANDARD
			ingredients: [],
			option_sets: [],
			// COMBO
			choices: [],
			price_type: "standard",
			taxes: taxes,
		};
	}

	generateCreateMenuButton = () => {
		const { store } = this.injected;
		return (
			<TabItem
				key="btn-create"
				onClick={() => store.setActiveMobileMenu('create-menu', 'menus', {panel: 'menu', index: -1})}
				className="footer-button"
				style={{ height: '42px', backgroundColor: '#FEFEFE' }}
			>
				Create Menu
			</TabItem>
		);
	}

	// Sync to POS lives here rather than in the integration settings: that screen is
	// configuration, and this is where someone has just changed a menu they want pushed.
	//
	// "Sync to POS", not "sync this menu": a sync sends the whole restaurant, and a label
	// naming one menu would promise something the payload cannot do.
	loadPOSSyncStatus = async () => {
		const { store } = this.injected;
		const r = store.restaurant;
		if (!r || !r.wpos || !r.wpos.wpos_status) {
			return;
		}
		const status: POSSyncStatus | null = await store.syncStatusWPOS(r._id);
		this.setState({ pos_sync_status: status || null });

		// A sync already under way when the screen opened: a reload mid-sync, or an automatic one.
		if (status && status.syncing && !this.state.syncing_pos) {
			this.setState({ syncing_pos: true });
			await waitForPOSSync(
				store,
				r._id,
				lastSyncAt(status),
				(s) => this.setState({ pos_sync_status: s }),
				() => this._unmounted,
			);
			if (!this._unmounted) {
				this.setState({ syncing_pos: false });
			}
		}
	}

	// The label carries the state, because this sits in a row of buttons with nowhere to
	// put a line of text. A failure outranks pending: it is the only one the operator has
	// to act on.
	posSyncLabel = () => {
		if (this.state.syncing_pos) {
			return "Syncing…";
		}
		const s = this.state.pos_sync_status;
		if (s && s.last_sync && !s.last_sync.ok) {
			return "Sync to POS — failed";
		}
		if (s && s.pending_since) {
			return "Sync to POS — pending";
		}
		return "Sync to POS";
	}

	// Undefined before the first sync: there is nothing to report, and saying so is noise.
	posSyncTitle = () => {
		const s = this.state.pos_sync_status;
		if (!s || !s.last_sync) {
			return undefined;
		}
		if (!s.last_sync.ok) {
			return `Last sync failed${s.last_sync.error ? `: ${s.last_sync.error}` : ""}`;
		}
		return `Last synced ${moment(s.last_sync.at).fromNow()}`;
	}

	generateSyncPOSButton = () => {
		const { store } = this.injected;
		const r = store.restaurant;
		if (!r || !r.wpos || !r.wpos.wpos_status) {
			return null;
		}

		return (
			<TabItem
				key="btn-sync-pos"
				title={this.posSyncTitle()}
				onClick={async () => {
					if (this.state.syncing_pos) {
						return;
					}
					this.setState({ syncing_pos: true });
					await requestPOSSync(
						store,
						r._id,
						(status) => this.setState({ pos_sync_status: status }),
						() => this._unmounted,
					);
					if (!this._unmounted) {
						this.setState({ syncing_pos: false });
					}
				}}
				className="footer-button"
				style={{ height: '42px' }}
			>
				{this.posSyncLabel()}
			</TabItem>
		);
	}

	list_menu = () => {
		const { store } = this.injected;
		const { activeMobileMenu } = store;
		const {
			loading: loading,
			mode,
			activeMenuColorModal,
			targetIndex,
			targetItem,
			activeMenuVisibilityModal,
			activeMenuPrevisibilityModal,
		} = this.state;
		const r = this.injected.store.restaurant!;
		const { menus } = r;
		const { pausedMenuItems,
			openMenuListItemActive,
			openMenuBottomMenuControl,
			setOpenMenuBottomMenuControl,
			setOpenMenuListItemActive } = this.injected.store;

		const menuIds = pausedMenuItems.map(dish => dish?.menu_id);
		const itemSearchText = this.props.filterText ?? "";
		const visibleMenus =
			mode === "normal" && itemSearchText.trim()
				? r.menus.filter((menu) => menuHasMatchingItem(menu, itemSearchText))
				: r.menus;
		const displayMenus = applyMenuListView(visibleMenus, {
			sortDirection: this.props.sortDirection ?? null,
			enabled: mode === "normal",
		});
		const pendingSort = hasPendingSorts(
			this.state.categorySortDirections,
			this.state.itemSortDirections
		);

		return (
			<>
				{(activeMobileMenu.name !== 'create-menu'
				&& activeMobileMenu.name !== 'edit-menu'
				&& activeMobileMenu.name !== 'create-category'
				 && activeMobileMenu.name !== 'edit-category'
				 && activeMobileMenu.name !== 'create-item'
				 && activeMobileMenu.name !== 'edit-item') && (
					<>

					<div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 4px 8px' }}>
						<MenuPresence />
					</div>
					<MenuList id="menu-list" type={MENU_ITEM_TYPE.MENU}>
					{displayMenus.map((menu: T.Schema.Restaurant.Menu.RestaurantMenu, i: number) => {
						const menuIndex = r.menus.findIndex((entry) => entry._id === menu._id);
						const totalItems = menu.categories.map((item: any) => item.dishes).flat().length
						return (

							<MenuListItemV2
								key={menu._id || 'menu-'+i}
								type={MENU_ITEM_TYPE.MENU}
								id={menu._id || 'menu-'+i}
								mode={mode}
								onClick={() => {
									const isAlreadyOpen = openMenuListItemActive === menu._id || (openMenuListItemActive && openMenuListItemActive.startsWith(menu._id + '_'));
									if (isAlreadyOpen) {
										setOpenMenuListItemActive('');
										store.notifications.menuPresenceSetMenu(null);
									} else {
										setOpenMenuListItemActive(menu._id);
										store.notifications.menuPresenceSetMenu(menu._id, 'viewing');
									}
								}}
								onMoreAction={this.openMenuMoreActions}
								active={openMenuListItemActive || ''}
								activeVisibility={activeMenuPrevisibilityModal}
								toggleActiveVisibility={() => this.setState({
									activeMenuPrevisibilityModal: !activeMenuPrevisibilityModal
								})}
								isMenu
								name={
									<div className="flex-line centered" style={{gap: 8}}>
										<TextEllipsis>{isObject(menu.name) ? JSON.stringify(menu.name): menu.name}</TextEllipsis>
										<MenuPresence menuId={menu._id} />
									</div>
								}
								index={mode === "reOrder" ? i : menuIndex}
								sortControls={
									mode === "normal" &&
									canShowListSortControls(menu.categories.length) ? (
										<MenuSortDirectionButtons
											compact
											sortDirection={getSortDirectionForId(
												this.state.categorySortDirections,
												menu._id
											)}
											onSortDirectionChange={(direction) =>
												this.setCategorySortDirection(menu._id, direction)
											}
										/>
									) : undefined
								}
								onEdit={() => {
                  store.notifications.menuPresenceSetMenu(menu._id, 'viewing');
                  store.setActiveMobileMenu('edit-menu', 'menus', {panel: 'menu', index: menuIndex});
                }}
								onRemove={() =>
									this.modal_remove_open("menu", [menuIndex])
								}
								onCopy={() => this.modal_copy_open("menu", [menuIndex])}
								onHistory={() => this.setState({ history_menu_id: (menu as any).id || (menu as any)._id })}
								isPausing={menuIds.includes(menu._id)}
								totalItems={totalItems}
								totalPausedItems={pausedMenuItems.filter(pausedItem => pausedItem?.menu_id === menu._id).length}
							>
								{this.list_category(menuIndex)}
							</MenuListItemV2>
						);
					})}
					</MenuList>
					{mode === "normal" && pendingSort &&
						this.saveCancelGroupButton(
							this.savePendingSorts,
							this.cancelPendingSorts,
							loading
						)}
					{mode === "normal" && !pendingSort && (
						<>
							{menus.length > 0 ? (
								<TabGroup className='menus menus-end menus-header' style={{ overflow: 'unset', marginTop: 10}}>
									{this.generateCreateMenuButton()}

										{this.generateSyncPOSButton()}

										<TabItem
											key="btn-re-arrange"
											onClick={() =>
												this.startEditMode("reOrder")
											}
											className="footer-button"
											style={{ height: '42px'}}
										>
											Re-Arrange
										</TabItem>

										<TabItem
											key="btn-edit-prices"
											onClick={() =>
												this.startEditMode("editPrices")
											}
											className="footer-button"
											style={{ height: '42px'}}
										>
											Edit Prices
										</TabItem>
								</TabGroup>
							) : (
								this.generateCreateMenuButton()
							)}
						</>
					)}

					{mode === "reOrder" &&
						this.saveCancelGroupButton(
							() =>
								this.saveEditMode("Menu Re-arranged Successfully!"),
							this.cancelEditMode,
							loading,
							"Re-Arrange Mode\n\nClick + hold to drag and drop items to re-order them"
						)}

					{mode === "editPrices" &&
						this.saveCancelGroupButtonV2(
							() => this.saveEditMode("Prices Updated Successfully!"),
							this.cancelEditMode,
							loading,
						"Bulk Edit Prices Mode"
					)}
					</>)}
			</>
		);
	};
	list_category = (menuIndex: number) => {
		const { mode, activeMenuColorModal, targetIndex, targetItem } = this.state;
		const r = this.injected.store.restaurant!;
		const menu = r.menus[menuIndex];
		const { pausedMenuItems, openMenuListItemActive, setOpenMenuListItemActive  } = this.injected.store;
		const pauseIds = pausedMenuItems.map(d => d?._id);
		const { store } = this.injected;
		const { activeMobileMenu } = store;
		const itemSearchText = this.props.filterText ?? "";
		const visibleCategories = menu.categories.filter((category) =>
			categoryHasMatchingItem(category, itemSearchText)
		);
		const displayCategories = applyMenuListView(visibleCategories, {
			sortDirection: getSortDirectionForId(this.state.categorySortDirections, menu._id),
			enabled: mode === "normal",
		});

		const checkCateIsPausing = (category: T.Schema.Restaurant.Menu.RestaurantCategory) => {
			if (category && category.dishes && category.dishes.length) {
				return category.dishes.map(cate => cate._id).filter(d => pauseIds.includes(d)).length
			}
		};

		return (
			<>
			{(activeMobileMenu.name !== 'create-category' && activeMobileMenu.name !== 'edit-category') && (
			<MenuList id={menu._id || 'menu-'+menuIndex} type={MENU_ITEM_TYPE.CATEGORY}>
				{displayCategories.map((category:  T.Schema.Restaurant.Menu.RestaurantCategory, i: number) => {
					const categoryIndex = menu.categories.findIndex(
						(entry) => entry._id === category._id
					);
					const totalItems = (category.dishes || []).filter((cate: any) => normalizeObject(cate).id).length;
					const isAbacusOpenItemsAvailable =
						!!r.abacus?.abacus_status &&
						!!r.abacus?.abacus_open_items;

					const noAbacusOpenItem =
						isAbacusOpenItemsAvailable && !category.abacus_open_item
							? "No Abacus Open Item"
							: "";
					return (
						<MenuListItemV2
							key={category._id || 'category-'+i}
							id={category._id || 'category-'+i}
							type={MENU_ITEM_TYPE.CATEGORY}
							onClick={() => {
								if(this.state.activeMobile === category._id) {
									this.setState({ activeMobile: '' });
								} else {
									this.setState({ activeMobile: category._id });
								}
							}}
							active={this.state.activeMobile || ''}
							onMoreAction={() => this.openCategoryMoreActions(i, menuIndex, 'category')}
							mode={mode}
							isMenu
							name={
								<div className="flex-l-r-center width100">
									<div className="flex-line centered" style={{gap: 8}}>
										<MenuCardTitle>
											<TextEllipsis>{isObject(category.name) ? JSON.stringify(category.name): category.name}</TextEllipsis>
										</MenuCardTitle>
									</div>

									{noAbacusOpenItem &&
										<Tooltip text="Please edit this category and set the Abacus Open Item" width={360} position="top">
											<Tag className="m-r-2">{noAbacusOpenItem}</Tag>
										</Tooltip>
									}
								</div>
							}
							index={mode === "reOrder" ? i : categoryIndex}
							sortControls={
								mode === "normal" &&
								canShowListSortControls((category.dishes || []).length) ? (
									<MenuSortDirectionButtons
										compact
										sortDirection={getSortDirectionForId(
											this.state.itemSortDirections,
											category._id
										)}
										onSortDirectionChange={(direction) =>
											this.setItemSortDirection(category._id, direction)
										}
									/>
								) : undefined
							}
							onEdit={() =>
								{ this.setPanel("category", [menuIndex, categoryIndex]);  store.setActiveMobileMenu('edit-category', 'menus', {panel: 'category', menuIndex: menuIndex, categoryIndex: categoryIndex});}
							}
							onRemove={() =>
								this.modal_remove_open("category", [
									menuIndex,
									categoryIndex,
								])
							}
							onCopy={() =>
								this.modal_copy_open("category", [menuIndex, categoryIndex])
							}
							isPausing={!!checkCateIsPausing(category)}
							totalItems={totalItems}
							totalPausedItems={checkCateIsPausing(category)}
						>
							{this.list_dish(menuIndex, categoryIndex)}
						</MenuListItemV2>
					);
				})}
				{mode === "normal" && (
					<TabGroup className='menus menus-end menus-header' style={{ overflow: 'unset', justifyContent: 'end'}}>
						<TabItem
						onClick={() => store.setActiveMobileMenu('create-category', 'menus', { panel: 'category', menuIndex: menuIndex, categoryIndex: -1})
						}
						className="menus-header-button"
					>
						Create New Category
					</TabItem>
					</TabGroup>
				)}
			</MenuList>)}
			</>
		);
	};
	list_dish = (menuIndex: number, categoryIndex: number) => {
		const { selected_dishes, activeMenuColorModal, targetIndex, targetItem } = this.state;
		const { mode } = this.state;
		const r = this.injected.store.restaurant!;
		const rs = this.injected.store.restaurant_stock!;
		const menu = r.menus[menuIndex];
		const category = menu.categories[categoryIndex];
		const currency = this.injected.store.intl.s.currency;
		const { pausedMenuItems, openMenuListItemActive, setOpenMenuListItemActive  } = this.injected.store;
		const { store } = this.injected;
		const { activeMobileMenu, setActiveMobileModal } = store;
		const displayDishes = applyMenuListView(category.dishes || [], {
			filterText: this.props.filterText,
			sortDirection: getSortDirectionForId(this.state.itemSortDirections, category._id),
			enabled: mode === "normal",
		});
		return (
			<>
			{(activeMobileMenu.name !== 'create-item' && activeMobileMenu.name !== 'edit-item') && (<MenuList id={category._id} type={MENU_ITEM_TYPE.DISH}>
				{displayDishes.map((item: any) => {
					const i = (category.dishes || []).findIndex(
						(dish: any) => dish._id === item._id
					);
					const stock = rs.dishes[item._id];
					let tag;

					if (item.status) {
						tag =
							item.status === "hidden"
								? "Hidden"
								: item.status === "no-stock"
									? "No Stock"
									: undefined;
					} else if (typeof stock !== "undefined") {
						tag = stock <= 0 ? `No Stock` : `${stock} units`;
					}

					const isPausing = pausedMenuItems.length > 0 && !!pausedMenuItems.find(d => normalizeObject(d)?.id === item._id)
					const receiptPrinters = r.settings?.printers ?? [];
					const receiptItemId = item._id;
					const showUnassignedReceiptPrinterIcon =
						receiptPrinters.length > 0 &&
						!RestaurantUtils.menu.itemAssignedToAnyReceiptPrinter(
							receiptPrinters,
							receiptItemId,
						);

					return (
						<MenuListItemV2
							key={item._id || 'item-'+i}
							id={item._id || 'item-'+i}
							onMoreAction={() => this.openItemMoreActions(i, categoryIndex, menuIndex, 'item')}
							type={MENU_ITEM_TYPE.DISH}
							mode={mode}
							isMenu
							name={
								<div className="flex-l-r-center width100" style={{ gap: 12 }}>
									<div className="flex-line centered" style={{ gap: 8, minWidth: 0, flex: 1 }}>
										{mode === "normal" && (
											<Checkbox
												id={`dish-${item._id}`}
												checked={
													selected_dishes.indexOf(
														item._id
													) !== -1
												}
												onChange={(e) => {
													e.stopPropagation();
													this.toggle_selected_dish(
														item._id
													);
												}}
											/>
										)}
										<MenuCardTitle >
											<TextEllipsis>{isObject(item.name) ? JSON.stringify(item.name): item.name}</TextEllipsis>
										</MenuCardTitle>
									</div>

									{tag && <Tag className="m-r-2" style={{ flexShrink: 0 }}>{tag}</Tag>}
								</div>
							}
							index={i}
							buttons={
								(r.reseller_id === "curbsidepivot" &&
									<Tooltip text="Stock" width={60} position="top">
										<ItemButton
											type="button"
											color="white"
											className="no-round no-border width32"
											paddinglr={5}
											onClick={(e) => {
												e.stopPropagation();
												this.dish_stock_update(item._id);
											}}
										>
										</ItemButton>
									</Tooltip>
								)
							}
							price={item.price}
							onEdit={() =>
							{ this.setPanel("item", [menuIndex, categoryIndex, i]);
								store.setActiveMobileMenu('edit-item', 'menus', {panel: 'item', menuIndex: menuIndex, categoryIndex: categoryIndex, dishIndex: i})
							}}
							onPause={() =>
								this.setOnPause("item", [
									menuIndex,
									categoryIndex,
									i,
								], item._id, item, [item])
							}
							onCopy={() =>
								this.modal_copy_open("item", [
									menuIndex,
									categoryIndex,
									i,
								])
							}
							currency={currency}
							onPriceEdit={(price) => {
								r.menus[menuIndex].categories[
									categoryIndex
								].dishes[i].price = price;
							}}
							isPausing={isPausing}
							isPauseInfinity={item?.pause_infinite}
							pauseDuration={item.pause_duration}
							pauseStart={item.pause_start}
							isItem
							showUnassignedReceiptPrinterIcon={showUnassignedReceiptPrinterIcon}
							onReceiptPrinterAssignClick={() => {
								this.setState({
									receiptPrinterAssignItem: {
										itemId: receiptItemId,
										name: isObject(item.name)
											? JSON.stringify(item.name)
											: String(item.name ?? ""),
									},
								});
								setActiveMobileModal("desktop-receipt-printer-assign-item");
							}}
						>
							{this.state.showBottomControl === 'item' && (
								<BottomMenuControls
									onEdit={() =>
										{ this.setPanel("item", [menuIndex, categoryIndex, i]);
											store.setActiveMobileMenu('edit-item', 'menus', {panel: 'item', menuIndex: menuIndex, categoryIndex: categoryIndex, dishIndex: i})
										}}
									onCopy={() =>
										this.modal_copy_open("item", [
											menuIndex,
											categoryIndex,
											i,
										])}
									onCancel={() => this.setState({ activeMobile: '', showBottomControl: '' })}
							/>
							)}
						</MenuListItemV2>
					);
				})}
				{mode === "normal" && (
					<TabGroup className='menus menus-end menus-header' style={{ overflow: 'unset', justifyContent: 'end'}}>
						<TabItem
							onClick={() => store.setActiveMobileMenu('create-item', 'menus', { panel: 'item', menuIndex: menuIndex, categoryIndex: categoryIndex, dishIndex: -1})}
							className="menus-header-button"
						>
							Create New Item
						</TabItem>
					</TabGroup>
				)}
			</MenuList>)}
			</>
		);
	};

	setCategorySortDirection = (menuId: string, direction: MenuListSortDirection) => {
		this.setState((prev) => ({
			categorySortDirections: {
				...prev.categorySortDirections,
				[menuId]: direction,
			},
		}));
	};

	setItemSortDirection = (categoryId: string, direction: MenuListSortDirection) => {
		this.setState((prev) => ({
			itemSortDirections: {
				...prev.itemSortDirections,
				[categoryId]: direction,
			},
		}));
	};

	cancelPendingSorts = () => {
		this.setState({ categorySortDirections: {}, itemSortDirections: {} });
	};

	savePendingSorts = async () => {
		const store = this.injected.store;
		const r = store.restaurant!;
		const { categorySortDirections, itemSortDirections } = this.state;
		const clearSorts = () => this.setState({ categorySortDirections: {}, itemSortDirections: {} });

		if (store.isNewMenuSchema && r.menus_v2?.length) {
			const backupMenus = cloneDeep(r.menus_v2);
			this.menu_backup_v2 = backupMenus;
			store.updateRestaurant({
				menus_v2: applyPendingSortsV2(r.menus_v2, categorySortDirections, itemSortDirections),
			});
			const saved = await this.saveEditMode("Menu Sorted Successfully!", "reOrder");
			if (saved) {
				clearSorts();
			} else {
				store.updateRestaurant({ menus_v2: backupMenus });
				this.menu_backup_v2 = null;
			}
			return;
		}

		const backup = cloneDeep(r);
		const sorted = applyPendingSorts(r.menus, categorySortDirections, itemSortDirections);
		store.setRestaurant({ ...r, menus: sorted });
		await this.saveRestaurantSilent({
			backup,
			update: { $set: { menus: sorted } },
			successMsg: "Menu Sorted Successfully!",
			before: () => this.setState({ loading: true }),
			onSuccess: () => {
				this.setState({ loading: false });
				clearSorts();
			},
			onFail: () => this.setState({ loading: false }),
			onError: () => this.setState({ loading: false }),
		});
	};

	//v2

	list_menu_v2 = () => {
		const { store } = this.injected;
		const { activeMobileMenu, openMenuListItemActive, openMenuBottomMenuControl, setOpenMenuListItemActive, setOpenMenuBottomMenuControl } = store;
		const {
			loading: loading,
			mode,
			activeMenuColorModal,
			activeMenuPrevisibilityModal,
			menuChannelFilterPos,
			menuChannelFilterOnline,
		} = this.state;
		const r = this.injected.store.restaurant!;
		const { menus_v2 } = r;

		const pausedV2Items = (menus_v2 || []).flatMap(m => m.items).filter(isV2ItemPaused);
		const menuIds = pausedV2Items.map(item => item.menu_id);
		const menus = menus_v2 || [];
		if(loading) return <RotateLoader size={4} />
		const itemSearchText = this.props.filterText ?? "";
		const pendingSort = hasPendingSorts(
			this.state.categorySortDirections,
			this.state.itemSortDirections
		);
		const searchedMenus = applyMenuListView(
			mode === "normal" && itemSearchText.trim()
				? menus.filter((menu) => menuHasMatchingItem(menu, itemSearchText))
				: menus,
			{
				sortDirection: this.props.sortDirection ?? null,
				enabled: mode === "normal",
			}
		);
		const menusRows = menuChannelFilterPos || menuChannelFilterOnline
			? searchedMenus
					.map((m: T.Schema.Restaurant.Menu.RestaurantMenuV2) => ({
						menu: m,
						i: menus.findIndex((entry) => entry.id === m.id),
					}))
					.filter(({ menu }) =>
						menuVisibleWithChannelFilters(menu, menuChannelFilterPos, menuChannelFilterOnline)
					)
			: searchedMenus.map((m: T.Schema.Restaurant.Menu.RestaurantMenuV2) => ({
					menu: m,
					i: menus.findIndex((entry) => entry.id === m.id),
				}));
		return (
			<>
				{(activeMobileMenu.name !== 'create-menu'
				&& activeMobileMenu.name !== 'edit-menu'
				&& activeMobileMenu.name !== 'create-category'
				 && activeMobileMenu.name !== 'edit-category'
				 && activeMobileMenu.name !== 'create-item'
				 && activeMobileMenu.name !== 'edit-item') && (
					<>

					<div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 4px 8px' }}>
						<MenuPresence />
					</div>
					<MenuList id="menu-list" type={MENU_ITEM_TYPE.MENU} mode={mode}>
					{menusRows.map(({ menu, i }: { menu: T.Schema.Restaurant.Menu.RestaurantMenuV2; i: number }) => {
						const totalItems = menu.items.length
						return (

							<MenuListItemV2
								key={menu.id}
								type={MENU_ITEM_TYPE.MENU}
								id={menu.id}
								conditions={menu.availabilities.find(a => a.id === menu.availability_id)}
								mode={mode}
								onMoreAction={this.openMenuMoreActions}
								onClick={() => {
									const isAlreadyOpen = openMenuListItemActive === menu.id || (openMenuListItemActive && openMenuListItemActive.startsWith(menu.id + '_'));
									if (isAlreadyOpen) {
										setOpenMenuListItemActive('');
										store.notifications.menuPresenceSetMenu(null);
									} else {
										setOpenMenuListItemActive(menu.id);
										store.notifications.menuPresenceSetMenu(menu.id, 'viewing');
									}
								}}
								active={(openMenuListItemActive === menu.id || (openMenuListItemActive && openMenuListItemActive.startsWith(menu.id + '_'))) ? menu.id : ''}
								activeVisibility={activeMenuPrevisibilityModal}
								toggleActiveVisibility={() => {
									setOpenMenuListItemActive(menu.id);
									setOpenMenuBottomMenuControl('menu');
									this.setState({
										preOverrideContext: {
											targetEntity: {
												type: 'menu',
												name: menu.name,
												categoryCount: menu.categories.length,
												itemCount: menu.items.length,
												conditions: menu.availabilities.find(a => a.id === menu.availability_id),
											},
											overrideSource: { menuId: menu.id },
										},
										activeMenuPrevisibilityModal: true,
									});
								}}
								isMenu
								name={
									<div className="flex-line centered" style={{gap: 8}}>
										{mode === 'normal' && <MenuItemColor
											color={menu.color || ''}
											onClick={(e) => {
												e.preventDefault()
												e.stopPropagation()
												this.setState({
													activeMenuColorModal: !activeMenuColorModal,
													activeMenuColorTitle: menu.name,
													targetItem: 'menu',
													targetIndex: i,
													menuColorBulkItemIds: null,
													activeMenuColorData: {
														color: menu.color!,
														media_url: menu.media_url,
														title: menu.name
													}
												})
											}}/>}
										<TextEllipsis>{menu.name}</TextEllipsis>
										<MenuPresence menuId={menu.id} />
									</div>
								}
								index={i}
								sortControls={
									mode === "normal" &&
									canShowListSortControls(menu.categories.length) ? (
										<MenuSortDirectionButtons
											compact
											sortDirection={getSortDirectionForId(
												this.state.categorySortDirections,
												menu.id
											)}
											onSortDirectionChange={(direction) =>
												this.setCategorySortDirection(menu.id, direction)
											}
										/>
									) : undefined
								}
								onEdit={() => {
									store.notifications.menuPresenceSetMenu(menu.id, 'viewing');
									store.setActiveMobileMenu('edit-menu', 'menus', {panel: 'menu', index: i});
								}}
								onRemove={() =>
									this.modal_remove_open("menu", [i])
								}
								onCopy={() => this.modal_copy_open("menu", [i])}
								onHistory={() => this.setState({ history_menu_id: menu.id })}
								isPausing={menuIds.includes(menu.id)}
								totalItems={totalItems}
								totalPausedItems={pausedV2Items.filter(item => item.menu_id === menu.id).length}
							>
								{this.list_category_v2(i)}

							</MenuListItemV2>
						);
					})}
					</MenuList>
					{mode === "normal" && pendingSort &&
						this.saveCancelGroupButton(
							this.savePendingSorts,
							this.cancelPendingSorts,
							loading
						)}
					{mode === "normal" && !pendingSort && (
						<>
							{menus.length > 0 ? (
								<TabGroup className='menus menus-end menus-header' style={{ overflow: 'unset', marginTop: 10}}>
									{this.generateCreateMenuButton()}

										{this.generateSyncPOSButton()}

										<TabItem
											key="btn-re-arrange"
											onClick={() =>
												this.startEditMode("reOrder")
											}
											className="footer-button"
											style={{ height: '42px' }}
										>
											Re-Arrange
										</TabItem>

										<TabItem
											key="btn-edit-prices"
											onClick={() =>
												this.startEditMode("editPrices")
											}
											className="footer-button"
											style={{ height: '42px' }}
										>
											Edit Prices
										</TabItem>
								</TabGroup>
							) : (
								this.generateCreateMenuButton()
							)}
						</>
					)}

					{mode === "reOrder" &&
						this.saveCancelGroupButton(
							() =>
								this.saveEditMode("Menu Re-arranged Successfully!"),
							this.cancelEditMode,
							loading,
							"Re-Arrange Mode\n\nClick + hold to drag and drop items to re-order them"
						)}

					{mode === "editPrices" &&
						this.saveCancelGroupButtonV2(
							() => this.saveEditMode("Prices Updated Successfully!"),
							this.cancelEditMode,
							loading,
						"Bulk Edit Prices Mode"
					)}

					</>)}
			</>
		);
	};
	list_category_v2 = (menuIndex: number) => {
		const {
			mode,
			activeMenuColorModal,
			targetIndex,
			targetItem,
			activeMenuPrevisibilityModal,
			menuChannelFilterPos,
			menuChannelFilterOnline,
		} = this.state;
		const r = this.injected.store.restaurant!;
		const menu = (r.menus_v2 || [])[menuIndex];
		const pauseIds = menu.items.filter(isV2ItemPaused).map(item => item.id);
		const { store } = this.injected;
		const { activeMobileMenu, openMenuListItemActive, openMenuBottomMenuControl, setOpenMenuListItemActive, setOpenMenuBottomMenuControl } = store;
		const itemSearchText = this.props.filterText ?? "";
		const visibleCategories = menu.categories.filter((category) =>
			categoryHasMatchingItem(category, itemSearchText, menu.items, category.id)
		);
		const filteredCategories = applyMenuListView(visibleCategories, {
			sortDirection: getSortDirectionForId(this.state.categorySortDirections, menu.id),
			enabled: mode === "normal",
		});

		const checkCateIsPausing = (category: T.Schema.Restaurant.Menu.RestaurantCategoryV2) => {
			const items = menu.items.filter(item => item.category_id === category.id)
			return items.map(cate => cate.id).filter(d => pauseIds.includes(d)).length
		};
		const categoryRows =
			menuChannelFilterPos || menuChannelFilterOnline
				? filteredCategories
						.map((c: T.Schema.Restaurant.Menu.RestaurantCategoryV2) => ({
							category: c,
							i: menu.categories.findIndex((entry) => entry.id === c.id),
						}))
						.filter(({ category }) =>
							categoryVisibleWithChannelFilters(
								menu,
								category,
								menuChannelFilterPos,
								menuChannelFilterOnline
							)
						)
				: filteredCategories.map((c: T.Schema.Restaurant.Menu.RestaurantCategoryV2) => ({
						category: c,
						i: menu.categories.findIndex((entry) => entry.id === c.id),
					}));
		return (
			<>
			{(activeMobileMenu.name !== 'create-category' && activeMobileMenu.name !== 'edit-category') && (
			<MenuList id={menu.id} type={MENU_ITEM_TYPE.CATEGORY}>
				{categoryRows.map(({ category, i }: { category: T.Schema.Restaurant.Menu.RestaurantCategoryV2; i: number }) => {
					const totalItems = menu.items.filter(item => item.category_id === category.id).length;
					const isAbacusOpenItemsAvailable =
						!!r.abacus?.abacus_status &&
						!!r.abacus?.abacus_open_items;

					const noAbacusOpenItem =
						//@ts-ignore
						isAbacusOpenItemsAvailable && !category.abacus_open_item
							? "No Abacus Open Item"
							: "";
					return (
						<MenuListItemV2
							key={category.id}
							id={category.id}
							conditions={menu.availabilities.find(
								(a: any) => a.id === effectiveCategoryAvailabilityId(menu, category)
							)}
							type={MENU_ITEM_TYPE.CATEGORY}
							onClick={() => {
								const categoryKey = menu.id + '_' + category.id;
								const isAlreadyOpen = openMenuListItemActive === categoryKey || (openMenuListItemActive && openMenuListItemActive.startsWith(categoryKey + '_'));
								if (isAlreadyOpen) {
									setOpenMenuListItemActive(menu.id);
								} else {
									setOpenMenuListItemActive(categoryKey);
								}
							}}
							onMoreAction={() => this.openCategoryMoreActions(i, menuIndex, 'category')}
							active={((openMenuListItemActive === menu.id + '_' + category.id) || (openMenuListItemActive && openMenuListItemActive.startsWith(menu.id + '_' + category.id + '_'))) ? category.id : ''}
							activeVisibility={activeMenuPrevisibilityModal}
							toggleActiveVisibility={() => {
								const categoryKey = menu.id + '_' + category.id;
								setOpenMenuListItemActive(categoryKey);
								setOpenMenuBottomMenuControl('category');
								this.setState({
									preOverrideContext: {
										targetEntity: {
											type: 'category',
											name: category.name,
											categoryCount: menu.categories.length,
											itemCount: totalItems,
											conditions: menu.availabilities.find(
												(a: any) =>
													a.id === effectiveCategoryAvailabilityId(menu, category)
											),
										},
										overrideSource: { menuId: menu.id, categoryId: category.id },
									},
									activeMenuPrevisibilityModal: true,
								});
							}}
							mode={mode}
							isMenu
							name={
								<div className="flex-l-r-center width100">
									<div className="flex-line centered" style={{gap: 8}}>
										{ mode !== 'editPrices' && (
										<MenuItemColor
											color={category.color || menu.color}
											onClick={(e) => {
												e.preventDefault()
												e.stopPropagation()
												this.setState({
													activeMenuColorModal: !activeMenuColorModal,
													activeMenuColorTitle: category.name,
													targetItem: 'category',
													targetIndex: i,
													targetMenuIndex: menuIndex,
													menuColorBulkItemIds: null,
													activeMenuColorData:  {
														color: category.color || menu.color || '',
														media_url: category.media_url,
														title: category.name
													}
												})
											}} />
										)}
										<MenuCardTitle>
											<TextEllipsis>{category.name}</TextEllipsis>
										</MenuCardTitle>
									</div>

									{noAbacusOpenItem &&
										<Tooltip text="Please edit this category and set the Abacus Open Item" width={360} position="top">
											<Tag className="m-r-2">{noAbacusOpenItem}</Tag>
										</Tooltip>
									}
								</div>
							}
							index={i}
							sortControls={
								mode === "normal" &&
								canShowListSortControls(
									menu.items.filter((item) => item.category_id === category.id).length
								) ? (
									<MenuSortDirectionButtons
										compact
										sortDirection={getSortDirectionForId(
											this.state.itemSortDirections,
											category.id
										)}
										onSortDirectionChange={(direction) =>
											this.setItemSortDirection(category.id, direction)
										}
									/>
								) : undefined
							}
							onEdit={() =>
								{ this.setPanel("category", [menuIndex, i]);  store.setActiveMobileMenu('edit-category', 'menus', {panel: 'category', menuIndex: menuIndex, categoryIndex: i});}
							}
							onRemove={() =>
								this.modal_remove_open("category", [
									menuIndex,
									i,
								])
							}
							onCopy={() =>
								this.modal_copy_open("category", [menuIndex, i])
							}
							isPausing={!!checkCateIsPausing(category)}
							totalItems={totalItems}
							totalPausedItems={checkCateIsPausing(category)}
						>
							{this.list_dish_v2(menuIndex, i)}

						</MenuListItemV2>
					);
				})}
				{mode === "normal" && (
					<TabGroup className='menus menus-end menus-header' style={{ overflow: 'unset', justifyContent: 'end'}}>
						<TabItem
						onClick={() => store.setActiveMobileMenu('create-category', 'menus', { panel: 'category', menuIndex: menuIndex, categoryIndex: -1})
						}
						className="menus-header-button"
					>
						Create New Category
					</TabItem>
					</TabGroup>
				)}
			</MenuList>)}
			</>
		);
	};
	list_dish_v2 = (menuIndex: number, categoryIndex: number) => {
		const {
			selected_dishes,
			activeMenuColorModal,
			targetIndex,
			targetItem,
			activeMenuPrevisibilityModal,
			menuChannelFilterPos,
			menuChannelFilterOnline,
		} = this.state;
		const { mode } = this.state;
		const r = this.injected.store.restaurant!;
		const rs = this.injected.store.restaurant_stock!;
		const menu = (r.menus_v2 || [])[menuIndex];
		const category = menu.categories[categoryIndex];
		const currency = this.injected.store.intl.s.currency;
		const { pausedMenuItems } = this.injected.store;
		const { store } = this.injected;
		const { activeMobileMenu, setActiveMobileModal, openMenuListItemActive, openMenuBottomMenuControl, setOpenMenuListItemActive, setOpenMenuBottomMenuControl } = store;
		let items = menu.items.filter(item => item.category_id === category.id);
		if (menuChannelFilterPos || menuChannelFilterOnline) {
			items = items.filter((item) =>
				passesChannelFilters(
					effectiveConditionsBlobForItem(menu, category, item),
					menuChannelFilterPos,
					menuChannelFilterOnline
				)
			);
		}
		items = applyMenuListView(items, {
			filterText: this.props.filterText,
			sortDirection: getSortDirectionForId(this.state.itemSortDirections, category.id),
			enabled: mode === "normal",
		});
		return (
			<>
			{(activeMobileMenu.name !== 'create-item' && activeMobileMenu.name !== 'edit-item') && (<MenuList id={category.id} type={MENU_ITEM_TYPE.DISH}>
				{items.map((item: T.Schema.Restaurant.Menu.RestaurantItem, localIdx: number) => {
          const i = menu.items.findIndex((d: any) => d.id === item.id);
					const stock = rs.dishes[item.id];
					let tag;

					if (item.status) {
						tag = item.status === "hidden" ? "Hidden"
								: item.status === "no-stock" ? "No Stock"
									: undefined;
					} else if (typeof stock !== "undefined") {
						tag = stock <= 0 ? `No Stock` : `${stock} units`;
					}

          			const pauseStartRaw = item.pause?.start_utc ?? item.pause?.start;
					const pauseEndRaw = item.pause?.end_utc ?? item.pause?.end;
					const pauseStartParsed = pauseStartRaw != null ? new Date(pauseStartRaw).getTime() : NaN;
					const pauseEndParsed = pauseEndRaw != null ? new Date(pauseEndRaw).getTime() : NaN;
					const nowMsForPause = Date.now();
					const pauseWindowActive = Number.isFinite(pauseStartParsed) && Number.isFinite(pauseEndParsed)
						&& nowMsForPause >= pauseStartParsed && nowMsForPause < pauseEndParsed;
					const isPausing = item.pause?.infinite || pauseWindowActive || pausedMenuItems.some(d => normalizeObject(d)?.id === item.id)
					const pauseStartMs = (item.pause?.infinite || pauseWindowActive) && Number.isFinite(pauseStartParsed) ? pauseStartParsed : undefined
					const receiptPrintersV2 = r.settings?.printers ?? [];
					const receiptItemIdV2 = item.id;
					const showUnassignedReceiptPrinterIconV2 =
						receiptPrintersV2.length > 0 &&
						!RestaurantUtils.menu.itemAssignedToAnyReceiptPrinter(
							receiptPrintersV2,
							receiptItemIdV2,
						);

					return (
						<MenuListItemV2
							key={item.id}
							id={item.id}
							conditions={menu.availabilities.find((a: any) => a.id === (item.availability_id ?? category.availability_id ?? menu.availability_id))}
							onMoreAction={() => this.openItemMoreActions(i, categoryIndex, menuIndex, 'item')}
							activeVisibility={activeMenuPrevisibilityModal}
							toggleActiveVisibility={() => {
								const itemKey = menu.id + '_' + category.id + '_' + item.id;
								setOpenMenuListItemActive(itemKey);
								setOpenMenuBottomMenuControl('item');
								this.setState({
									preOverrideContext: {
										targetEntity: {
											type: 'item',
											name: item.name,
											conditions: menu.availabilities.find((a: any) => a.id === (item.availability_id ?? category.availability_id ?? menu.availability_id)),
										},
										overrideSource: { menuId: menu.id, categoryId: category.id, itemId: item.id },
									},
									activeMenuPrevisibilityModal: true,
								});
							}}
							type={MENU_ITEM_TYPE.DISH}
							mode={mode}
							isMenu
							name={
								<div className="flex-l-r-center width100" style={{ gap: 12 }}>
									<div className="flex-line centered" style={{ gap: 8, minWidth: 0, flex: 1 }}>
										{mode === "normal" && (
											<Checkbox
												id={`dish-${item.id}`}
												checked={
													selected_dishes.indexOf(
														item.id
													) !== -1
												}
												onChange={(e) => {
													e.stopPropagation();
													if(window.innerWidth < 768) {
														this.setState({ hideMoreActionsOnMobile: true });
													}

													this.toggle_selected_dish(
														item.id
													);
												}}
											/>
										)}
										{store.isNewMenuSchema && mode === 'normal' && <MenuItemColor
											color={item.color || category.color || menu.color}
											onClick={(e) => {
												e.preventDefault()
												e.stopPropagation()
												const bulkIds =
													selected_dishes.length > 0 && selected_dishes.indexOf(item.id) !== -1
														? [...selected_dishes]
														: null;
												this.setState({
													activeMenuColorModal: !activeMenuColorModal,
													activeMenuColorTitle:
														bulkIds && bulkIds.length > 1 ? `${bulkIds.length} items` : item.name,
													targetItem: 'item',
													targetIndex: i,
													targetMenuIndex: menuIndex,
													targetCategoryIndex: categoryIndex,
													menuColorBulkItemIds: bulkIds,
													activeMenuColorData:  {
														color: item.color || category.color || menu.color || '',
														media_url: item.media_url || ''
													}

												})
											}} /> }
										<MenuCardTitle >
											<TextEllipsis>{item.name}</TextEllipsis>
										</MenuCardTitle>
									</div>

									{tag && mode === 'normal' && <Tag className="m-r-2" style={{ flexShrink: 0 }}>{tag}</Tag>}
								</div>
							}
							index={localIdx}
							buttons={
								(r.reseller_id === "curbsidepivot" &&
									<Tooltip text="Stock" width={60} position="top">
										<ItemButton
											type="button"
											color="white"
											className="no-round no-border width32"
											paddinglr={5}
											onClick={(e) => {
												e.stopPropagation();
												this.dish_stock_update(item.id);
											}}
										>
										</ItemButton>
									</Tooltip>
								)
							}
							price={item.price}
							onEdit={() =>
							{ this.setPanel("item", [menuIndex, categoryIndex, i]);
								store.setActiveMobileMenu('edit-item', 'menus', {panel: 'item', menuIndex: menuIndex, categoryIndex: categoryIndex, dishIndex: i})
							}}
							onPause={() =>
								this.setOnPause("item", [
									menuIndex,
									categoryIndex,
									i,
								], item.id, item, [item])
							}
							onCopy={() =>
								this.modal_copy_open("item", [
									menuIndex,
									categoryIndex,
									i,
								])
							}
							onRemove={() =>
								this.modal_remove_open("item", [
									menuIndex,
									categoryIndex,
									i
								])
							}
							currency={currency}
							onPriceEdit={(price) => {
								(r.menus_v2 || [])[menuIndex].items[i].price = price;
							}}
							isPausing={isPausing}
							isPauseInfinity={item.pause?.infinite}
							pauseDuration={item.pause?.duration}
							pauseStart={pauseStartMs}
							isItem
							showUnassignedReceiptPrinterIcon={showUnassignedReceiptPrinterIconV2}
							onReceiptPrinterAssignClick={() => {
								this.setState({
									receiptPrinterAssignItem: {
										itemId: receiptItemIdV2,
										name: String(item.name ?? ""),
									},
								});
								setActiveMobileModal("desktop-receipt-printer-assign-item");
							}}
						>
						</MenuListItemV2>
					);
				})}
				{mode === "normal" && (
					<TabGroup className='menus menus-end menus-header' style={{ overflow: 'unset', justifyContent: 'end'}}>
						<TabItem
							onClick={() => store.setActiveMobileMenu('create-item', 'menus', { panel: 'item', menuIndex: menuIndex, categoryIndex: categoryIndex, dishIndex: -1})}
							className="menus-header-button"
						>
							Create New Item
						</TabItem>
					</TabGroup>
				)}
			</MenuList>)}
			</>
		);
	};

	panel_menu = () => {
		const r = this.injected.store.restaurant!;
		const { store } = this.injected;
		const { activeMobileMenu } = store;
		const { panel, panel_index } = this.state;
		const [menuIndex, categoryIndex, dishIndex] = panel_index;
		const active = activeMobileMenu?.data?.panel === 'menu';
		let initialValues = null;
		if (active) {
			if (store.activeMobileMenu?.data?.index === -1) {
				// When creating a new menu, use the correct schema
				// shape for the currently active menu version.
				initialValues = store.isNewMenuSchema
					? this.create_menu_v2()
					: this.create_menu();
			} else {
				let item: any;
				if (store.isNewMenuSchema) {
					const targetMenu = (r.menus_v2 || [])[store.activeMobileMenu?.data?.index];
					const targetAvailability = targetMenu.availabilities.find(a => a.id === targetMenu.availability_id);
					item = {
						...targetMenu,
						conditions: targetAvailability,
						availability_id: targetMenu.availability,
						availability: targetAvailability,
					};
				} else {
					item = r.menus[store.activeMobileMenu?.data?.index];
				}
				initialValues = cloneDeep(item);
			}
		}

		return (
			<RestaurantFormMenuV2
				type={activeMobileMenu.data?.index === -1 ? "create" : "edit"}
				initialValues={initialValues}
				close={() => this.setPanel(null, [])}
				v2VisibilityDelegate={this.v2FormVisibilityDelegate}
				onReceiptPrinterMenuConflict={
					this.injected.store.isNewMenuSchema ? this.openMenuReceiptPrinterOverride : undefined
				}
				v2PrintersDelegate={this.v2FormPrintersDelegate}
			/>
		);
	};

	panel_category = () => {
		const r = this.injected.store.restaurant!;
		const { panel, panel_index } = this.state;
		const [menuIndex, categoryIndex] = panel_index;
		const { store } = this.injected;
		const { activeMobileMenu } = store;
		const active = activeMobileMenu?.data?.panel === "category";

		let initialValues = null;
		if (active) {
			let menu, item;
			menu = r.menus[activeMobileMenu?.data?.menuIndex];
			if (activeMobileMenu?.data?.categoryIndex === -1) {
				if(store.isNewMenuSchema) {
					menu = (r.menus_v2 || [])[activeMobileMenu?.data?.menuIndex];
					initialValues = this.create_category(menu.id);
				} else {
					initialValues = this.create_category(menu._id);
				}
			} else {
				if(store.isNewMenuSchema) {
					const targetCategory = (r.menus_v2 || [])[activeMobileMenu?.data?.menuIndex].categories[activeMobileMenu?.data?.categoryIndex]
					const targetAvailability = (r.menus_v2 || [])[activeMobileMenu?.data?.menuIndex].availabilities.find(a => a.id === targetCategory.availability_id);
						menu = (r.menus_v2 || [])[activeMobileMenu?.data?.menuIndex];
						item = {
							...menu.categories[activeMobileMenu?.data?.categoryIndex],
							availability_id: targetCategory.availability_id,
							conditions: targetAvailability,
							availability: targetAvailability
						};
				} else {
					item = menu.categories[activeMobileMenu?.data?.categoryIndex];
				}
				initialValues = cloneDeep(item);
			}
		}

		return (
			<RestaurantFormCategoryV2
				type={activeMobileMenu?.data?.categoryIndex === -1 ? "create" : "edit"}
				menuIndex={activeMobileMenu?.data?.menuIndex}
				initialValues={initialValues}
				close={() => this.setPanel(null, [])}
				v2VisibilityDelegate={this.v2FormVisibilityDelegate}
				onReceiptPrinterCategoryConflict={
					this.injected.store.isNewMenuSchema ? this.openCategoryReceiptPrinterOverride : undefined
				}
				v2PrintersDelegate={this.v2FormPrintersDelegate}
			/>
		);
	};

	panel_dish = () => {
		const r = this.injected.store.restaurant!;
		const { panel, panel_index } = this.state;
		const [menuIndex, categoryIndex, dishIndex] = panel_index;
		const { store } = this.injected;
		const { activeMobileMenu } = store;
		const active = activeMobileMenu?.data?.panel === "item";
		let initialValues = null;
		if (active) {
			let menu;
			let category: T.Schema.Restaurant.Menu.RestaurantCategory | T.Schema.Restaurant.Menu.RestaurantCategoryV2 | null = null;
			let item;
			if (activeMobileMenu?.data?.dishIndex === -1) {
				if(store.isNewMenuSchema) {
					//@ts-ignore
					menu = (r.menus_v2 || [])[activeMobileMenu?.data?.menuIndex];
					//@ts-ignore
					category = menu.categories[activeMobileMenu?.data?.categoryIndex];
					//@ts-ignore
					initialValues = this.create_dish(menu.id, category.id);
				} else {
					menu = r.menus[activeMobileMenu?.data?.menuIndex];
					category = menu.categories[activeMobileMenu?.data?.categoryIndex];
					initialValues = this.create_dish(menu._id, category._id);
				}
			} else {
				if(!store.isNewMenuSchema) {
					menu = r.menus[activeMobileMenu?.data?.menuIndex];
					category = menu.categories[activeMobileMenu?.data?.categoryIndex];
					item = category.dishes[
							activeMobileMenu?.data?.dishIndex
						];
					item = cloneDeep(item);
					item.taxes = [];
				} else {
					//@ts-ignore
					menu = (r.menus_v2 || [])[activeMobileMenu?.data?.menuIndex];
					category = menu.categories[activeMobileMenu?.data?.categoryIndex];
					//@ts-ignore
          const targetItem = menu.items[activeMobileMenu?.data?.dishIndex];
          const targetAvailability = menu.availabilities.find(a => a.id === targetItem.availability_id);

					//@ts-ignore
					item = {
						...targetItem,
						conditions: targetAvailability,
						availability_id: targetItem.availability_id,
						availability: targetAvailability,
						option_sets: targetItem.modifiers,
						option_set_primary:
							(targetItem as any).primary_modifier ||
							(targetItem as any).option_set_primary ||
							"",
						choices: (targetItem.choices || []).map((choice: any) => ({
							_id: choice.id,
							name: choice.name,
							dishes: choice.items || [],
							selected: null,
							lpo: choice.lowest_price_option || 0,
						})),
						image: CoreUtils.image.ucReverse(targetItem.media_url),
						taxes: (targetItem.taxes || []).map((t: any) =>
							typeof t === 'string' ? t : getId(t)
						),
					}
				}
				initialValues = cloneDeep(item);
			}
		}
		return (
			<RestaurantFormDishV2
				type={activeMobileMenu?.data?.dishIndex === -1 ? "create" : "edit"}
				menuIndex={activeMobileMenu?.data?.menuIndex}
				categoryIndex={activeMobileMenu?.data?.categoryIndex}
				//@ts-ignore
				initialValues={initialValues}
			/>
		);
	};

	sidebar_panel_dish = () => {
		const { panel_pause, currentMenuItemId, currentMenuItem, currentMenuItems } = this.state;
		const handleCloseSidebar = () => {
			this.setOnPause(null, [], "", null, []);
			this.setState({
				selected_dishes: [],
				bulkSelectedDishesSheetDismissed: false,
			});
		}
		const r = this.injected.store.restaurant!;
		const openHours = r.location && r.location.opening_hours ? r.location.opening_hours : [];
		const specialHours = r.location && r.location.special_hours ? r.location.special_hours : [];

		const nextDayOpenTime = getNextDayOpenTime(openHours, specialHours);
		return (
			<PauseSidebar
				isOpen={panel_pause === MENU_COMPONENTS.ITEM}
				close={handleCloseSidebar}
				panel={MENU_COMPONENTS.ITEM}
				currentMenuItemId={currentMenuItemId}
				//@ts-ignore
				currentMenuItem={currentMenuItem}
				//@ts-ignore
				currentMenuItems={currentMenuItems}
				nextDayOpenTime={nextDayOpenTime}
			/>
		);
	};

	modal_copy = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const { copy_type, copy_index, copy_loading } = this.state;

		const [menuIndex, categoryIndex, dishIndex] = copy_index;

		let item;
		let copyFn;
		const menus_v2 = r.menus_v2 || [];
		if (copy_type === "menu") {
			if(store.isNewMenuSchema) {
				item = menus_v2[menuIndex];
			} else {
				item = r.menus[menuIndex];
			}
			copyFn = () => this.menu_copy(menuIndex);
		} else if (copy_type === "category") {
			if(store.isNewMenuSchema) {
				item = menus_v2[menuIndex].categories[categoryIndex]
			} else {
				item = r.menus[menuIndex].categories[categoryIndex];
			}
			copyFn = () => this.category_copy(menuIndex, categoryIndex);
		} else if (copy_type === "item") {
			if(store.isNewMenuSchema) {
				item = menus_v2[menuIndex].items[dishIndex];
			} else {
				item = r.menus[menuIndex].categories[categoryIndex].dishes[dishIndex];
			}
			copyFn = () => this.dish_copy(menuIndex, categoryIndex, dishIndex);
		}

		if(!item) return null
		return (
			<Modal
				width="sm"
				close={this.modal_copy_close}
				closeButton={false}
				active={!!item}
			>
				<ModalContent className="flex-l-r-center">
					<h4 className="">Copy {copy_type}</h4>
					<p className="big underline">
						{ item.name }
					</p>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button
						type="button"
						className="m-r-2 width100 max100px"
						onClick={this.modal_copy_close}
					>
						Cancel
					</Button>
					<Button
						type="button"
						className="width100 max100px"
						color="primary"
						onClick={copyFn}
					>
						{copy_loading && (
							<RotateLoader size={2} color="white" />
						)}
						{!copy_loading && "Copy"}
					</Button>
				</ModalContent>
			</Modal>
		);
	};
	modal_remove = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const { remove_type, remove_index, remove_loading } = this.state;

		const [menuIndex, categoryIndex, dishIndex] = remove_index;

		let item;
		let removeFn;
		const menus_v2 = r.menus_v2 || [];
		if (remove_type === "menu") {
			if(store.isNewMenuSchema) {
				item = menus_v2[menuIndex];
			} else {
				item = r.menus[menuIndex];
			}
			removeFn = () => this.menu_remove(menuIndex);
		} else if (remove_type === "category") {
			if(store.isNewMenuSchema) {
				item = menus_v2[menuIndex].categories[categoryIndex];
			} else {
				item = r.menus[menuIndex].categories[categoryIndex];
			}
			removeFn = () => this.category_remove(menuIndex, categoryIndex);
		} else if (remove_type === "item") {
			let dishId: string;
			if(store.isNewMenuSchema) {
				item = menus_v2[menuIndex].items[dishIndex];
				if(item) {
					dishId = item.id;
				}
			} else {
				item = r.menus[menuIndex].categories[categoryIndex].dishes[dishIndex];
				if(item) {
					dishId = item._id;
				}
			}
			removeFn = () => this.dish_remove(menuIndex, categoryIndex, dishIndex, dishId);
		}

		if(!item) return null
		return (
			<Modal
				width="sm"
				close={this.modal_remove_close}
				closeButton={false}
				active={!!item}
			>
				<ModalContent className="flex-l-r-center">
					<h4 className="">Delete {remove_type}</h4>
					<p className="big underline">
						{ item.name }
					</p>
				</ModalContent>
				<ModalContent className="flex-right">
					<Button
						type="button"
						className="m-r-2 width100 max100px"
						onClick={this.modal_remove_close}
					>
						Cancel
					</Button>
					<Button
						type="button"
						className="width100 max100px"
						color="primary"
						onClick={removeFn}
					>
						{remove_loading && (
							<RotateLoader size={2} color="white" />
						)}
						{!remove_loading && "Delete"}
					</Button>
				</ModalContent>
			</Modal>
		);
	};
	menu_copy = async (menuIndex: number) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		if(store.isNewMenuSchema) {
			try {
				const menu = cloneDeep((r.menus_v2 || [])[menuIndex]);
				this.setState({
					copy_loading: true
				});
        const menuCopied = await store.api.menu_copy_node({
          restaurant_id: r._id,
          menu_id: menu.id,
          type: 'menu'
        });
				store.updateRestaurant({
					menus_v2: (r.menus_v2 || []).concat(menuCopied)
				});
				UI.notification.success("Menu copied")
			} catch(e) {
				UI.notification.error(e.error)
			}
			this.modal_copy_close();
		} else {
			await this.copy({
				successMsg: "Menu copied",
				process: (r) => {
					const menu = cloneDeep(r.menus[menuIndex]);
					const menu_id = shortid.generate();
					menu._id = menu_id;
					menu.name = `${menu.name} - Copy`;
					for (let i = 0; i < menu.categories.length; i++) {
						const category_id = shortid.generate();
						menu.categories[i]._id = category_id;
						menu.categories[i].menu_id = menu_id;
						for (let k = 0; k < menu.categories[i].dishes.length; k++) {
							menu.categories[i].dishes[k]._id = shortid.generate();
							menu.categories[i].dishes[k].menu_id = menu_id;
							menu.categories[i].dishes[k].category_id = category_id;
						}
					}
					r.menus.splice(menuIndex + 1, 0, menu);
				},
			});
		}
	};
	menu_remove = async (menuIndex: number) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		if(store.isNewMenuSchema) {
			const targetMenu = (r.menus_v2 || [])[menuIndex]
			console.log('targetMenu', targetMenu)
			this.setState({ remove_loading: true })
			try {
				const deleteRes = await store.api.menu_delete_menu({
					restaurant_id: r._id,
					menu_id: targetMenu.id
				})
				store.updateRestaurant({
					menus_v2: (r.menus_v2 || []).filter(m => m.id !== targetMenu.id)
				})

        if (deleteRes.error) {
          UI.notification.error(deleteRes.message);
          return;
        } else {
          UI.notification.success("Menu deleted");
        }
				this.modal_remove_close()
			} catch(e) {
				UI.notification.error(e.error)
			}
			this.setState({ remove_loading: false })
			return
		} else {
			await this.remove({
				successMsg: "Menu deleted",
				process: (r) => {
					r.menus.splice(menuIndex, 1);
				},
			});
		}
	};
	category_copy = async (menuIndex: number, categoryIndex: number) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		if(store.isNewMenuSchema) {
			try {
				const menus = (r.menus_v2 || [])
				const menu = cloneDeep(menus[menuIndex]);
				const category = cloneDeep(menu.categories[categoryIndex]);
        const categoryCopied = await store.api.menu_copy_node({
          restaurant_id: r._id,
          menu_id: menu.id,
          category_id: category.id,
          type: 'category'
        });
				menu.categories.push(categoryCopied.category);
        menu.items.push(...categoryCopied.items);
				menus[menuIndex] = menu
				store.updateRestaurant({
					menus_v2: menus
				});
				UI.notification.success("Category copied");
				this.modal_copy_close();
			} catch(e) {
				UI.notification.error(e.error)
			};
		} else {
			await this.copy({
				successMsg: "Category copied",
				process: (r) => {
					const menu = r.menus[menuIndex];
					const category = cloneDeep(menu.categories[categoryIndex]);
					const category_id = shortid.generate();
					category._id = category_id;
					category.name = `${category.name} - Copy`;
					for (let i = 0; i < category.dishes.length; i++) {
						category.dishes[i]._id = shortid.generate();
						category.dishes[i].category_id = category_id;
					}
					r.menus[menuIndex].categories.splice(
						categoryIndex + 1,
						0,
						category
					);
				},
			});
		}
	};
	category_remove = async (menuIndex: number, categoryIndex: number) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		if(store.isNewMenuSchema) {
			const menus = (r.menus_v2 || [])
			const menu = cloneDeep(menus[menuIndex]);
			const category = cloneDeep(menu.categories[categoryIndex]);

			this.setState({ remove_loading: true })
			try {
				await store.api.menu_delete_category({
					restaurant_id: r._id,
					menu_id: menu.id,
					category_id: category.id
				})
				UI.notification.success("Category deleted")
				menu.categories = menu.categories.filter(c => c.id !== category.id)

				menus[menuIndex] = menu
				store.updateRestaurant({
					menus_v2: menus
				});
				this.modal_remove_close()
			} catch(e) {
				UI.notification.error(e.error)
			}
			this.setState({ remove_loading: false })
			return
		} else {
			await this.remove({
				successMsg: "Category deleted",
				process: (r) => {
					r.menus[menuIndex].categories.splice(categoryIndex, 1);
				},
			});
		}
	};
	dish_copy = async (
		menuIndex: number,
		categoryIndex: number,
		dishIndex: number
	) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		if(store.isNewMenuSchema) {
			const menus = (r.menus_v2 || [])
			const menu = cloneDeep(menus[menuIndex]);
			const category = cloneDeep(menu.categories[categoryIndex]);
			const item = cloneDeep(menus[menuIndex].items[dishIndex]);
			this.setState({
				copy_loading: true
			})
			try {
				const itemCopied = await store.api.menu_copy_node({
					restaurant_id: r._id,
					menu_id: menu.id,
					category_id: category.id,
					item_id: item.id,
          type: 'item'
				})
				menu.items.push(itemCopied)
				menus[menuIndex] = menu
				store.updateRestaurant({
					menus_v2: menus
				})
				UI.notification.success("Item copied")
				this.modal_copy_close()
			} catch(e) {
				UI.notification.error(e.error)
			}
			this.setState({
				copy_loading: false
			})

		} else {
			await this.copy({
				successMsg: "Item copied",
				process: (r) => {
					const item = cloneDeep(
						r.menus[menuIndex].categories[categoryIndex].dishes[
						dishIndex
						]
					);
					item._id = shortid.generate();
					item.name = `${item.name} - Copy`;
					r.menus[menuIndex].categories[categoryIndex].dishes.splice(
						categoryIndex + 1,
						0,
						item
					);
				},
			});
		}
	};
	dish_remove = async (
		menuIndex: number,
		categoryIndex: number,
		dishIndex: number,
		dishId: string
	) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		if(store.isNewMenuSchema) {
			const menus = (r.menus_v2 || [])
			const menu = cloneDeep(menus[menuIndex]);
			const category = cloneDeep(menu.categories[categoryIndex]);
			console.log('dish remove dishid', dishId)
			this.setState({
				remove_loading: true
			})
			try {
				await store.api.menu_delete_item({
					restaurant_id: r._id,
					menu_id: menu.id,
					category_id: category.id,
					item_id: dishId
				})
				menu.items = menu.items.filter(item => item.id !== dishId)
				menus[menuIndex] = menu
				store.updateRestaurant({
					menus_v2: menus
				})
				this.modal_remove_close()
				UI.notification.success("Item deleted")
			} catch(e) {
				UI.notification.error(e.error)
			}
			this.setState({
				remove_loading: false
			})
		} else {
			await this.remove({
				successMsg: "Item deleted",
				dishId: dishId,
				process: (r) => {
					r.menus[menuIndex].categories[categoryIndex].dishes.splice(
						dishIndex,
						1
					);
				},
			});
		}
	};

	dish_stock_update = async (dishId: string) => {
		const inputValue = prompt(
			`Enter a numeric value for the stock quantity of this item. Leave blank to disable stock management`
		);

		if (inputValue === null) {
			return;
		}

		let value: "" | number;
		try {
			value = inputValue === "" ? "" : parseInt(inputValue);
		} catch (e) {
			UI.notification.error(
				"Please enter a numeric value or leave blank"
			);
			return;
		}

		const { store } = this.injected;

		try {
			Untrusive.start();
			const { stock } = await store.api.menu_stock_update({
				_id: store.restaurant!._id,
				items: [
					{
						_id: dishId,
						key: "dishes",
						value: value,
					},
				],
			});
			store.setRestaurantStock(stock);
			UI.notification.success("Stock updated");
		} catch (e) {
			logger.captureException(e);
			UI.notification.error("Something went wrong, try again");
		} finally {
			Untrusive.stop();
		}
	};

	copy = async (opts: {
		process: (r: T.Schema.Restaurant.RestaurantSchema) => void;
		successMsg: string;
	}) => {
		await this.saveRestaurant({
			successMsg: opts.successMsg,
			process: (r) => {
				opts.process(r);
				return {
					update: { $set: { menus: r.menus } },
				};
			},
			before: () => this.setState({ copy_loading: true }),
			onSuccess: () => this.modal_copy_close(),
			onFail: () => this.setState({ copy_loading: false }),
			onError: () => this.setState({ copy_loading: false }),
		});
	};
	remove = async (opts: {
		process: (r: T.Schema.Restaurant.RestaurantSchema) => void;
		successMsg: string;
		dishId?: string;
	}) => {
		await this.saveRestaurant({
			successMsg: opts.successMsg,
			process: (r) => {
				opts.process(r);
				return {
					update: { $set: { menus: r.menus } },
				};
			},
			before: () => this.setState({ remove_loading: true }),
			onSuccess: () => {
				this.modal_remove_close();
				if (opts.dishId) {
					this.injected.store.api
						.menu_stock_update({
							_id: this.injected.store.restaurant!._id,
							items: [
								{
									_id: opts.dishId,
									key: "dishes",
									value: "",
								},
							],
						})
						.catch(logger.captureException);
				}
			},
			onFail: () => this.setState({ remove_loading: false }),
			onError: () => this.setState({ remove_loading: false }),
		});
	};

	modal_copy_open = (type: MenuComponents, index: number[]) => {
		this.setState({
			copy_type: type,
			copy_index: index,
			copy_loading: false,
		});
	};
	modal_copy_close = () => {
		this.setState({
			copy_type: null,
			copy_index: [],
			copy_loading: false,
		});
	};
	modal_remove_open = (type: MenuComponents, index: number[]) => {
		this.setState({
			remove_type: type,
			remove_index: index,
			remove_loading: false,
		});
	};
	modal_remove_close = () => {
		this.setState({
			remove_type: null,
			remove_index: [],
			remove_loading: false,
		});
	};

	toggle_selected_dish = (dish_id: string) => {
		const prev = this.state.selected_dishes;
		const selected_dishes = [...prev];
		const index = selected_dishes.indexOf(dish_id);
		if (index === -1) selected_dishes.push(dish_id);
		else selected_dishes.splice(index, 1);
		const reopenSheet = prev.length === 0 && selected_dishes.length > 0;
		this.setState({
			selected_dishes,
			bulkSelectedDishesSheetDismissed: reopenSheet
				? false
				: selected_dishes.length === 0
					? false
					: this.state.bulkSelectedDishesSheetDismissed,
		});
	};
	float_button_cancel = () => {
		this.setState({
			selected_dishes: [],
			activeDeleteItemModal: false,
			bulkSelectedDishesSheetDismissed: false,
		});
	};
	closeBulkSelectedDishesSheet = () => {
		this.setState({
			bulkSelectedDishesSheetDismissed: true,
			selected_dishes: [],
			activeDeleteItemModal: false,
		});
	};

	/** Reset v2 bulk item selection so checkboxes clear and the bulk actions sheet can open again. */
	clearBulkItemSelectionState = () => {
		this.setState({
			selected_dishes: [],
			bulkSelectedDishesSheetDismissed: false,
			menuColorBulkItemIds: null,
			printerAssignBulkItemIds: null,
			printerAssignTarget: null,
		});
	};

	closeBulkItemPreOverrideModal = () => {
		this.setState({ preOverrideContext: null });
		this.clearBulkItemSelectionState();
		this.injected.store.setActiveMobileModal('');
	};

	isBulkItemPreOverridePending = (pending?: MenuPendingOverride | null) =>
		!!(pending?.bulkItemRefs && pending.bulkItemRefs.length > 0);
	/** Select every item in the same category as the first selected dish (matches list_dish_v2 scope). */
	bulkSelectAllInCategoryOfFirst = () => {
		const { selected_dishes } = this.state;
		if (selected_dishes.length === 0) return;
		const r = this.injected.store.restaurant!;
		const menus = r.menus_v2 || [];
		const found = findItemInMenusV2(menus, selected_dishes[0]);
		if (!found) return;
		const menu = menus[found.menuIndex];
		const catId = found.categoryId;
		const ids = (menu.items || [])
			.filter((i: any) => i.category_id === catId)
			.map((i: any) => i.id);
		this.setState({ selected_dishes: [...new Set(ids)] });
	};
	openBulkItemColorFromSheet = () => {
		const { selected_dishes } = this.state;
		if (selected_dishes.length === 0) return;
		const r = this.injected.store.restaurant!;
		const menus = r.menus_v2 || [];
		const found = findItemInMenusV2(menus, selected_dishes[0]);
		if (!found) return;
		const menu = menus[found.menuIndex];
		const category = menu.categories?.find((c: any) => c.id === found.categoryId);
		const categoryIndex = menu.categories?.findIndex((c: any) => c.id === found.categoryId) ?? -1;
		const { item } = found;
		this.setState({
			activeMenuColorModal: true,
			activeMenuColorTitle:
				selected_dishes.length > 1 ? `${selected_dishes.length} items` : item.name,
			targetItem: 'item',
			targetIndex: found.globalIdx,
			targetMenuIndex: found.menuIndex,
			targetCategoryIndex: categoryIndex >= 0 ? categoryIndex : 0,
			menuColorBulkItemIds: [...selected_dishes],
			activeMenuColorData: {
				color: item.color || category?.color || menu.color || '',
				media_url: item.media_url || '',
			},
			bulkSelectedDishesSheetDismissed: true,
		});
	};
	openBulkPrinterAssignFromSheet = () => {
		const refs = this.getSelectedItemRefsV2();
		if (refs.length === 0) return;
		const first = refs[0];
		const r = this.injected.store.restaurant!;
		const menus = r.menus_v2 || [];
		const menuIndex = menus.findIndex((m) => m.id === first.menu_id);
		if (menuIndex === -1) return;
		const menu = menus[menuIndex];
		const categoryIndex = menu.categories?.findIndex((c: any) => c.id === first.category_id) ?? -1;
		this.setState({
			printerAssignTarget: {
				menuIndex,
				categoryIndex,
				itemId: first.id,
				menuId: first.menu_id,
				categoryId: first.category_id,
			},
			printerAssignBulkItemIds: [...this.state.selected_dishes],
			bulkSelectedDishesSheetDismissed: true,
		});
		this.injected.store.setActiveMobileModal('desktop-printer-assign-items');
	};

	// Resolve selected item IDs to (menu_id, category_id, item_id) for v2 schema
	getSelectedItemRefsV2 = (): MenuItemRefV2[] => {
		const r = this.injected.store.restaurant!;
		const menus = r.menus_v2 || [];
		const selectedSet = new Set(this.state.selected_dishes);
		const refs: MenuItemRefV2[] = [];
		for (const menu of menus) {
			for (const item of menu.items || []) {
				if (selectedSet.has(item.id)) {
					refs.push({
						menu_id: menu.id,
						category_id: item.category_id,
						id: item.id,
					});
				}
			}
		}
		return refs;
	};

	// Menu v2: update status for selected items via menu_partial_update_item
	float_button_update_status_v2 = async (status: string | null) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const refs = this.getSelectedItemRefsV2();
		if (refs.length === 0) return;
		this.setState({ selected_loading: true });
		try {
      await store.api.menu_partial_update_batch_items({
        restaurant_id: r._id,
        data: {
          items: refs.map((item: any) => ({ ...item, status: status })),
        }
      })
			const menus = cloneDeep(r.menus_v2 || []);
			for (const menu of menus) {
				for (const item of menu.items || []) {
					if (this.state.selected_dishes.indexOf(item.id) !== -1) {
						item.status = status as any;
					}
				}
			}
			store.updateRestaurant({ menus_v2: menus });
			UI.notification.success("Item status set");
		} catch (e) {
			UI.notification.error((e as any)?.error ?? "Failed to update");
		} finally {
			this.setState({
				selected_loading: false,
				selected_dishes: [],
				bulkSelectedDishesSheetDismissed: false,
			});
		}
	};

	/** V2: delete selected items via menu_delete_item (called after modal confirm) */
	float_button_delete_v2 = async () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const refs = this.getSelectedItemRefsV2();
		if (refs.length === 0) {
			this.setState({
				activeDeleteItemModal: false,
				selected_dishes: [],
				bulkSelectedDishesSheetDismissed: false,
			});
			return;
		}
		this.setState({ remove_loading: true });
		try {
			await Promise.all(
				refs.map(({ menu_id, category_id, id }) =>
					store.api.menu_delete_item({
						restaurant_id: r._id,
						menu_id,
						category_id,
						item_id: id,
					})
				)
			);
			const menus = cloneDeep(r.menus_v2 || []);
			const selectedSet = new Set(this.state.selected_dishes);
			for (const menu of menus) {
				menu.items = (menu.items || []).filter((item) => !selectedSet.has(item.id));
			}
			store.updateRestaurant({ menus_v2: menus });
			UI.notification.success("Items deleted");
			this.setState({
				activeDeleteItemModal: false,
				selected_dishes: [],
				bulkSelectedDishesSheetDismissed: false,
			});
		} catch (e) {
			UI.notification.error((e as any)?.error ?? "Failed to delete");
		} finally {
			this.setState({ remove_loading: false });
		}
	};

	showDeleteItemsModal = () => {
		this.setState({
			activeDeleteItemModal: true,
			bulkSelectedDishesSheetDismissed: true,
		});
	};

	float_button_delete = async () => {
		const { store } = this.injected;
		if (store.isNewMenuSchema) {
			await this.float_button_delete_v2();
			return;
		}
		await this.float_button_process({
			successMsg: "Items deleted",
			process: (r, selected_dishes) => {
				for (const [i, menu] of r.menus.entries()) {
					for (const [k, category] of menu.categories.entries()) {
						r.menus[i].categories[k].dishes =
							category.dishes.filter(
								(dish) =>
									selected_dishes.indexOf(dish._id) === -1
							);
					}
				}
			},
		});
	};
	float_button_no_stock = async () => {
		const { store } = this.injected;
		if (store.isNewMenuSchema) {
			await this.float_button_update_status_v2("no-stock");
			return;
		}
		await this.float_button_process({
			successMsg: "Item status set",
			process: (r, selected_dishes) => {
				for (const [i, menu] of r.menus.entries()) {
					for (const [k, category] of menu.categories.entries()) {
						for (const [z, dish] of category.dishes.entries()) {
							if (selected_dishes.indexOf(dish._id) !== -1) {
								r.menus[i].categories[k].dishes[z].status =
									"no-stock";
							}
						}
					}
				}
			},
		});
	};
	float_button_hidden = async () => {
		const { store } = this.injected;
		if (store.isNewMenuSchema) {
			await this.float_button_update_status_v2("hidden");
			return;
		}
		await this.float_button_process({
			successMsg: "Item status set",
			process: (r, selected_dishes) => {
				for (const [i, menu] of r.menus.entries()) {
					for (const [k, category] of menu.categories.entries()) {
						for (const [z, dish] of category.dishes.entries()) {
							if (selected_dishes.indexOf(dish._id) !== -1) {
								r.menus[i].categories[k].dishes[z].status =
									"hidden";
							}
						}
					}
				}
			},
		});
	};
	float_button_available = async () => {
		const { store } = this.injected;
		if (store.isNewMenuSchema) {
			await this.float_button_update_status_v2(null);
			return;
		}
		await this.float_button_process({
			successMsg: "Item status set",
			process: (r, selected_dishes) => {
				for (const [i, menu] of r.menus.entries()) {
					for (const [k, category] of menu.categories.entries()) {
						for (const [z, dish] of category.dishes.entries()) {
							if (selected_dishes.indexOf(dish._id) !== -1) {
								r.menus[i].categories[k].dishes[z].status =
									null;
							}
						}
					}
				}
			},
		});
	};
	float_button_process = async (opts: {
		process: (
			r: T.Schema.Restaurant.RestaurantSchema,
			selected_dishes: string[]
		) => void;
		successMsg: string;
	}) => {
		const { selected_dishes } = this.state;
		await this.saveRestaurant({
			successMsg: opts.successMsg,
			process: (r) => {
				opts.process(r, selected_dishes);
				return {
					update: { $set: { menus: r.menus } },
				};
			},
			before: () => this.setState({ selected_loading: true }),
			onSuccess: () =>
				this.setState({
					selected_loading: false,
					selected_dishes: [],
					bulkSelectedDishesSheetDismissed: false,
				}),
			onFail: () => this.setState({ selected_loading: false }),
			onError: () => this.setState({ selected_loading: false }),
		});
	};

	startEditMode = (mode: MenusEditMode) => {
		this.setState({ mode });
		const r = this.injected.store.restaurant!;
		this.menu_backup = cloneDeep(r.menus);
		if (this.injected.store.isNewMenuSchema && r.menus_v2?.length) {
			this.menu_backup_v2 = cloneDeep(r.menus_v2);
		} else {
			this.menu_backup_v2 = null;
		}
	};

	cancelEditMode = () => {
		const r = this.injected.store.restaurant!;
		if (this.injected.store.isNewMenuSchema && this.menu_backup_v2) {
			(this as any).injected.store.updateRestaurant({ menus_v2: this.menu_backup_v2 });
			this.menu_backup_v2 = null;
		} else {
			r.menus = this.menu_backup || [];
		}
		this.setState({ mode: "normal" });
	};

	/** Resolves true when the v2 save succeeded. `modeOverride` lets a pending sort reuse the reOrder save. */
	saveEditMode = async (successMsg: string, modeOverride?: MenusEditMode): Promise<boolean> => {
		const r = this.injected.store.restaurant!;
		const store = this.injected.store;

		if (store.isNewMenuSchema && r.menus_v2?.length) {
			const backup = cloneDeep(r);
			if (this.menu_backup_v2) backup.menus_v2 = this.menu_backup_v2;
			this.setState({ loading: true });
			try {
				const currentMode = modeOverride ?? (this.state as any)?.mode;
				if (currentMode === 'reOrder') {
					const backupMenus = (this.menu_backup_v2 || []) as any[];
					const currentMenus = r.menus_v2 as any[];

					const arraysEqual = (a: string[], b: string[]) =>
						a.length === b.length && a.every((v, i) => v === b[i]);

					const backupItemById = new Map<string, any>();
					for (const m of backupMenus) {
						for (const it of m.items || []) backupItemById.set(it.id, it);
					}
					const backupCategoryToMenu = new Map<string, string>();
					for (const m of backupMenus) {
						for (const c of m.categories || []) backupCategoryToMenu.set(c.id, m.id);
					}

					const categoriesToMove: any[] = [];
					for (const menu of currentMenus) {
						for (const cat of menu.categories || []) {
							const prevMenuId = backupCategoryToMenu.get(cat.id);
							if (prevMenuId && prevMenuId !== menu.id) {
								categoriesToMove.push({
									menu_id: prevMenuId,
									id: cat.id,
									data: { menu_id: menu.id },
								});
							}
						}
					}

					const itemsToMove: any[] = [];
					for (const menu of currentMenus) {
						for (const it of menu.items || []) {
							const prev = backupItemById.get(it.id);
							if (!prev) continue;
							if (prev.category_id !== it.category_id || prev.menu_id !== menu.id) {
								itemsToMove.push({
									menu_id: prev.menu_id,
									category_id: prev.category_id,
									id: it.id,
									data: { menu_id: menu.id, category_id: it.category_id },
								});
							}
						}
					}

					const currentMenuOrder: string[] = currentMenus.map((m: any) => m.id);
					const backupMenuOrder: string[] = backupMenus.map((m: any) => m.id);
					const menuOrderChanged = !arraysEqual(currentMenuOrder, backupMenuOrder);

					const categoryOrderTasks: Array<{ menu_id: string; order: string[] }> = [];
					for (const menu of currentMenus) {
						const currentCatOrder: string[] = (menu.categories || []).map((c: any) => c.id);
						const backupMenu = backupMenus.find((m: any) => m.id === menu.id);
						const backupCatOrder: string[] = backupMenu ? (backupMenu.categories || []).map((c: any) => c.id) : [];
						if (!arraysEqual(currentCatOrder, backupCatOrder)) {
							categoryOrderTasks.push({ menu_id: menu.id, order: currentCatOrder });
						}
					}

					const itemOrderTasks: Array<{ menu_id: string; category_id: string; order: string[] }> = [];
					for (const menu of currentMenus) {
						const backupMenu = backupMenus.find((m: any) => m.id === menu.id);
						for (const cat of menu.categories || []) {
							const currentItemOrder: string[] = (menu.items || [])
								.filter((i: any) => i.category_id === cat.id)
								.map((i: any) => i.id);
							const backupItemOrder: string[] = backupMenu
								? (backupMenu.items || [])
									.filter((i: any) => i.category_id === cat.id)
									.map((i: any) => i.id)
								: [];
							if (!arraysEqual(currentItemOrder, backupItemOrder)) {
								itemOrderTasks.push({ menu_id: menu.id, category_id: cat.id, order: currentItemOrder });
							}
						}
					}

					if (categoriesToMove.length > 0) {
						await store.api.menu_partial_update_batch_categories({
							restaurant_id: r._id,
							data: { categories: categoriesToMove },
						});
					}
					if (itemsToMove.length > 0) {
						await store.api.menu_partial_update_batch_items({
							restaurant_id: r._id,
							data: { items: itemsToMove },
						});
					}
					if (menuOrderChanged) {
						await store.api.menu_order({ restaurant_id: r._id, order: currentMenuOrder });
					}
					for (const task of categoryOrderTasks) {
						await store.api.menu_categories_order({
							restaurant_id: r._id,
							menu_id: task.menu_id,
							order: task.order,
						});
					}
					for (const task of itemOrderTasks) {
						await store.api.menu_items_order({
							restaurant_id: r._id,
							menu_id: task.menu_id,
							category_id: task.category_id,
							order: task.order,
						});
					}
				} else {
					//loop through each menu.items but only the updated ones
					const updatedItems = r.menus_v2.flatMap((menu: any) => menu.items.filter((item: any) => item.price !== this.menu_backup_v2?.find((m: any) => m.id === menu.id)?.items.find((i: any) => i.id === item.id)?.price));
					if (updatedItems.length > 0) {
						await store.api.menu_partial_update_batch_items({
							restaurant_id: r._id,
							data: {
								items: updatedItems.map((item: any) =>
									({ id: item.id, menu_id: item.menu_id, category_id: item.category_id, price: item.price })),
							}
						})
					}
				}
				UI.notification.success(successMsg);
				this.setState({ mode: "normal", loading: false });
				this.menu_backup_v2 = null;
				return true;
			} catch (e) {
				UI.notification.error((e as any)?.message ?? 'Failed to save prices');
				this.setState({ loading: false });
				return false;
			}
		}

		const backup = cloneDeep(r);
		if (this.menu_backup) backup.menus = this.menu_backup;

		await this.saveRestaurantSilent({
			backup: backup,
			update: { $set: { menus: r.menus } },
			successMsg,
			before: () => this.setState({ loading: true }),
			onSuccess: () => {
				this.setState({ mode: "normal", loading: false });
				this.menu_backup = null;
			},
			onFail: () => this.setState({ loading: false }),
			onError: () => this.setState({ loading: false }),
		});
		return true;
	};

	onDragStart = () => { };
	onDragUpdate = () => { };
	onDragEnd = (result: DropResult) => {
		const { destination, source, type } = result;
		if (!destination) return;
		const store = this.injected.store;
		const r = { ...store.restaurant! };

		if (store.isNewMenuSchema && r.menus_v2?.length) {
			const currentMenus = store.restaurant!.menus_v2!;
			if (type === "MENU") {
				if (destination.index !== source.index && source.index >= 0 && destination.index >= 0 && source.index < currentMenus.length && destination.index < currentMenus.length) {
					const next = arrayMove([...currentMenus], source.index, destination.index);
					store.updateRestaurant({ menus_v2: next });
				}
				return;
			}
			const menus = cloneDeep(r.menus_v2) as T.Schema.Restaurant.Menu.RestaurantMenuV2[];
			if (type === "CATEGORY") {
				const menuId = source.droppableId;
				const menuIndex = menus.findIndex((m: any) => m.id === menuId);
				if (menuIndex === -1) return;
				const menu = menus[menuIndex];
				if (destination.droppableId !== source.droppableId) {
					const destMenuId = destination.droppableId;
					const destMenuIndex = menus.findIndex((m: any) => m.id === destMenuId);
					if (destMenuIndex === -1) return;
					const cat = cloneDeep(menu.categories[source.index]) as any;
					cat.menu_id = destMenuId;
					const catItems = (menu.items || []).filter((i: any) => i.category_id === cat.id);
					menu.categories.splice(source.index, 1);
					menus[menuIndex] = { ...menu, items: (menu.items || []).filter((i: any) => i.category_id !== cat.id) };
					const destMenu = menus[destMenuIndex];
					catItems.forEach((it: any) => { it.menu_id = destMenuId; it.category_id = cat.id; });
					destMenu.categories.splice(destination.index, 0, cat);
					destMenu.items = [...(destMenu.items || []), ...catItems];
					menus[destMenuIndex] = destMenu;
				} else if (destination.index !== source.index) {
					menu.categories = arrayMove(menu.categories, source.index, destination.index);
					menu.categories.forEach((c: any, i: number) => { c.sort_order = i; });
					menus[menuIndex] = menu;
				}
				menus.forEach((m, i) => { (m as any).sort_order = i; });
				store.updateRestaurant({ menus_v2: menus });
			} else if (type === "DISH") {
				const categoryId = source.droppableId;
				let menuIndex = -1;
				let categoryIndex = -1;
				for (let mi = 0; mi < menus.length; mi++) {
					const ci = menus[mi].categories.findIndex((c: any) => c.id === categoryId);
					if (ci !== -1) { menuIndex = mi; categoryIndex = ci; break; }
				}
				if (menuIndex === -1) return;
				const menu = menus[menuIndex];
				const category = menu.categories[categoryIndex];
				const categoryItems = (menu.items || []).filter((i: any) => i.category_id === category.id);
				if (destination.droppableId !== source.droppableId) {
					const destCategoryId = destination.droppableId;
					let destMenuIndex = -1, destCategoryIndex = -1;
					for (let mi = 0; mi < menus.length; mi++) {
						const ci = menus[mi].categories.findIndex((c: any) => c.id === destCategoryId);
						if (ci !== -1) { destMenuIndex = mi; destCategoryIndex = ci; break; }
					}
					if (destMenuIndex === -1) return;
					const item = cloneDeep(categoryItems[source.index]) as any;
					item.category_id = destCategoryId;
					item.menu_id = menus[destMenuIndex].id;
					const rest = categoryItems.filter((_: any, i: number) => i !== source.index);
					menu.items = (menu.items || []).filter((i: any) => i.category_id !== category.id).concat(rest);
					menus[menuIndex] = menu;
					const destMenu = menus[destMenuIndex];
					const destCategoryItems = (destMenu.items || []).filter((i: any) => i.category_id === destCategoryId);
					destCategoryItems.splice(destination.index, 0, item);
					destMenu.items = (destMenu.items || []).filter((i: any) => i.category_id !== destCategoryId).concat(destCategoryItems);
					menus[destMenuIndex] = destMenu;
				} else if (destination.index !== source.index) {
					const reordered = arrayMove(categoryItems, source.index, destination.index);
					reordered.forEach((it: any, i: number) => { it.sort_order = i; });
					const otherItems = (menu.items || []).filter((i: any) => i.category_id !== category.id);
					menu.items = otherItems.concat(reordered);
					menus[menuIndex] = menu;
				}
				store.updateRestaurant({ menus_v2: menus });
			}
			return;
		}

		if (type === "MENU") {
			if (destination.index !== source.index) {
				r.menus = arrayMove(r.menus, source.index, destination.index);
				this.injected.store.setRestaurant(r);
			}
		} else if (type === "CATEGORY") {
			const menus = cloneDeep(r.menus);
			if (destination.droppableId !== source.droppableId) {
				const sourceMenu = source.droppableId;
				const destinationMenu = destination.droppableId;
				const sourceMenuIndex = menus.findIndex(
					(m) => m._id === sourceMenu
				);
				const destinationMenuIndex = menus.findIndex(
					(m) => m._id === destinationMenu
				);
				// CLT.ONE CATEGORY
				const item = cloneDeep(
					menus[sourceMenuIndex].categories[source.index]
				);
				item.menu_id = destinationMenu;
				for (let i = 0; i < item.dishes.length; i++) {
					item.dishes[i].menu_id = destinationMenu;
				}
				// REMOVE FROM OLD MENU
				menus[sourceMenuIndex].categories.splice(source.index, 1);
				// ADD TO NEW MENU
				menus[destinationMenuIndex].categories.splice(
					destination.index,
					0,
					item
				);
			} else if (destination.index !== source.index) {
				const menu_id = source.droppableId;
				const menu_index = menus.findIndex((m) => m._id === menu_id);
				menus[menu_index].categories = arrayMove(
					menus[menu_index].categories,
					source.index,
					destination.index
				);
			}
			r.menus = menus;
			this.injected.store.setRestaurant(r);
		} else if (type === "DISH") {
			const menus = cloneDeep(r.menus);
			if (destination.droppableId !== source.droppableId) {
				const sourceCategory = source.droppableId;
				const destinationCategory = destination.droppableId;

				const s = RestaurantUtils.menu.findCategoryById(
					r,
					sourceCategory
				);
				const d = RestaurantUtils.menu.findCategoryById(
					r,
					destinationCategory
				);

				if (!s || !d) return;

				// CLT.ONE CATEGORY
				const item = cloneDeep(
					menus[s.menuIndex].categories[s.categoryIndex].dishes[
					source.index
					]
				);
				item.menu_id = d.menu._id;
				item.category_id = d.category._id;

				// REMOVE FROM CATEGORY
				menus[s.menuIndex].categories[s.categoryIndex].dishes.splice(
					source.index,
					1
				);
				// ADD TO NEW CATEGORY
				menus[d.menuIndex].categories[d.categoryIndex].dishes.splice(
					destination.index,
					0,
					item
				);
			} else if (destination.index !== source.index) {
				const category_id = source.droppableId;
				const query = RestaurantUtils.menu.findCategoryById(
					r,
					category_id
				);
				if (!query) throw new Error("Category not found by id");
				const { menu, category, menuIndex, categoryIndex } = query;
				const dishes =
					menus[menuIndex].categories[categoryIndex].dishes;
				menus[menuIndex].categories[categoryIndex].dishes = arrayMove(
					dishes,
					source.index,
					destination.index
				);
			}
			r.menus = menus;
			this.injected.store.setRestaurant(r);
		}
	};

	float_button_pause = () => {
		const { selected_dishes } = this.state;
		const r = this.injected.store.restaurant!;
    const menus = r.menus_v2 || [];
		const allDishes = menus.flatMap(menu => menu.items);
		const selectedItems = allDishes.filter(item => selected_dishes.includes(item.id));
		this.setState({
			panel_pause: "item",
			currentMenuItems: selectedItems,
			bulkSelectedDishesSheetDismissed: true,
		});
	};

	float_button_resume = () => {
    const { selected_dishes } = this.state;
		if (selected_dishes.length > 0) {
			this.setState({ resumeModalOpen: true, bulkSelectedDishesSheetDismissed: true });
		}
	};

	handleCloseResumeModal = () => {
		this.setState({
			resumeModalOpen: false,
			selected_dishes: [],
			bulkSelectedDishesSheetDismissed: false,
		});
	};

	menu_list_floating = (
		selected_dishes: string[],
		selected_loading: boolean
	) => {
		return (
			<>
				<MenuListFloatingButtonWrapper>
					<MenuListFloatingContent>
						<MenuListFloatingTitle>
							<p className="font-semi-bold big">Actions</p>
							<p className="font-semi-bold small m-t-1">
								{selected_dishes.length} Selected
							</p>
						</MenuListFloatingTitle>
						<Button size="xs" onClick={this.float_button_cancel}>
							Cancel
						</Button>
						<Button size="xs" onClick={this.float_button_available}>
							Available
						</Button>
						<Button size="xs" onClick={this.float_button_no_stock}>
							No Stock
						</Button>
						<Button size="xs" onClick={this.float_button_hidden}>
							Hide
						</Button>
						<Button size="xs" onClick={this.showDeleteItemsModal}>
							Delete
						</Button>
						<Button size="xs" onClick={this.float_button_pause}>
							Pause Item
						</Button>
						<Button size="xs" onClick={this.float_button_resume}>
							Resume Item
						</Button>
						{selected_loading && (
							<div className="text-center p-1">
								<RotateLoader size={2} />
							</div>
						)}
					</MenuListFloatingContent>
				</MenuListFloatingButtonWrapper>
			</>
		);
	};

	saveCancelGroupButton(
		saveCallback: () => void,
		cancelCallback: () => void,
		loading: boolean,
		helperText?: string
	) {
		return (
			<div>
				<div className="grid-2 xs xs-gap m-t-10">
					<div className="col">
						<MenuListButton onClick={saveCallback}>
							{loading && <RotateLoader size={2} color="white" />}
							{!loading && "Save"}
						</MenuListButton>
					</div>
					<div className="col">
						<MenuListButton onClick={cancelCallback}>
							Cancel
						</MenuListButton>
					</div>
				</div>
				{helperText && (
					<div className="text-center m-t-3 font-semi-bold">
						<p>{helperText}</p>
					</div>
				)}
			</div>
		);
	}

	saveCancelGroupButtonV2(
		saveCallback: () => void,
		cancelCallback: () => void,
		loading: boolean,
		helperText?: string
	) {
		return (
			<EditPricesModeWrapper>
				<div className="grid-2 xs xs-gap edit-prices-mode-buttons">
					<div className="col">
						<ButtonV2 onClick={saveCallback}>
							{loading && <RotateLoader size={2} color="white" />}
							{!loading && "Save"}
						</ButtonV2>
					</div>
					<div className="col">
						<ButtonV2
							variant="default"
							onClick={cancelCallback}>
							Cancel
						</ButtonV2>
					</div>
				</div>
				{helperText && (
					<div className="text-center m-t-3 font-semi-bold">
						<p>{helperText}</p>
					</div>
				)}
			</EditPricesModeWrapper>
		);
	}

	openMenuMoreActions = (index: number, type: string) => {
		this.setState({
			targetMenuIndex: index,
			targetItem: type
		});
		this.injected.store.setActiveMobileModal('menu-more-actions');
	}
	openCategoryMoreActions = (index: number, menuIndex: number, type: string) => {
		this.setState({
			targetMenuIndex: menuIndex,
			targetCategoryIndex: index,
			targetItem: type
		});
		this.injected.store.setActiveMobileModal('category-more-actions');
	}
	openItemMoreActions = (index: number, categoryIndex: number, menuIndex: number, type: string) => {
		this.setState({
			targetMenuIndex: menuIndex,
			targetCategoryIndex: categoryIndex,
			targetIndex: index,
			targetItem: type
		});
		this.injected.store.setActiveMobileModal('item-more-actions');
	}

	closeMenuMoreActions = () => {
		this.setState({
			targetMenuIndex: -1,
			targetCategoryIndex: -1,
			targetIndex: -1,
			targetItem: 'menu'
		});
		this.injected.store.setActiveMobileModal('');
	}

	closeCategoryMoreActions = () => {
		this.setState({
			targetMenuIndex: -1,
			targetCategoryIndex: -1,
			targetIndex: -1,
			targetItem: 'category'
		});
		this.injected.store.setActiveMobileModal('');
	}

	closeItemMoreActions = () => {
		this.setState({
			targetMenuIndex: -1,
			targetCategoryIndex: -1,
			targetIndex: -1,
			targetItem: 'item'
		});
		this.injected.store.setActiveMobileModal('');
	}

	modal_menu_more_actions = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const { activeMobileModal, setActiveMobileModal } = store;
		let menu;
		if(store.isNewMenuSchema) {
			menu = (r.menus_v2 || [])[this.state.targetMenuIndex];
		} else {
			menu = r.menus[this.state.targetMenuIndex];
		}
		if(!menu) return null;
		let title = menu?.name || 'Menu';
		return (
			<MenuMoreAction
				show={activeMobileModal === 'menu-more-actions'}
				title={title || ''	}
				onHide={this.closeMenuMoreActions}
				onSave={() => {
					setActiveMobileModal('')
					UI.notification.success('Menu updated');
				}}>
				<MenuActionContainer>
					<MenuActionItem onClick={() => {
						store.setActiveMobileMenu('edit-menu', 'menus', {panel: 'menu', index: this.state.targetMenuIndex});
						this.closeMenuMoreActions();
					}}>
						<IconEdit /> Edit
					</MenuActionItem>
					<MenuActionItem onClick={() => {
						this.modal_remove_open("menu", [this.state.targetMenuIndex]);
						this.closeMenuMoreActions();
					}}>
						<IconDelete /> Delete
					</MenuActionItem>
					<MenuActionItem onClick={() => {
						this.modal_copy_open("menu", [this.state.targetMenuIndex]);
						this.closeMenuMoreActions();
					}}>
						<IconCopyAlt /> Duplicate
					</MenuActionItem>
					<MenuActionItem onClick={() => {
						this.setState({ history_menu_id: (menu as any).id || (menu as any)._id });
						this.closeMenuMoreActions();
					}}>
						<FaHistory /> Version history
					</MenuActionItem>
				</MenuActionContainer>
			</MenuMoreAction>
		)
	}
	modal_category_more_actions = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const { activeMobileModal, setActiveMobileModal } = store;
		let category;
		if(store.isNewMenuSchema) {
			category = (r.menus_v2 || [])[this.state.targetMenuIndex]?.categories[this.state.targetCategoryIndex];
		} else {
			category = r.menus[this.state.targetMenuIndex]?.categories[this.state.targetCategoryIndex];
		}
		if(!category) return null;
		let title = category?.name || 'Category';
		return (
			<MenuMoreAction
				show={activeMobileModal === 'category-more-actions'}
				title={title}
				onHide={this.closeMenuMoreActions}
				onSave={() => {
					setActiveMobileModal('')
					UI.notification.success('Category updated');
				}}>
				<MenuActionContainer>
					<MenuActionItem onClick={() => {
						store.setActiveMobileMenu('edit-category', 'menus', {panel: 'category', menuIndex: this.state.targetMenuIndex, categoryIndex: this.state.targetCategoryIndex});
						this.closeCategoryMoreActions();
					}}>
						<IconEdit /> Edit
					</MenuActionItem>
					<MenuActionItem onClick={() => {
						this.modal_remove_open("category", [this.state.targetMenuIndex, this.state.targetCategoryIndex]);
						this.closeCategoryMoreActions();
					}}>
						<IconDelete /> Delete
					</MenuActionItem>
					<MenuActionItem onClick={() => {
						this.modal_copy_open("category", [this.state.targetMenuIndex, this.state.targetCategoryIndex]);
						this.closeCategoryMoreActions();
					}}>
						<IconCopyAlt /> Duplicate
					</MenuActionItem>
				</MenuActionContainer>
			</MenuMoreAction>
		)
	}
	modal_item_more_actions = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const { activeMobileModal, setActiveMobileModal } = store;
		let item;
		if(store.isNewMenuSchema) {
			item = (r.menus_v2 || [])[this.state.targetMenuIndex]?.items[this.state.targetIndex] as T.Schema.Restaurant.Menu.RestaurantItem;
		} else {
			item = r.menus[this.state.targetMenuIndex]?.categories[this.state.targetCategoryIndex]?.dishes[this.state.targetIndex];
		}
		if(!item) return null;
		let title = item?.name || 'Item';
		return (
			<MenuMoreAction
				show={activeMobileModal === 'item-more-actions'}
				title={title}
				onHide={this.closeItemMoreActions}
				onSave={() => {
					setActiveMobileModal('')
					UI.notification.success('Item updated');
				}}>
				<MenuActionContainer>
					<MenuActionItem onClick={() => {
						this.setOnPause("item", [
							this.state.targetMenuIndex,
							this.state.targetCategoryIndex,
							this.state.targetIndex,
							//@ts-ignore
						], item?.id || item?._id, item, [item])
						this.closeItemMoreActions();
					}}>
						<IoIosPause /> Pause
					</MenuActionItem>
					<MenuActionItem onClick={() => {
						store.setActiveMobileMenu('edit-item', 'menus', {panel: 'item', menuIndex: this.state.targetMenuIndex, categoryIndex: this.state.targetCategoryIndex, dishIndex: this.state.targetIndex});
						this.closeItemMoreActions();
					}}>
						<IconEdit /> Edit
					</MenuActionItem>
					<MenuActionItem onClick={() => {
						this.modal_remove_open("item", [this.state.targetMenuIndex, this.state.targetCategoryIndex, this.state.targetIndex]);
						this.closeItemMoreActions();
					}}>
						<IconDelete /> Delete
					</MenuActionItem>
					<MenuActionItem onClick={() => {
						this.modal_copy_open("item", [this.state.targetMenuIndex, this.state.targetCategoryIndex, this.state.targetIndex]);
						this.closeItemMoreActions();
					}}>
						<IconCopyAlt /> Duplicate
					</MenuActionItem>
				</MenuActionContainer>
			</MenuMoreAction>
		)
	}

	render() {
		const {
			selected_dishes,
			selected_loading,
			activeMenuColorModal,
			activeMenuColorTitle,
			activeMenuColorData,
			activeMenuVisibilityModal,
			activeMenuPrevisibilityModal,
			targetItem,
			menuColorBulkItemIds,
			resumeModalOpen,
			activeDeleteItemModal,
			remove_loading,
			mode,
			bulkSelectedDishesSheetDismissed,
			hideMoreActionsOnMobile
		} = this.state;

		const { store } = this.injected;
		const { activeMobileModal, setActiveMobileModal, openMenuListItemActive } = store;
		const r = store.restaurant;

		const menuEditCollaboration =
			store.organisation?.dashboard === 'v2' && store.isNewMenuSchema;
		const menuEditLock = store.ably.menuEditLock;
		const menuEditMyConnectionId = store.notifications.ablyRestaurant?.connection?.id;
		const menuEditBlocked =
			menuEditCollaboration &&
			store.ably.connected_once &&
			!!menuEditLock &&
			!!menuEditMyConnectionId &&
			menuEditLock.connectionId !== menuEditMyConnectionId;
		const menusV2 = (r?.menus_v2 ?? []) as any[];
		const derivedOverrideTarget: OverrideTargetEntity | null = (() => {
			if (!openMenuListItemActive || !store.isNewMenuSchema || !menusV2.length) return null;
			const parts = openMenuListItemActive.split('_');
			if (parts.length === 1) {
				const menu = menusV2.find((m: any) => m.id === parts[0]);
				return menu ? { type: 'menu', name: menu.name } : null;
			}
			if (parts.length === 2) {
				const menu = menusV2.find((m: any) => m.id === parts[0]);
				const category = menu?.categories?.find((c: any) => c.id === parts[1]);
				return category ? { type: 'category', name: category.name } : null;
			}
			if (parts.length === 3) {
				const menu = menusV2.find((m: any) => m.id === parts[0]);
				const item = menu?.items?.find((i: any) => i.id === parts[2]);
				return item ? { type: 'item', name: item.name } : null;
			}
			return null;
		})();
		const overrideTargetEntity = this.state.preOverrideContext?.targetEntity ?? derivedOverrideTarget;
		const overrideChangesSummary = this.state.preOverrideContext?.changesSummary ?? null;
		const overrideEditKind = this.state.preOverrideContext?.editKind ?? 'visibility';
		const overrideAffectedEntities = this.state.preOverrideContext?.affectedEntities ?? null;
		const overrideSource = (() => {
			if (this.state.preOverrideContext?.overrideSource) return this.state.preOverrideContext.overrideSource;
			if (!openMenuListItemActive || !store.isNewMenuSchema || !menusV2.length) return undefined;
			const parts = openMenuListItemActive.split('_');
			return {
				menuId: parts[0],
				categoryId: parts.length >= 2 ? parts[1] : undefined,
				itemId: parts.length >= 3 ? parts[2] : undefined,
			};
		})();

		return (
			<>
				<ResumeModal
					isOpen={resumeModalOpen}
					handleClose={this.handleCloseResumeModal}
					menuItemIds={selected_dishes}
				/>
				{activeDeleteItemModal && selected_dishes.length > 0 && (
					<div>
						<BulkConfirmModal
							active={activeDeleteItemModal}
							title={
								selected_dishes.length > 1
									? `Delete ${selected_dishes.length} items`
									: "Delete Item"
							}
							description="This will permanently delete the selected items."
							confirmText="Delete"
							loading={remove_loading}
							onClose={this.float_button_cancel}
							onConfirm={this.float_button_delete}
						/>
					</div>
				)}
				{store.isNewMenuSchema &&
					mode === "normal" &&
					selected_dishes.length > 0 &&
					!bulkSelectedDishesSheetDismissed && (
						<MenuMoreAction
							show={true}
							title={`More Actions`}
							onHide={this.closeBulkSelectedDishesSheet}
							onSave={() => {}}
							itemCount={selected_dishes.length}
							hideOnMobile={hideMoreActionsOnMobile}
						>
							<MenuActionContainer>

								<MenuActionItem onClick={this.float_button_available}>Available</MenuActionItem>
								<MenuActionItem onClick={this.float_button_no_stock}>No Stock</MenuActionItem>
								<MenuActionItem onClick={this.float_button_hidden}>Hide</MenuActionItem>
								<MenuActionItem onClick={this.showDeleteItemsModal}>Delete</MenuActionItem>
								<MenuActionItem onClick={this.float_button_pause}>
									Pause Items
								</MenuActionItem>
								<MenuActionItem onClick={this.float_button_resume}>Resume Items</MenuActionItem>
								<MenuActionItem onClick={this.bulkSelectAllInCategoryOfFirst}>
									Select All
								</MenuActionItem>
								<MenuActionItem onClick={this.openBulkItemColorFromSheet}>
									Item Color
								</MenuActionItem>
								<MenuActionItem onClick={this.openBulkPrinterAssignFromSheet}>
									Assign Printer
								</MenuActionItem>
								{selected_loading && (
									<div className="text-center p-1">
										<RotateLoader size={2} />
									</div>
								)}
							</MenuActionContainer>
						</MenuMoreAction>
					)}
				<div className="relative" style={{ width: '100%'}}>
					<div
						style={{
							opacity: menuEditBlocked ? 0.55 : 1,
							transition: 'opacity 0.15s ease',
						}}>
						{selected_dishes.length > 0 && !store.isNewMenuSchema &&
							this.menu_list_floating(selected_dishes, selected_loading)}

						<DragDropContext
							onDragStart={this.onDragStart}
							onDragUpdate={this.onDragUpdate}
							onDragEnd={this.onDragEnd}
						>
							{store.isNewMenuSchema && this.list_menu_v2()}
							{!store.isNewMenuSchema && this.list_menu()}
							{this.modal_copy()}
							{this.modal_remove()}
							{this.panel_menu()}
							{this.panel_category()}
							{this.panel_dish()}
						</DragDropContext>
					</div>
					{menuEditBlocked && (
						<div
							role="presentation"
							style={{
								position: 'absolute',
								inset: 0,
								zIndex: 40,
								cursor: 'not-allowed',
							}}
						/>
					)}
				</div>
				{this.sidebar_panel_dish()}
				{this.modal_menu_more_actions()}
				{this.state.history_menu_id && (
					<MenuVersionHistory
						autoOpen
						hideTrigger
						menuId={this.state.history_menu_id}
						onClose={() => this.setState({ history_menu_id: null })}
					/>
				)}
				{this.modal_category_more_actions()}
				{this.modal_item_more_actions()}
				<MenuColor
					show={activeMenuColorModal}
					title={activeMenuColorTitle}
					item={targetItem}
					hideItemImage={targetItem === 'item' && !!menuColorBulkItemIds?.length}
					onHide={() => {
						const wasBulkColor = !!(this.state.menuColorBulkItemIds && this.state.menuColorBulkItemIds.length > 0);
						if (wasBulkColor) {
							this.setState({ activeMenuColorModal: false, menuColorBulkItemIds: null });
							this.clearBulkItemSelectionState();
							return;
						}
						this.setState((s) => ({
							activeMenuColorModal: !s.activeMenuColorModal,
							menuColorBulkItemIds: null,
							bulkSelectedDishesSheetDismissed:
								s.selected_dishes.length > 0 ? false : s.bulkSelectedDishesSheetDismissed,
						}));
					}}
					onSave={(color, media_url) => {
						const bulkIds = this.state.menuColorBulkItemIds;
						this.setState(
							{
								activeMenuColorData: {
									...activeMenuColorData,
									color: color,
									media_url,
								},
								activeMenuColorModal: !activeMenuColorModal,
								menuColorBulkItemIds: null,
							},
							async () => {
						const r = store.restaurant!;
						const copyMenus = cloneDeep(r.menus_v2 || []);
						const { targetMenuIndex, targetCategoryIndex, targetIndex, targetItem } = this.state;

						if (targetItem === 'item') {
							if (bulkIds && bulkIds.length > 0) {
								const affectedEntities: AffectedEntityRow[] = [];
								const refsToUpdate: MenuItemRefV2[] = [];
								for (const itemId of bulkIds) {
									const found = findItemInMenusV2(copyMenus, itemId);
									if (!found) continue;
									const { menuIndex, categoryId, item } = found;
									const menu = copyMenus[menuIndex];
									const category = menu.categories?.find((c: any) => c.id === categoryId);
									if (!itemDisplayColorWouldChange(item, category, menu, color)) continue;
									const previousResolved = resolveDisplayColorForEntity(
										'item',
										item,
										category,
										menu,
									);
									affectedEntities.push({
										entityType: 'item',
										id: item.id,
										name: item.name ?? String(item.id).slice(0, 8),
										displayColor: {
											from: trimColorStr(previousResolved).length > 0 ? previousResolved : '—',
											to: color,
										},
									});
									refsToUpdate.push({
										menu_id: menu.id,
										category_id: categoryId,
										id: item.id,
									});
								}
								if (refsToUpdate.length === 0) {
									UI.notification.success('No item color changes detected');
									this.clearBulkItemSelectionState();
									return;
								}
								const firstRef = refsToUpdate[0];
								this.setState({
									preOverrideContext: {
										targetEntity: {
											type: 'menu',
											name:
												refsToUpdate.length > 1
													? `${refsToUpdate.length} selected items`
													: (affectedEntities[0]?.name ?? 'Selected item'),
											categoryCount: 0,
											itemCount: refsToUpdate.length,
										},
										overrideSource: { menuId: firstRef.menu_id, categoryId: firstRef.category_id },
										changesSummary: {
											displayColor: {
												from: [
													`${refsToUpdate.length} item(s) currently use a different color`,
												].join(', '),
												to: color,
											},
										},
										editKind: 'color',
										affectedEntities,
										pendingOverride: {
											kind: 'color',
											targetEntityType: 'item',
											menuIndex: 0,
											menuId: firstRef.menu_id,
											parentAvailabilityId: '',
											parentMergedAvailability: {},
											newColor: color,
											bulkItemRefs: refsToUpdate,
										},
									},
								});
								setActiveMobileModal('desktop-menu-preoverride');
								return;
							}
							const menu = copyMenus[targetMenuIndex];
							const targetIt = menu.items[targetIndex];
							if (!targetIt) {
								UI.notification.error('Could not update item');
								return;
							}
							const category = menu.categories?.find(
								(c: any) => c.id === targetIt.category_id
							);
							if (!category) {
								UI.notification.error('Could not update item');
								return;
							}

							const previousResolved = resolveDisplayColorForEntity(
								'item',
								targetIt,
								category,
								menu
							);
							const nextMedia = media_url || '';
							const prevMedia = targetIt.media_url || '';
							const mediaChanged = prevMedia !== nextMedia;
							const colorChanged =
								trimColorStr(color) !== trimColorStr(previousResolved);
							if (!colorChanged && !mediaChanged) {
								return;
							}
							if (mediaChanged && !colorChanged) {
								await store.api.menu_partial_update_item({
									restaurant_id: r._id,
									menu_id: menu.id,
									category_id: category.id,
									item_id: targetIt.id,
									data: { media_url: toMenuApiMediaUrlValue(nextMedia) },
								});
								const nextItems = [...menu.items];
								nextItems[targetIndex] = {
									...nextItems[targetIndex],
									media_url: nextMedia,
								};
								copyMenus[targetMenuIndex] = { ...menu, items: nextItems };
								await store.updateRestaurant({ menus_v2: copyMenus });
								UI.notification.success('Item updated');
								return;
							}

							const mergedAvailSlot =
								getAvailFromMenuById(menu, menu.availability_id) || {};

							const changesSummary: OverrideChangesSummary = {
								displayColor: {
									from:
										trimColorStr(previousResolved).length > 0
											? previousResolved
											: '—',
									to: color,
								},
							};

							const pending: MenuPendingOverride = {
								kind: 'color',
								targetEntityType: 'item',
								menuIndex: targetMenuIndex,
								menuId: menu.id,
								categoryId: category.id,
								itemId: targetIt.id,
								parentAvailabilityId: menu.availability_id || '',
								parentMergedAvailability: mergedAvailSlot,
								newColor: color,
								newMediaUrl: nextMedia,
								awaitingParentPersist: true,
							};

							this.setState({
								preOverrideContext: {
									targetEntity: {
										type: 'item',
										name: targetIt.name ?? '',
									},
									overrideSource: {
										menuId: menu.id,
										categoryId: category.id,
										itemId: targetIt.id,
									},
									changesSummary,
									editKind: 'color',
									affectedEntities: [],
									pendingOverride: pending,
								},
							});
							setActiveMobileModal('desktop-menu-preoverride');
							return;
						}

						const menuIdx = targetItem === 'menu' ? targetIndex : targetMenuIndex;
						const menu = copyMenus[menuIdx];
						const preMergeAvailById = new Map<string, any>();
						(menu.availabilities || []).forEach((a: any) => preMergeAvailById.set(a.id, cloneDeep(a)));

						const menuForAnalysis = cloneDeep(menu);
						if (targetItem === 'category') {
							const copyCategories = [...menuForAnalysis.categories];
							copyCategories[targetIndex] = {
								...copyCategories[targetIndex],
								color,
								media_url: media_url || '',
							};
							menuForAnalysis.categories = copyCategories;
						} else {
							menuForAnalysis.color = color;
							menuForAnalysis.media_url = media_url || '';
						}

						const mergedAvailSlot =
							getAvailFromMenuById(menuForAnalysis, menuForAnalysis.availability_id) || {};
						const previousParentDisplayColor =
							targetItem === 'menu'
								? trimColorStr(menu.color)
								: trimColorStr(menu.categories[targetIndex]?.color) ||
									trimColorStr(menu.color);
						const previousParentMediaUrl =
							targetItem === 'menu'
								? (menu.media_url || '')
								: (menu.categories[targetIndex]?.media_url || '');
						const nextParentMediaUrl = media_url || '';
						const parentMediaChanged = previousParentMediaUrl !== nextParentMediaUrl;
						const nextParentColor = trimColorStr(color);
						const parentColorChanged = previousParentDisplayColor !== nextParentColor;

						if (parentMediaChanged && !parentColorChanged) {
							if (targetItem === 'category') {
								const cat = menu.categories[targetIndex];
								await store.api.menu_partial_update_category({
									restaurant_id: r._id,
									menu_id: menu.id,
									category_id: cat.id,
									data: { media_url: toMenuApiMediaUrlValue(nextParentMediaUrl) },
								});
								const copyCategories = [...menu.categories];
								copyCategories[targetIndex] = {
									...copyCategories[targetIndex],
									media_url: nextParentMediaUrl,
								};
								copyMenus[menuIdx] = { ...menu, categories: copyCategories };
								UI.notification.success('Category updated');
							} else {
								await store.api.menu_partial_update_menu({
									restaurant_id: r._id,
									menu_id: menu.id,
									data: { media_url: toMenuApiMediaUrlValue(nextParentMediaUrl) },
								});
								copyMenus[menuIdx] = { ...menu, media_url: nextParentMediaUrl };
								UI.notification.success('Menu updated');
							}
							await store.updateRestaurant({ menus_v2: copyMenus });
							return;
						}
						const { affectedRows, changesSummary } = analyzeDescendantsOverride({
							menu: menuForAnalysis,
							targetType: targetItem === 'menu' ? 'menu' : 'category',
							categoryId:
								targetItem === 'category' ? menuForAnalysis.categories[targetIndex]?.id : undefined,
							editKind: 'color',
							preMergeAvailById,
							mergedParentAvailability: mergedAvailSlot,
							mergedParentAvailabilityId: menuForAnalysis.availability_id || '',
							newColor: color,
							newMediaUrl: media_url || '',
							previousParentDisplayColor,
						});

						if (affectedRows.length === 0) {
							if (targetItem === 'category') {
								const cat = menu.categories[targetIndex];
								const payload = { color, media_url: toMenuApiMediaUrlValue(media_url) } as any;
								await store.api.menu_partial_update_category({
									restaurant_id: r._id,
									menu_id: menu.id,
									category_id: cat.id,
									data: payload,
								});
								const copyCategories = [...menu.categories];
								copyCategories[targetIndex] = {
									...copyCategories[targetIndex],
									color,
									media_url: media_url || '',
								};
								copyMenus[menuIdx] = { ...menu, categories: copyCategories };
								UI.notification.success('Category updated');
							} else {
								const payload = { color, media_url: toMenuApiMediaUrlValue(media_url) } as any;
								await store.api.menu_partial_update_menu({
									restaurant_id: r._id,
									menu_id: menu.id,
									data: payload,
								});
								copyMenus[menuIdx] = { ...menu, color, media_url: media_url || '' };
								UI.notification.success('Menu updated');
							}
							await store.updateRestaurant({ menus_v2: copyMenus });
							return;
						}

						const pending: MenuPendingOverride = {
							kind: 'color',
							targetEntityType: targetItem === 'menu' ? 'menu' : 'category',
							menuIndex: menuIdx,
							menuId: menu.id,
							categoryId: targetItem === 'category' ? menu.categories[targetIndex]?.id : undefined,
							parentAvailabilityId: menu.availability_id || '',
							parentMergedAvailability: mergedAvailSlot,
							newColor: color,
							newMediaUrl: media_url || '',
							awaitingParentPersist: true,
						};

						const targetEntity: OverrideTargetEntity =
							targetItem === 'menu'
								? {
										type: 'menu',
										name: menu.name,
										categoryCount: menu.categories?.length,
										itemCount: menu.items?.length,
									}
								: {
										type: 'category',
										name: menu.categories[targetIndex]?.name ?? '',
										itemCount: menu.items?.filter(
											(i: any) => i.category_id === menu.categories[targetIndex]?.id
										).length,
									};

						this.setState({
							preOverrideContext: {
								targetEntity,
								overrideSource: {
									menuId: menu.id,
									categoryId: targetItem === 'category' ? menu.categories[targetIndex]?.id : undefined,
								},
								changesSummary,
								editKind: 'color',
								affectedEntities: affectedRows,
								pendingOverride: pending,
							},
						});
						setActiveMobileModal('desktop-menu-preoverride');
							}
						);
					}}
					value={activeMenuColorData}
				/>

				<MenuVisibility
					show={activeMenuVisibilityModal}
					title={overrideTargetEntity?.name || ''}
					onHide={() => this.setState({
						activeMenuVisibilityModal: false,
						activeMenuPrevisibilityModal: false,
					})}
					onSave={async (conditions) => {
						const r = store.restaurant!;
						const baseCtx = this.state.preOverrideContext;
						if (!overrideSource?.menuId || !baseCtx?.targetEntity) return;

						const copyMenus = cloneDeep(r.menus_v2 || []);
						const targetMenuIndex = copyMenus.findIndex((menu: any) => menu.id === overrideSource.menuId);
						if (targetMenuIndex < 0) return;

						const targetMenu = copyMenus[targetMenuIndex];
						const preMergeAvailById = new Map<string, any>();
						(targetMenu.availabilities || []).forEach((a: any) => preMergeAvailById.set(a.id, cloneDeep(a)));

						const targetCategoryIndex = targetMenu.categories.findIndex(
							(category: any) => category.id === overrideSource.categoryId
						);
						const targetCategory =
							targetCategoryIndex >= 0 ? targetMenu.categories[targetCategoryIndex] : null;
						const targetItemIndex = targetMenu.items.findIndex((item: any) => item.id === overrideSource.itemId);
						const targetItemRow = targetItemIndex >= 0 ? targetMenu.items[targetItemIndex] : null;

						const effItemAid = effectiveItemAvailabilityId(targetMenu, targetCategory, targetItemRow);
						const targetItemAvailabilityIndex = effItemAid
							? targetMenu.availabilities.findIndex((a: any) => a.id === effItemAid)
							: -1;
						const targetItemAvailability =
							targetItemAvailabilityIndex >= 0
								? targetMenu.availabilities[targetItemAvailabilityIndex]
								: null;

						const catEffSlotId = targetCategory
							? effectiveCategoryAvailabilityId(targetMenu, targetCategory)
							: undefined;
						const targetCategoryAvailabilityIndex =
							catEffSlotId != null
								? targetMenu.availabilities.findIndex((a: any) => a.id === catEffSlotId)
								: -1;
						const targetCategoryAvailability =
							targetCategoryAvailabilityIndex >= 0
								? targetMenu.availabilities[targetCategoryAvailabilityIndex]
								: null;

						const targetMenuAvailabilityIndex = targetMenu.availabilities.findIndex(
							(a: any) => a.id === targetMenu.availability_id
						);
						const targetMenuAvailability = targetMenu.availabilities[targetMenuAvailabilityIndex];

						let mergedParentAvailability: any;
						let mergedParentAvailabilityId: string;

						if (overrideTargetEntity?.type === 'item') {
							if (!targetCategory || !targetItemRow || targetItemAvailabilityIndex < 0 || !targetItemAvailability) {
								UI.notification.error('Could not update item visibility');
								return;
							}
							const vnorm = normalizeMenuVisibilityConditionsForSave(conditions);
							const beforeAvail = cloneDeep(targetItemAvailability);
							const afterPreview = itemInheritsParentAvailability(
								targetMenu,
								targetCategory,
								targetItemRow
							)
								? buildForkedAvailabilityRowWithVisibility(
										targetItemAvailability,
										conditions,
										'__preview__'
									)
								: ({ ...cloneDeep(targetItemAvailability), ...vnorm } as any);

							if (!itemVisibilityMeaningfullyChanged(beforeAvail, afterPreview)) {
								await this.commitMenuCategoryVisibilitySave(conditions);
								return;
							}

							let changesSummary = buildItemVisibilityChangesSummary(beforeAvail, afterPreview);
							if (!changesSummary) {
								changesSummary = {
									posOnlinePreview: {
										from: formatPosOnlineAvailabilityPreview(beforeAvail),
										to: formatPosOnlineAvailabilityPreview(afterPreview),
									},
								};
							}
							await this.commitMenuCategoryVisibilitySave(conditions);
							return;
						}

						const vnormMenuCategory = normalizeMenuVisibilityConditionsForSave(conditions);

						// Menu or category: merge availability in memory only, then analyze before any API.
						if (overrideTargetEntity?.type === 'category') {
							if (
								!targetCategory ||
								targetCategoryAvailabilityIndex < 0 ||
								!targetCategoryAvailability
							) {
								UI.notification.error('Could not update category visibility');
								return;
							}
							const mergedCatPreview = {
								...cloneDeep(targetCategoryAvailability),
								...vnormMenuCategory,
							} as any;
							mergedParentAvailability = mergedCatPreview;
							mergedParentAvailabilityId = catEffSlotId!;
							if (!categoryInheritsMenuAvailability(targetMenu, targetCategory)) {
								copyMenus[targetMenuIndex].availabilities[targetCategoryAvailabilityIndex] =
									mergedCatPreview as any;
							}
						} else {
							if (targetMenuAvailabilityIndex < 0 || !targetMenuAvailability) {
								UI.notification.error('Could not update menu visibility');
								return;
							}
							const mergedMenuPreview = {
								...cloneDeep(targetMenuAvailability),
								...vnormMenuCategory,
							} as any;
							copyMenus[targetMenuIndex].availabilities[targetMenuAvailabilityIndex] =
								mergedMenuPreview as any;
							mergedParentAvailability = mergedMenuPreview;
							mergedParentAvailabilityId = targetMenu.availability_id!;
						}

						const menuForAnalysis = copyMenus[targetMenuIndex];
						const { affectedRows, changesSummary } = analyzeDescendantsOverride({
							menu: menuForAnalysis,
							targetType: overrideTargetEntity!.type as 'menu' | 'category',
							categoryId: overrideSource.categoryId,
							editKind: 'visibility',
							preMergeAvailById,
							mergedParentAvailability,
							mergedParentAvailabilityId,
						});

						const hasDescendantsForTarget =
							overrideTargetEntity?.type === 'menu'
								? (targetMenu.categories?.length ?? 0) > 0 || (targetMenu.items?.length ?? 0) > 0
								: (targetMenu.items ?? []).some(
										(i: any) => i.category_id === overrideSource.categoryId
								  );
						if (affectedRows.length > 0 || hasDescendantsForTarget) {
							const visibilityAffectedRows =
								affectedRows.length > 0
									? affectedRows
									: overrideTargetEntity?.type === 'menu'
										? [
												...(targetMenu.categories ?? []).map((c: any) => ({
													entityType: 'category' as const,
													name: c?.name ?? '—',
													id: c?.id,
												})),
												...(targetMenu.items ?? []).map((i: any) => ({
													entityType: 'item' as const,
													name: i?.name ?? '—',
													id: i?.id,
												})),
										  ]
										: (targetMenu.items ?? [])
												.filter((i: any) => i.category_id === overrideSource.categoryId)
												.map((i: any) => ({
													entityType: 'item' as const,
													name: i?.name ?? '—',
													id: i?.id,
												}));
							const pending: MenuPendingOverride = {
								kind: 'visibility',
								targetEntityType: overrideTargetEntity!.type as 'menu' | 'category',
								menuIndex: targetMenuIndex,
								menuId: targetMenu.id,
								categoryId: overrideSource.categoryId,
								parentAvailabilityId: mergedParentAvailabilityId,
								parentMergedAvailability: mergedParentAvailability,
								awaitingParentPersist: true,
								visibilityConditions: conditions,
							};

							this.setState({
								activeMenuVisibilityModal: false,
								activeMenuPrevisibilityModal: false,
								preOverrideContext: {
									...baseCtx,
									changesSummary,
									editKind: 'visibility',
									affectedEntities: visibilityAffectedRows,
									pendingOverride: pending,
								},
							});
							setActiveMobileModal('desktop-menu-preoverride');
							return;
						}

						await this.commitMenuCategoryVisibilitySave(conditions);
					}}
					initialConditions={overrideTargetEntity?.conditions || null}
				/>
				<MenuPreVisibility
					show={activeMenuPrevisibilityModal}
					title={overrideTargetEntity?.name || ''}
					onHide={() => this.setState({
						activeMenuPrevisibilityModal: !activeMenuPrevisibilityModal})}
					onSave={(color) => this.setState({
						activeMenuPrevisibilityModal: !activeMenuPrevisibilityModal
					}) }
					onShowSettings={() => {
						this.setState({
							activeMenuVisibilityModal:!activeMenuVisibilityModal,
							activeMenuPrevisibilityModal: !activeMenuPrevisibilityModal
						})
					}}
					conditions={overrideTargetEntity?.conditions || null}
					type={overrideTargetEntity?.type || 'menu'}
					menuId={overrideSource?.menuId}
					categoryId={overrideSource?.categoryId}
				/>

				<MenuFilterMenu
					show={activeMobileModal === 'desktop-filter-menu'}
					onHide={() => setActiveMobileModal('')}
					onSave={() => setActiveMobileModal('')}
					filterPos={this.state.menuChannelFilterPos}
					filterOnline={this.state.menuChannelFilterOnline}
					onTogglePos={() =>
						this.setState((s) => ({ menuChannelFilterPos: !s.menuChannelFilterPos }))
					}
					onToggleOnline={() =>
						this.setState((s) => ({ menuChannelFilterOnline: !s.menuChannelFilterOnline }))
					}
				/>

				<MenuPrinterSettings
						show={activeMobileModal === 'desktop-printer-settings'}
						title={"Printer Setup"}
						onHide={() => setActiveMobileModal('')}
						onClickPrinter={(printer) => {
							this.setState({ selectedPrinterForEligible: { name: printer.name, _id: printer._id } });
							setActiveMobileModal('desktop-printer-eligible-items');
						}}
				/>
				<MenuPrinterEligibleItems
						show={activeMobileModal === 'desktop-printer-eligible-items'}
						title={this.state.selectedPrinterForEligible?.name ?? 'Eligible Items'}
						printerName={this.state.selectedPrinterForEligible?.name}
            printerId={this.state.selectedPrinterForEligible?._id}
						onHide={() => { this.setState({ selectedPrinterForEligible: null }); setActiveMobileModal('desktop-printer-settings'); }}
						onSave={async (selectedIds: string[]) => {
              const printerId = this.state.selectedPrinterForEligible?._id;
              if (!printerId) {
                UI.notification.error('No printer selected');
                return;
              }
              const r = store.restaurant!;
              const printers = r.settings.printers.map((p: any) => {
                if (p._id === printerId) {
                  return { ...p, receipt: { ...p.receipt, eligible_items: selectedIds } };
                }
                return p;
              });
              try {
                const update = { "$set": { "settings.printers": printers } }
                await store.api.restaurant_update({
                  _id: r._id,
                  update
                })
                r.settings.printers = printers;
                store.setRestaurant({ ...r });
              } catch (err) {
                UI.notification.error((err as any)?.message ?? 'Failed to save');
                return;
              }

							UI.notification.success('Printer eligible items saved');
							this.setState({ selectedPrinterForEligible: null });
							setActiveMobileModal('desktop-printer-settings');
						}}
				/>

				<MenuReceiptPrinterAssignItem
					show={activeMobileModal === 'desktop-receipt-printer-assign-item'}
					itemId={this.state.receiptPrinterAssignItem?.itemId}
					title={
						this.state.receiptPrinterAssignItem?.name
							? `Receipt printers: ${this.state.receiptPrinterAssignItem.name}`
							: 'Receipt printers'
					}
					onHide={() => {
						this.setState({ receiptPrinterAssignItem: null });
						setActiveMobileModal('');
					}}
					onSave={async (selectedPrinterIds) => {
						const ctx = this.state.receiptPrinterAssignItem;
						if (!ctx?.itemId) {
							UI.notification.error('No item selected');
							return;
						}
						const r = store.restaurant!;
						const { updatedPrinters, updateQuery } =
							RestaurantUtils.menu.assignItemsToPrinterV2(r, [ctx.itemId], selectedPrinterIds);
						try {
							await store.api.restaurant_update({
								_id: r._id,
								update: updateQuery,
							});
							r.settings.printers = updatedPrinters;
							store.setRestaurant({ ...r });
						} catch (err) {
							UI.notification.error((err as any)?.message ?? 'Failed to save');
							return;
						}
						UI.notification.success('Receipt printers updated');
						this.setState({ receiptPrinterAssignItem: null });
						setActiveMobileModal('');
					}}
				/>

				<MenuPrinterAssignItems
						show={activeMobileModal === 'desktop-printer-assign-items'}
						title={
							this.state.printerAssignBulkItemIds && this.state.printerAssignBulkItemIds.length > 1
								? `Assign printer (${this.state.printerAssignBulkItemIds.length} items)`
								: 'Assign printer for this item'
						}
						selectedPrinterValues={(() => {
							const t = this.state.printerAssignTarget;
							if (!t || !store.isNewMenuSchema) return undefined;
							const r = store.restaurant!;
							const menus = r.menus_v2 || [];
							const menu = menus[t.menuIndex];
							if (!menu?.items) return undefined;
							const item = menu.items.find((i: any) => i.id === t.itemId);
							return (item as any)?.integrations?.pushpos?.printers;
						})()}
						onHide={() => {
							if (this.state.printerAssignBulkItemIds?.length) {
								this.clearBulkItemSelectionState();
								setActiveMobileModal('');
								return;
							}
							this.setState((s) => ({
								printerAssignTarget: null,
								printerAssignBulkItemIds: null,
								bulkSelectedDishesSheetDismissed:
									s.selected_dishes.length > 0 ? false : s.bulkSelectedDishesSheetDismissed,
							}));
							setActiveMobileModal('');
						}}
						onSave={async (selectedPrinterValues) => {
							const t = this.state.printerAssignTarget;
							const bulkIds = this.state.printerAssignBulkItemIds;
							if (!t || !store.isNewMenuSchema) {
								setActiveMobileModal('');
								return;
							}
							const r = store.restaurant!;
							const refs = bulkIds?.length
								? this.getSelectedItemRefsV2().filter((ref) => bulkIds.includes(ref.id))
								: [{ menu_id: t.menuId, category_id: t.categoryId, id: t.itemId }];
							if (refs.length === 0) {
								this.setState({ printerAssignTarget: null, printerAssignBulkItemIds: null });
								setActiveMobileModal('');
								return;
							}
							if (bulkIds?.length) {
								const selectedLabels = formatPosPrinterValuesForDisplay(selectedPrinterValues || []);
								const affectedEntities: AffectedEntityRow[] = [];
								const refsToUpdate: MenuItemRefV2[] = [];
								for (const ref of refs) {
									const menu = (r.menus_v2 || []).find((m: any) => m.id === ref.menu_id);
									const item = menu?.items?.find((i: any) => i.id === ref.id);
									if (!menu || !item) continue;
									if (!itemPosPrintersWouldChange(item, selectedPrinterValues)) continue;
									const current = ((item as any)?.integrations?.pushpos?.printers || []) as any[];
									const currentLabels = formatPosPrinterValuesForDisplay(current);
									affectedEntities.push({
										entityType: 'item',
										id: item.id,
										name: item.name ?? String(item.id).slice(0, 8),
										posPrinters: {
											from: currentLabels.length ? currentLabels : ['—'],
											to: selectedLabels.length ? selectedLabels : ['—'],
										},
									});
									refsToUpdate.push(ref);
								}
								if (refsToUpdate.length === 0) {
									UI.notification.success('No printer changes detected');
									this.clearBulkItemSelectionState();
									setActiveMobileModal('');
									return;
								}
								this.setState((s) => ({
									printerAssignTarget: null,
									printerAssignBulkItemIds: null,
									preOverrideContext: {
										targetEntity: {
											type: 'menu',
											name: `${refsToUpdate.length} selected items`,
											categoryCount: 0,
											itemCount: refsToUpdate.length,
										},
										overrideSource: { menuId: refsToUpdate[0].menu_id },
										changesSummary: {
											posPrinters: {
												from: [
													`${refsToUpdate.length} item(s) currently use different printers`,
												],
												to: selectedLabels.length ? selectedLabels : ['—'],
											},
										},
										editKind: 'printers',
										affectedEntities,
										pendingOverride: {
											kind: 'printers',
											targetEntityType: 'item',
											menuIndex: 0,
											menuId: refsToUpdate[0].menu_id,
											parentAvailabilityId: '',
											parentMergedAvailability: {},
											newPosPrinters: selectedPrinterValues,
											bulkItemRefs: refsToUpdate,
										},
									},
									bulkSelectedDishesSheetDismissed:
										s.selected_dishes.length > 0 ? false : s.bulkSelectedDishesSheetDismissed,
								}));
								setActiveMobileModal('desktop-menu-preoverride');
								return;
							}
							const refsToUpdate = refs.filter((ref) => {
								const menu = (r.menus_v2 || []).find((m: any) => m.id === ref.menu_id);
								const item = menu?.items?.find((i: any) => i.id === ref.id);
								return !!item && itemPosPrintersWouldChange(item, selectedPrinterValues);
							});
							if (refsToUpdate.length === 0) {
								UI.notification.success('No printer changes detected');
								this.setState({ printerAssignTarget: null, printerAssignBulkItemIds: null });
								setActiveMobileModal('');
								return;
							}
							try {
								await store.api.menu_partial_update_batch_items({
								  restaurant_id: r._id,
                  data: {
                    items: refsToUpdate.map((item: any) => ({ ...item, integrations: { pushpos: { printers: selectedPrinterValues } }}) ),
                  }
								})
								UI.notification.success(
									refsToUpdate.length > 1 ? `Printers updated for ${refsToUpdate.length} items` : 'Printers updated'
								);
								const menus = cloneDeep(r.menus_v2 || []);
								for (const { menu_id, id } of refsToUpdate) {
									const menu = menus.find((m: any) => m.id === menu_id);
									const item = menu?.items?.find((i: any) => i.id === id);
									if (item) {
										(item as any).integrations = (item as any).integrations || {};
										(item as any).integrations.pushpos = {
											...(item as any).integrations?.pushpos,
											printers: selectedPrinterValues,
										};
									}
								}
								store.updateRestaurant({ menus_v2: menus });
							} catch (e) {
								UI.notification.error((e as any)?.message ?? 'Failed to update');
								setActiveMobileModal('');
								return;
							}
							if (bulkIds?.length) {
								this.clearBulkItemSelectionState();
							} else {
								this.setState({
									printerAssignTarget: null,
									printerAssignBulkItemIds: null,
								});
							}
							setActiveMobileModal('');
						}}
				/>

				<MenuPreOverride
					show={activeMobileModal === 'desktop-menu-preoverride'}
					targetEntity={overrideTargetEntity}
					overrideSource={overrideSource}
					changesSummary={overrideChangesSummary}
					editKind={overrideEditKind}
					affectedEntities={overrideAffectedEntities}
					onHide={() => {
						const p = this.state.preOverrideContext?.pendingOverride;
						if (this.isBulkItemPreOverridePending(p)) {
							this.closeBulkItemPreOverrideModal();
							return;
						}
						this.setState({ preOverrideContext: null });
						setActiveMobileModal('');
					}}
					onKeepCurrent={async () => {
						const ctx = this.state.preOverrideContext;
						const p = ctx?.pendingOverride;
						const isBulkItemOverride = this.isBulkItemPreOverridePending(p);
						const keepCurrentTargetLabel =
							p?.targetEntityType === 'menu'
								? 'Menu'
								: p?.targetEntityType === 'category'
									? 'Category'
									: p?.targetEntityType === 'item'
										? 'Item'
										: 'Selection';
						if (p?.awaitingParentPersist && p.kind === 'visibility' && p.visibilityConditions != null) {
							await this.commitMenuCategoryVisibilitySave(p.visibilityConditions, { persistOnly: true });
							UI.notification.success(`${keepCurrentTargetLabel} visibility updated`);
							setActiveMobileModal('');
							return;
						}
						if (p?.awaitingParentPersist && p.kind === 'printers' && p.newPosPrinters != null) {
							await this.commitMenuCategoryPrintersSave(p.newPosPrinters, { persistOnly: true });
							UI.notification.success(`${keepCurrentTargetLabel} printers updated`);
							setActiveMobileModal('');
							return;
						}
						if (p?.awaitingParentPersist && p.kind === 'color' && p.newColor != null) {
							const r = store.restaurant!;
							const menus = cloneDeep(r.menus_v2 || []);
							await this.persistParentMenuColor(p, menus);
							await store.updateRestaurant({ menus_v2: menus });
							UI.notification.success(`${keepCurrentTargetLabel} color updated`);
							setActiveMobileModal('');
							return;
						}
						if (p?.kind === 'receipt_printers') {
							UI.notification.success(`${keepCurrentTargetLabel} receipt printers kept`);
						}
						if (isBulkItemOverride) {
							this.closeBulkItemPreOverrideModal();
							return;
						}
						this.setState({ preOverrideContext: null });
						setActiveMobileModal('');
					}}
					onConfirm={async () => {
						const p = this.state.preOverrideContext?.pendingOverride;
						const isBulkItemOverride = this.isBulkItemPreOverridePending(p);
						if (p) {
							await this.applyPendingMenuOverride();
						} else {
							UI.notification.success('Overrides applied');
						}
						if (isBulkItemOverride) {
							this.closeBulkItemPreOverrideModal();
							return;
						}
						this.setState({ preOverrideContext: null });
						setActiveMobileModal('');
					}}
				/>

				<EditingStatusAlertModal
					active={activeMobileModal === 'desktop-editing-status'}>
					<EditingStatusAlertModalContent>
						<EditingStatusAlertModalHeader>
							<EditingStatusAlertModalHeaderText>
							Someone is currently editing this menu
							</EditingStatusAlertModalHeaderText>
							<EditingStatusIconWrapper
								onClick={() => setActiveMobileModal('')}>
								<IconEditingClose />
							</EditingStatusIconWrapper>
						</EditingStatusAlertModalHeader>
						<EditingStatusAlertModalBody>
							{menuEditLock?.userName ? (
								<p style={{ marginBottom: 12, color: '#4B4F5E', fontSize: 14 }}>
									{menuEditLock.userName} has control. Request control to take over editing.
								</p>
							) : null}
							<ButtonV2
								className="flex-auto"
								onClick={() => {
									store.notifications.menuEditingRequestTakeover();
									setActiveMobileModal('');
								}}>
								Request Control <IconArrowForwardWhite/>
							</ButtonV2>
							<ButtonV2
								variant='default'
								onClick={() => setActiveMobileModal('')}>
								Cancel
							</ButtonV2>
						</EditingStatusAlertModalBody>
					</EditingStatusAlertModalContent>
				</EditingStatusAlertModal>

				{selected_dishes.length > 0 && <MobileMoreActionsWrapper>
					<SettingsActionButtonContainer>
					<SettingsActionButtonWrapper style={{ left: 0 }}>
						<button
							className='btn-cancel'
							onClick={() => {
								this.setState({
									hideMoreActionsOnMobile: !hideMoreActionsOnMobile,
									selected_dishes: [],
									bulkSelectedDishesSheetDismissed: false,
									activeDeleteItemModal: false
								});

							}}>
							Cancel
						</button>
						<button onClick={() => {
							this.setState({ hideMoreActionsOnMobile: !hideMoreActionsOnMobile });
						}}>
							More Actions
						</button>
					</SettingsActionButtonWrapper>
				</SettingsActionButtonContainer>
				</MobileMoreActionsWrapper> }
			</>
		);
	}
}
